import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';
import {
  AfterViewInit,
  ApplicationRef,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  TemplateRef,
  ViewChild
} from '@angular/core';
import { MatSidenav, MatSidenavContainer } from '@angular/material/sidenav';
import { Store } from '@ngrx/store';
import {
  catchError,
  debounceTime,
  EMPTY,
  filter,
  fromEvent,
  Observable,
  Subject,
  Subscription,
  take,
  takeUntil,
  tap
} from 'rxjs';

import * as L from 'leaflet';

import { HttpBackend, HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

import {
  FormGroup,
  FormControl,
  FormBuilder,
  Validators
} from '@angular/forms';
import { formatDate } from '@angular/common';

import { ActiaMapService } from 'src/app/core/services/actia-map.service';
import {
  Journey,
  JourneyDetails,
  IotAsset
} from 'src/app/core/models/iotAssetsModel.data';

import { MapContainerService } from 'src/app/core/services/mapContainer.service';
import { ConfirmationModalComponent } from '../confirmation-modal/confirmation-modal.component';
import {
  MatDialog,
  MatDialogConfig,
  MatDialogRef
} from '@angular/material/dialog';
import { SelectItem } from 'primeng/api';
import { FilterRefSetting } from 'src/app/core/store/interface/filterRefSetting.interface';
import { AppStateInterface } from 'src/app/appState.interface';
import * as PostActions from '../../../core/store/actions';
import { Calendar } from 'primeng/calendar';
import { TranslateService } from '@ngx-translate/core';
import { DataService } from 'src/app/core/services/data.service';
import { DateAdapter } from '@angular/material/core';

import { UtilsService } from 'src/app/core/services/utils.service';
import * as XLSX from 'xlsx';
import { saveAs } from 'file-saver';
import { CurrentUserInfo } from 'src/app/core/models/currentUserInfo';
import { ActiaJourneyService } from 'src/app/core/services/actia-journey.service';
import { HistorialJourneyContainerService } from 'src/app/core/services/historicalJourneyContainer.service';
import { KpiCrudService, WidgetRequest } from 'src/app/core/services/kpi-crud.service';
import { CatalogService } from 'src/app/core/services/catalog.service';
import { forkJoin } from 'rxjs';
import { select } from '@ngrx/store';
import { selectedCustomerSelector, selectUserFeature } from '../../../core/store/selectors';

// Il backend restituisce timestamp "naive" (es. "2026-07-07T10:08:02", senza 'Z'/offset) che
// rappresentano l'ora nel fuso preferito dall'utente (varUtc), NON l'ora locale del browser.
// new Date(str).toISOString() assume erroneamente che siano ora locale del browser: se il
// browser non è sullo stesso fuso dell'utente (es. è su UTC), non applica nessuno shift.
// Questo helper interpreta esplicitamente la stringa come wall-clock nel fuso indicato e
// restituisce il vero istante UTC corrispondente.
function normalizeIanaTimeZone(timezone: string): string {
  if (!timezone || timezone.toLowerCase() === 'utc') return 'UTC';
  return timezone
    .split('/')
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join('/');
}

function parseWallClockInTimeZone(dateStr: string, timezone: string): Date {
  const match = dateStr.match(/(\d{4})-(\d{2})-(\d{2})[ T](\d{2}):(\d{2}):(\d{2})/);
  if (!match) {
    return new Date(dateStr);
  }
  const [y, mo, d, h, mi, s] = match.slice(1).map(Number);
  const tz = normalizeIanaTimeZone(timezone);

  // Ipotesi iniziale: questi numeri fossero già UTC
  const guessUtcMs = Date.UTC(y, mo - 1, d, h, mi, s);

  // Verifica che ora mostrerebbe il fuso indicato per l'istante ipotizzato
  const parts = new Intl.DateTimeFormat('en-US', {
    timeZone: tz,
    year: 'numeric', month: '2-digit', day: '2-digit',
    hour: '2-digit', minute: '2-digit', second: '2-digit',
    hour12: false
  }).formatToParts(new Date(guessUtcMs));

  const get = (type: string) => Number(parts.find(p => p.type === type)?.value ?? 0);
  const shownHour = get('hour') === 24 ? 0 : get('hour');
  const shownUtcMs = Date.UTC(get('year'), get('month') - 1, get('day'), shownHour, get('minute'), get('second'));

  // Corregge l'ipotesi con la differenza tra quanto mostrato dal fuso indicato e quanto ipotizzato
  return new Date(guessUtcMs + (guessUtcMs - shownUtcMs));
}

@Component({
  selector: 'app-actia-journey',
  templateUrl: './actia-journey.component.html',
  styleUrls: ['./actia-journey.component.scss'],
  providers: [ActiaMapService]
})
export class ActiaJourneyComponent implements AfterViewInit {
  /** Waypoints ad alta frequenza globali */
  public highFrequencyGlobalWaypoints: [number, number][] = [];

  /** Layer globale per punti alta frequenza */
  public highFrequencyGlobalLayer: any = null;
  // ROUTING DISABLED: Non più utilizzato HERE per calcolo percorsi
  // public highFrequencyGlobalLayerNoHere:any = null;
  map: L.Map | undefined;
  // isLoading$: Observable<boolean>;
  // isRefreshing$: Observable<boolean>;
  //unmatchedWaypointsVisible: boolean = true; // Di default visibile
  waypointsVisible: boolean = false; // Di default visibile
  waypointLinesVisible: boolean = false; // Linee che collegano i waypoints

  isMobile: boolean = false;
  activeFooterButton: string = 'sidebar'; // Imposta il bottone attivo iniziale

  // Proprietà per il loading con percentuale su mobile
  loadingProgress: number = 0;
  loadingMessage: string = '';

  ActiaAttribution =
      `© ${new Date().getFullYear()} Actia Italia - <a href="https://actiafleet.com/actia-italia/"> </a>`;
  @ViewChild('sidenavJourney') sidenav!: MatSidenav; // Riferimento al sidenav
  @ViewChild('dateRangeDialog') dateRangeDialog!: TemplateRef<any>;

  @ViewChild('journeyContent') sidenavContainer: MatSidenavContainer;
  waypoints: [number, number][] = [];
  waypointData: any;
  startTime: string = '00:00';
  endTime: string = formatDate(new Date(), 'HH:mm', 'en-US');
  // Use current date for start and end times
  paramStartTime: string =
    formatDate(new Date(), 'yyyy-MM-dd', 'en-US') + ' ' + this.startTime;
  paramEndTime: string =
    formatDate(new Date(), 'yyyy-MM-dd', 'en-US') + ' ' + this.endTime;

  paramUid: string[] = [];

  //5E5D8F38-D692-4AA7-9FFE-4B66FBAD2BBB
  //42B5AF0-52FF-4892-9AC7-A10F682BC210
  private mapMessageControl: L.Control | null = null;
  private messageTimeoutId: any = null;
  // Aggiungi questa proprietà alla classe
  private waypointDotsGroup: L.FeatureGroup | null = null;
  private waypointLinesGroup: L.FeatureGroup | null = null;
  // Aggiungi queste proprietà alla classe
  // ROUTING DISABLED: Non più utilizzato per calcolo percorsi
   private routeDotsGroup: L.FeatureGroup | null = null;
   public routePointsVisible: boolean = false;
  private journeyEndpointsGroup: L.FeatureGroup | null = null;
  public journeyEndpointsVisible: boolean = false;
  // Aggiungi queste proprietà alla classe per tenere traccia dei decoratori
  private arrowDecoratorsGroup: L.LayerGroup = L.layerGroup();
  private journeyArrowDecoratorsMap: Map<number, L.LayerGroup> = new Map();

  private highFrequencyLayers: Map<
    number,
    { layer: L.LayerGroup; pointCount: number }
  > = new Map();
  private displayedTripIds: number[] = [];
  // Aggiungi questa proprietà alla classe ActiaJourneyLastComponent
  private showFallbackMarkers: Map<number, boolean> = new Map(); // Mappa trip_id -> visibilità marker
  // private waypointDotsGroup: L.FeatureGroup = L.featureGroup();
  public Math = Math;
  //private resizeObserver: ResizeObserver | null = null;
  // Proprietà da aggiungere alla classe

  //  public segments: Array<{
  //   coordinates: [number, number][];
  //   selected: boolean;
  //   journeyId?: number;  // ID del journey a cui appartiene questo segmento
  //   journey?: Journey;   // Riferimento all'oggetto journey
  // }> = [];

  // Ordina i journey per trip_id per garantire la sequenza corretta
  backClicked = false;
  public JourneyCoords: Array<{
    trip_id: number;
    coordinates: {
      start: [number, number] | null;
      end: [number, number] | null;
    };
  }> = [];

  // ROUTING DISABLED: Coordinate percorsi non più utilizzate
   public allRoutesCoords: [number, number][] = [];
  public allHFCoords: [number, number][] = [];

  // ROUTING DISABLED: Mappa percorsi non più utilizzata
  // private journeyRoutesMap:
  //   | Map<
  //       number,
  //       {
  //         coordinates: [number, number][];
  //         startIndex: number;
  //         endIndex: number;
  //         startPoint: [number, number];
  //         endPoint: [number, number];
  //         journey: Journey | undefined;
  //       }
  //     >
  //   | undefined;

  unmatchedWaypointsGroup: any;
  waypointsGroup: any;
  startMarkersGroup: any; // Layer per i marker "P"
  endMarkersGroup: any; // Layer per i marker "A"
  //private zoomThreshold: number = 10; // Livello di zoom minimo per mostrare i waypoint

  private highlightedSegments: L.Polyline[] = []; // Array per tenere traccia dei segmenti evidenziati
  currentlySelectedJourneyId: number | null = null;
  // ROUTING DISABLED: Flag di routing non più utilizzato
   public isRoutingInProgress = false;
  // Una mappa che associa ogni journey ai suoi segmenti evidenziati
  private journeyHighlightMap: Map<number, L.Layer[]> = new Map();
  journeys: Journey[] = [];
  journeysDetails: JourneyDetails[] = [];

  // Array per KPI journey divisi per scope
  journeyKpiList: any[] = []; // Scope 'kpi'
  journeyKpiCustomList: any[] = []; // Scope 'kpi_custom'

  // Stati accordion per KPI
  kpiAccordionOpen: boolean = true;
  kpiCustomAccordionOpen: boolean = true;

  // Getter per la lingua corrente
  get currentLang(): string {
    return this.dataService.getLanguage() || 'it-IT';
  }

  /**
   * Ottiene la label localizzata per un KPI custom
   */
  getLocalizedLabel(kpi: any): string {
    if (kpi.i18nLabel && typeof kpi.i18nLabel === 'object') {
      const lang = this.currentLang;

      // Cerca prima con la chiave esatta (es: "en-US")
      if (kpi.i18nLabel[lang]) {
        return kpi.i18nLabel[lang];
      }

      // Se non trovato, cerca una chiave che inizia con la lingua (es: "en" -> "en-US")
      const matchingKey = Object.keys(kpi.i18nLabel).find(key =>
        key.toLowerCase().startsWith(lang.toLowerCase())
      );

      if (matchingKey && kpi.i18nLabel[matchingKey]) {
        return kpi.i18nLabel[matchingKey];
      }

      // Fallback su label o name
      return kpi.label || kpi.name || '';
    }
    return kpi.label || kpi.name || '';
  }

  getScopeTabLabel(scope: string): string {
    return this.catalogService.getScopeLabel(scope);
  }

  isLoading = false;
  error: string | null = null;
  private isInitialized = false;
  journeyWaypointsGroup: L.FeatureGroup = L.featureGroup();

  // state
  journeys$: Observable<any[]> | undefined; // Observable per i journey
  journeyDetails$: Observable<JourneyDetails[]> | undefined; // Observable per i dettagli
  isJourneyLoading$: Observable<boolean> | undefined; // Observable per lo stato di caricamento
  journeyError$: Observable<any> | undefined; // Observable per gli errori
  private destroy$ = new Subject<void>();

  private httpWithoutInterceptor: HttpClient;

  // Proprietà per PrimeNG
  displayDateRangeDialog: boolean = false;
  selectedPresetOption: string | null = null;
  dateRange: Date[] = [];
  today: Date = new Date();
  startTimeDate: Date = new Date();
  endTimeDate: Date = new Date();
  selectedVehicle: IotAsset; // Puoi anche usare un'interfaccia appropriata invece di any
  mergedDataHF_Waypoint: any;
  public highFrequencyGlobalVisible: boolean = false;
  // ROUTING DISABLED: Proprietà NoHere non più utilizzata
   public highFrequencyGlobalVisibleNoHere: boolean = false; // Nuova proprietà per i punti alta frequenza senza HERE
  private waypointPolylineGroup: L.FeatureGroup | null = null;
  private hfpointPolylineGroup: L.FeatureGroup | null = null;
  private highFrequencyPointsByTripId: Map<number, any[]> = new Map();
  @ViewChild('calendar') calendar: Calendar;
  // Opzioni predefinite per il dropdown
  presetOptions: SelectItem[] = [
    { label: 'Today', value: 'today' },
    { label: 'Yesterday', value: 'yesterday' },
    { label: 'Last 2 days', value: 'last2' },
    { label: 'Last 3 days', value: 'last3' },
    { label: 'Last 4 days', value: 'last4' },
    { label: 'Last 5 days', value: 'last5' },
    { label: 'Last 6 days', value: 'last6' },
    { label: 'Last 7 days', value: 'last7' },
    { label: 'Custom', value: 'customdate' }
  ];

  lastDateSelectionMethod: 'preset' | 'datepicker' = 'preset'; // Default al preset
  // Proprietà per il date range picker
  materialDateRange: { start: Date | null; end: Date | null } = {
    start: null,
    end: null
  };
  resizeObserver: ResizeObserver;
  get maxEndDate(): Date | null {
    if (!this.materialDateRange.start) return null;

    const maxDate = new Date(this.materialDateRange.start);
    maxDate.setDate(maxDate.getDate() + 6); // +6 per avere 7 giorni in totale

    // Non permettere date future oltre oggi
    if (maxDate > this.today) {
      return this.today;
    }
    return maxDate;
  }

  dialogRef: MatDialogRef<any> | null = null;

  // GESTIONE SCIA PER Journey IN_PROGRESS
  // Aggiungi queste proprietà alla classe ActiaJourneyComponent
  private autoUpdateInterval: any = null;
  private autoUpdateIntervalTime = 10000; // 10 secondi, configurabile
  private inProgressJourneys: Map<
    number,
    {
      coordinates: [number, number][];
      lastUpdateTime: Date;
    }
  > = new Map();

  // per gestire se subscrivpotion e UnSubscribve quando ci sganciamo
  private activeHttpRequests: Subscription[] = [];
  private cancelPendingRequests$ = new Subject<void>();
  public activationSource: 'selectedVehicle' | 'journeysList' | null = null;

  // ROUTING DISABLED: Route summaries non più utilizzate
 public routeSummaries: any[] = [];
  // Add this property to your component class
  @Input() mapId: string = 'map-journey-mobile'; // Default fallback value
   @Input() toolbarButton: any[] = [
    false,
    false,
    true,
    false,
    false,
    false,
    true,
    false,
    'map-journey'
  ];

  constructor(
    private store: Store<AppStateInterface>,
    private breakpointObserver: BreakpointObserver,
    public mapService: ActiaMapService,

    private journeyService: ActiaJourneyService,
    private mapContainerService: MapContainerService,
    private hjContaierService: HistorialJourneyContainerService,
    private httpBackend: HttpBackend, // Aggiungi HttpBackend
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private translate: TranslateService,
    private dateAdapter: DateAdapter<Date>,
    private appRef: ApplicationRef,
    private dataService: DataService,
    private vehicleService: ActiaMapService,
    private utlService: UtilsService,
    private kpiCrudService: KpiCrudService,
    private catalogService: CatalogService
  ) {
    this.httpWithoutInterceptor = new HttpClient(this.httpBackend);
    // Inizializza l'ora di inizio e fine
    this.startTimeDate.setHours(0, 0, 0);
    // this.endTimeDate.setHours(23, 59, 59);

    const now = new Date();
    this.endTimeDate.setHours(
      now.getHours(),
      now.getMinutes(),
      now.getSeconds()
    );

    this.dateAdapter.setLocale('it-IT');

    this.mapContainerService.selectedIotAsset$
      .pipe(takeUntil(this.destroy$))
      .subscribe((vehicle) => {
        // console.log('SCATTA Selected vehicle:', vehicle);
        if (vehicle) {
          console.log('VIN Veicolo selezionato:', vehicle.uid);
           console.log('Veicolo selezionato:', vehicle);
          this.selectedVehicle = vehicle;
          this.activationSource = 'selectedVehicle';
          // Forza l'aggiornamento della vista
         // this.appRef.tick();
          // Controlla se il veicolo è cambiato
          console.log('paramUid[0]', this.paramUid[0]);
          console.log('vehicle.uid.toString()', vehicle.uid.toString());
          if (
            this.paramUid.length > 0 &&
            this.paramUid[0] !== vehicle.uid.toString()
          ) {
            console.log('Rilevato cambio di veicolo, reset della mappa');
            // Prima resetta la mappa per pulire tutto
            // Use current date for start and end times
            this.paramStartTime =
              formatDate(new Date(), 'yyyy-MM-dd', 'en-US') + ' 00:00:00';
            this.paramEndTime =
              formatDate(new Date(), 'yyyy-MM-dd', 'en-US') +
              ' ' +
              this.endTime;
            this.resetMap();
          }

          // Aggiorna il parametro uid con il veicolo selezionato
          // console.log('ZAKKKK', vehicle);
          if (vehicle.uid) {
            this.paramUid = [vehicle.uid];
           } else {
            console.warn('Nessun UID disponibile per il veicolo:', vehicle);
            this.paramUid = [];
          }
          console.log('paramuid', this.paramUid)

          // Carica i nuovi dati se il componente è già stato inizializzato
          // console.log('isInitialized', this.isInitialized);
          // if (this.isInitialized) {
          //   this.loadJourneys();
          //   this.loadJourneyDetails();
          // }
        } else {
           console.log('Nessun veicolo selezionato');
        }
      });

    // Quando ricevi nuovi journeys dal servizio
    this.hjContaierService.journeysList$
      .pipe(takeUntil(this.destroy$))
      .subscribe((journeys) => {
        if (journeys && journeys.length > 0) {
          console.log(
            'Ricevuti nuovi journeys dal servizio HistorialJourneyContainer',
            journeys
          );

          // Puoi assegnare i journeys se necessario
          this.journeys = journeys;
          this.activationSource = 'journeysList';

          // Imposta date di inizio e fine basate sui journey ricevuti
          if (journeys.length > 0) {
            // Ordina i journey per data di inizio (crescente)
            const sortedByStart = [...journeys].sort((a, b) => {
              const timeA = a.start_time ? new Date(a.start_time).getTime() : 0;
              const timeB = b.start_time ? new Date(b.start_time).getTime() : 0;
              return timeA - timeB;
            });

            // Ordina i journey per data di fine (decrescente)
            const sortedByEnd = [...journeys].sort((a, b) => {
              const timeA = a.end_time ? new Date(a.end_time).getTime() : 0;
              const timeB = b.end_time ? new Date(b.end_time).getTime() : 0;
              return timeB - timeA;
            });

            // Prendi il primo journey per la data di inizio (il più vecchio)
            if (sortedByStart[0].start_time) {
              this.paramStartTime = new Date(
                sortedByStart[0].start_time
              ).toISOString();
              console.log(
                `Impostato paramStartTime: ${this.paramStartTime} dal journey ${sortedByStart[0].trip_id}`
              );
            }

            // Prendi il primo journey per la data di fine (il più recente)
            if (sortedByEnd[0].end_time) {
              this.paramEndTime = new Date(
                sortedByEnd[0].end_time
              ).toISOString();
              console.log(
                `Impostato paramEndTime: ${this.paramEndTime} dal journey ${sortedByEnd[0].trip_id}`
              );
            }
          }

          const serviceVehicle: any =
            this.hjContaierService.getSelectedVehicle();
          if (serviceVehicle) {
            this.selectedVehicle = serviceVehicle;
            this.paramUid = [serviceVehicle.uid];
          } else {
            this.selectedVehicle = null as any;
            console.warn(
              'No vehicle selected from HistorialJourneyContainerService'
            );
          }

          // Mostra indicatore di caricamento
          this.showRouteLoadingIndicator(true);

          // Prima di caricare nuovi dati, rimuovi tutti gli highlight
          this.clearAllHighlights();
          this.resetMapData();
          this.loadJourneyDetails();
          this.appRef.tick();
        } else {
          console.log('Lista journeys vuota o reset');
        }
      });
  }

  loadJourneys(isRefresh: boolean = false): void {
    if (this.mapContainerService.getJourneyLoadingState()) {
      console.log('Operazione in corso, richiesta ignorata');
      return;
    }

    this.isLoading = true;
    this.error = null;

    // Salva i trip_id attualmente visualizzati
    const currentDisplayedTripIds = [...this.displayedTripIds];
    // Controlla esplicitamente che l'array contenga elementi
    const hasDisplayedTrips =
      currentDisplayedTripIds && currentDisplayedTripIds.length > 0;

    // Mostra indicatore di caricamento
    this.showRouteLoadingIndicator(true);

    // Salva i journey attuali per confronto
    const currentJourneys = [...this.journeys];

    // Prima di caricare nuovi dati, rimuovi tutti gli highlight
    this.clearAllHighlights();
    this.resetMapData();
    if (!isRefresh) {
      // Determina la data di riferimento per l'intervallo: quella dell'ultimo journey (per trip_id) se disponibile, altrimenti oggi
      let referenceDate = new Date();

      if (this.journeys && this.journeys.length > 0) {
        // Ordina i journey per trip_id (decrescente) per prendere quello con ID più alto
        const sortedJourneys = [...this.journeys].sort(
          (a, b) => b.trip_id - a.trip_id
        );

        // Prendi il journey con trip_id più alto (il primo dopo l'ordinamento)
        const lastJourney = sortedJourneys[0];

        if (lastJourney && lastJourney.end_time) {
          referenceDate = new Date(lastJourney.end_time);
          console.log(
            `Usando la data del journey con trip_id più alto (${lastJourney.trip_id}) come riferimento: ${referenceDate}`
          );
        } else {
          console.log('Nessun end_time valido trovato, uso la data odierna come riferimento');
        }
      } else {
        console.log('Nessun journey disponibile, uso la data odierna come riferimento');
      }

      // Imposta l'intervallo sull'intera giornata di riferimento: 00:00:00 - 23:59:59
      const startOfDay = new Date(referenceDate);
      startOfDay.setHours(0, 0, 0, 0);
      const endOfDay = new Date(referenceDate);
      endOfDay.setHours(23, 59, 59, 0);

      this.paramStartTime = formatDate(startOfDay, 'yyyy-MM-dd HH:mm:ss', 'en-US');
      this.paramEndTime = formatDate(endOfDay, 'yyyy-MM-dd HH:mm:ss', 'en-US');
    }

    // Call the getJourneyList method
    const subscription = this.journeyService
      .getJourneyList(this.paramUid, this.paramStartTime, this.paramEndTime)
      .pipe(
        // Aggiungi takeUntil per cancellare la richiesta quando necessario
        takeUntil(this.cancelPendingRequests$)
      )
      .subscribe({
        next: (data) => {
          // Controllo se i dati ricevuti sono uguali a quelli già presenti
          // const journeysChanged = this.areJourneysChanged(
          //   currentJourneys,
          //   data
          // );

          this.journeys = data;
          this.isLoading = false;
          console.log('Journeys loaded:', this.journeys.length);

          // Carica i dettagli solo se i journey sono cambiati
          // if (journeysChanged) {
          console.log('Journey data changed, loading details...');
          // Resetta completamente la mappa e i dati quando ci sono cambiamenti

          this.loadJourneyDetails();

          // Carica la configurazione e i dati dei KPI
          this.loadJourneyKpiConfig();

          // Dopo aver caricato i dettagli, se avevamo dei trip_id visualizzati,
          // mostra i punti ad alta frequenza per ciascun trip_id
          if (hasDisplayedTrips) {
            console.log('Ripristino visualizzazione punti ad alta frequenza');

            // Ripristina i trip_id visualizzati se necessario
            if (this.displayedTripIds.length === 0) {
              this.displayedTripIds = currentDisplayedTripIds;
            }

            // Attendi che i dati siano caricati, poi ripristina i punti
            setTimeout(() => {
              // Cicla su ogni trip_id e richiama displayHighFrequencyPoints
              currentDisplayedTripIds.forEach((tripId) => {
                console.log(
                  `Ripristino punti ad alta frequenza per journey ${tripId}`
                );
                this.displayHighFrequencyPoints(tripId);
              });
            }, 500);
          }
          // } else {
          //   console.log('Journey data unchanged, skipping details reload');
          //   this.showInfoMessage(this.translate.instant(
          //     'mapPage.noDataRefresh')
          //   );

          // Nascondi l'indicatore di caricamento se era visibile
          //  this.showRouteLoadingIndicator(false);
          //}
        },
        error: (err) => {
          this.error = 'Failed to load journeys: ' + err.message;
          this.isLoading = false;
          console.error('Error loading journeys:', err);

          // Nascondi l'indicatore di caricamento in caso di errore
          this.showRouteLoadingIndicator(false);
        },
        complete: () => {
          // Rimuovi questa sottoscrizione dall'elenco quando completata
          const index = this.activeHttpRequests.indexOf(subscription);
          if (index > -1) {
            this.activeHttpRequests.splice(index, 1);
          }
        }
      });
  }

  /**
   * Verifica se i journey sono cambiati confrontando gli array
   * @param oldJourneys Array dei journey precedenti
   * @param newJourneys Array dei journey nuovi
   * @returns true se i journey sono cambiati, false altrimenti
   */
  private areJourneysChanged(
    oldJourneys: Journey[],
    newJourneys: Journey[]
  ): boolean {
    // Controllo rapido sulla lunghezza
    if (oldJourneys.length !== newJourneys.length) {
      return true;
    }

    // Se la lunghezza è uguale, confronta gli ID e l'orario dei journey
    // Ordina gli array per trip_id per un confronto più accurato
    const sortedOld = [...oldJourneys].sort((a, b) => a.trip_id - b.trip_id);
    const sortedNew = [...newJourneys].sort((a, b) => a.trip_id - b.trip_id);

    // Confronta ogni journey
    for (let i = 0; i < sortedOld.length; i++) {
      // Verifica trip_id, orari e coordinate
      if (
        sortedOld[i].trip_id !== sortedNew[i].trip_id ||
        sortedOld[i].start_time !== sortedNew[i].start_time ||
        sortedOld[i].end_time !== sortedNew[i].end_time ||
        sortedOld[i].start_lat !== sortedNew[i].start_lat ||
        sortedOld[i].start_lon !== sortedNew[i].start_lon ||
        sortedOld[i].end_lat !== sortedNew[i].end_lat ||
        sortedOld[i].end_lon !== sortedNew[i].end_lon
      ) {
        return true;
      }
    }

    // Se arriviamo qui, i journey sono identici
    return false;
  }

  //   loadJourneys(): void {
  //     // Dispatch l'azione loadJourneys allo store
  //     this.journeyStore.dispatch(JourneyActions.loadJourneys({
  //       uid: this.paramUid,
  //       startTime: this.paramStartTime,
  //       endTime: this.paramEndTime
  //     }));
  //   //  console.log('loadJourneys');
  //     // Sottoscrivi ai journey dallo store
  // //    console.log('journeys$', this.journeys$);
  //     if (this.journeys$) {
  //       this.journeys$.pipe(
  //         takeUntil(this.destroy$), // Aggiungi un Subject<void> destroy$ per la gestione delle sottoscrizioni
  //         tap(journeys => {
  //           //console.log('Journeys ricevuti:', journeys.length, journeys); // Questo mostrerà sempre i journey, anche se vuoti
  //         }),
  //         filter(journeys => journeys.length > 0) // Procedi solo quando ci sono journey
  //       ).subscribe(journeys => {
  //         this.journeys = journeys;
  //        // console.log('Journeys caricati dallo store:', journeys.length);
  //       });
  //     } else {
  //       console.warn('journeys$ is undefined');
  //     }

  //     // Monitora gli errori
  //     this.journeyError$?.pipe(
  //       takeUntil(this.destroy$),
  //       filter(error => !!error)
  //     ).subscribe(error => {
  //       this.error = `Errore nel caricamento dei journey: ${error.message || 'Errore sconosciuto'}`;
  //       console.error('Errore journey:', error);
  //     });
  //   }
  loadJourneyDetails(): void {
    this.isLoading = true;
    this.error = null;
    // Mostra l'indicatore di caricamento
    //  this.showRouteLoadingIndicator(true);
    // Reset esplicito degli array di waypoints
    this.waypoints = [];

    // IMPORTANTE: qui vogliamo sempre l'ISO string con 'Z' (NON il formato piatto 'yyyy-MM-dd HH:mm:ss').
    // getJourneyDetails() passa questo valore a convertToTimezone(), che fa new Date(valore) al suo
    // interno: senza 'Z' verrebbe riletto come ora LOCALE (doppio shift errato). Con 'Z' invece
    // new Date() lo interpreta sempre correttamente come UTC, ovunque venga riletto.
    //
    // journeys[0].start_time arriva dal backend "naive" (es. "2026-07-07T10:08:02", senza 'Z') e
    // rappresenta l'ora nel fuso PREFERITO DALL'UTENTE (varUtc), non l'ora locale del browser:
    // new Date(str).toISOString() da solo assumerebbe erroneamente "locale browser" e, se il
    // browser non è sullo stesso fuso dell'utente, non applicherebbe nessuno shift. Per questo usiamo
    // parseWallClockInTimeZone() per interpretarla esplicitamente nel fuso corretto.
    const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
    const varUtc = userInfo.user?.preferences.timezone.label || 'utc';

    let startTime = new Date(this.paramStartTime).toISOString();
    if (this.journeys && this.journeys.length > 0 && this.journeys[0].start_time) {
      startTime = parseWallClockInTimeZone(this.journeys[0].start_time, varUtc).toISOString();
      console.log(`Usando start_time del primo journey: ${startTime}`);
    }
    console.log('siamo qui con starttime', startTime);


    // Call the getJourneyList method
    const subscription = this.journeyService
      .getJourneyDetails(this.paramUid, startTime, this.paramEndTime)
      .pipe(
        // Aggiungi takeUntil per cancellare la richiesta quando necessario
        takeUntil(this.cancelPendingRequests$)
      )
      .subscribe({
        next: (data) => {
          this.journeysDetails = data;
          this.waypointData = [...data];
          //  this.isLoading = false;
          // console.log('Journeys details:', this.journeysDetails);

          // Clear existing waypoints
          //  this.waypoints = [];

          // Populate waypoints with start and end coordinates from each journey
          data.forEach((journey) => {
            if (journey.gps_lat != null && journey.gps_lon != null) {
              // Add the point to waypoints

              this.waypoints.push([journey.gps_lat, journey.gps_lon]);
            }
          });

          console.log('Waypoints viaggio:', this.waypoints);

          // After waypoints are populated, initialize the map
          // After waypoints are populated, initialize the map
          if (this.map) {
            // Prima di disegnare, rimuovi i layer esistenti
            this.clearMapWaypoints();
            // ROUTING DISABLED: drawRoute non più utilizzato

             this.drawRoute();
            // L'indicatore verrà nascosto alla fine del metodo percorso()
          } else {
            // Se non c'è una mappa, nascondi comunque l'indicatore
            this.isLoading = false;
            this.showRouteLoadingIndicator(false);
          }
        },
        error: (err) => {
          this.error = 'Failed to load journeys: ' + err.message;
          this.isLoading = false;
          console.error('Error loading journeys:', err);

          this.showRouteLoadingIndicator(false);
        },
        complete: () => {
          // Rimuovi questa sottoscrizione dall'elenco quando completata
          const index = this.activeHttpRequests.indexOf(subscription);
          if (index > -1) {
            this.activeHttpRequests.splice(index, 1);
          }
        }
      });
  }

  /**
   * Carica la configurazione dei KPI per i journey
   */
  loadJourneyKpiConfig(): void {
    // Ottieni customer dallo store e user info
    this.store.pipe(
      select(selectedCustomerSelector),
      take(1)
    ).subscribe(selectedCustomer => {
      const currentUserInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();

      if (!selectedCustomer?.uid || !currentUserInfo?.user?.uid) {
        console.warn('loadJourneyKpiConfig: Informazioni customer o utente non disponibili');
        return;
      }

      // Invalida la cache per forzare il ricaricamento dei dati aggiornati
      this.catalogService.invalidateJourneyCache(selectedCustomer.uid);

      this.catalogService.getJourneyConfigFromAPI(selectedCustomer.uid, currentUserInfo.user.uid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (configs) => {
          console.log('Configurazione KPI journey caricata:', configs);

          // Reset degli array
          this.journeyKpiList = [];
          this.journeyKpiCustomList = [];

          if (configs && configs.length > 0) {
            const latestConfig = configs[0];
            const kpiJourneyData = latestConfig?.config?.mappingData?.kpiJourney;

            if (kpiJourneyData && Array.isArray(kpiJourneyData)) {
              // Dividi i KPI in base allo scope
              kpiJourneyData.forEach((kpi: any) => {
                if (kpi.scope === 'kpi') {
                  this.journeyKpiList.push(kpi);
                } else if (kpi.scope === 'kpi_custom') {
                  this.journeyKpiCustomList.push(kpi);
                }
              });

              // Ordina i KPI in base al campo order
              this.journeyKpiList.sort((a, b) => (a.order || 0) - (b.order || 0));
              this.journeyKpiCustomList.sort((a, b) => (a.order || 0) - (b.order || 0));

              console.log(`KPI journey caricati - kpi: ${this.journeyKpiList.length}, kpi_custom: ${this.journeyKpiCustomList.length}`);

              // Carica i dati dei KPI dopo aver ottenuto la configurazione
              this.loadKpiData();
            }
          }
        },
        error: (error) => {
          console.error('Errore nel caricamento della configurazione KPI journey:', error);
        }
      });
    });
  }

  /**
   * Carica i dati dei KPI per i journey
   */
  private loadKpiData(): void {
    // Ottieni customer dallo store
    this.store.pipe(
      select(selectedCustomerSelector),
      take(1)
    ).subscribe(selectedCustomer => {
      if (!selectedCustomer?.uid || !this.selectedVehicle?.uid) {
        console.warn('loadKpiData: Informazioni customer o veicolo non disponibili');
        return;
      }

    // IMPORTANTE: qui vogliamo l'intera giornata 00:00:00 - 23:59:59 così come sono in
    // paramStartTime/paramEndTime, SENZA convertirle in UTC: il fuso di riferimento per queste ore
    // è già dichiarato a parte nel campo 'timezone: varUtc' delle request qui sotto. Convertendo in
    // UTC (es. con .toISOString()) le ore si sposterebbero fuori da 00:00:00/23:59:59.
    // Formato richiesto dal backend: 'T' tra data e ora invece dello spazio.
    const fromDate = this.paramStartTime.replace(' ', 'T');
    const toDate = this.paramEndTime.replace(' ', 'T');

      const requests: (Observable<any> | null)[] = [];
 const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
  const varUtc = userInfo.user?.preferences.timezone.label || 'utc';
      // Richiesta per segnali (scope = 'kpi') - usa name
      if (this.journeyKpiList.length > 0) {
        const signalRequest: WidgetRequest = {
          uidCustomer: selectedCustomer.uid,
          signals: this.journeyKpiList.map(kpi => kpi.name),
          fromDate: fromDate,
          toDate: toDate,
          timezone: varUtc,
          aggregate: false,
          uidVehicles: [this.selectedVehicle.uid]
        };

        requests.push(
          this.kpiCrudService.getSignalData(signalRequest)
        );
      } else {
        requests.push(null);
      }

      // Richiesta per KPI compositi (scope = 'kpi_custom') - usa uid
      if (this.journeyKpiCustomList.length > 0) {
        const compositeRequest: WidgetRequest = {
          uidCustomer: selectedCustomer.uid,
          uidsKpi: this.journeyKpiCustomList.map(kpi => kpi.uid),
          fromDate: fromDate,
          toDate: toDate,
          timezone: varUtc,
          aggregate: false,
          uidVehicles: [this.selectedVehicle.uid]
        };

        requests.push(
          this.kpiCrudService.getCompositeSignalData(compositeRequest)
        );
      } else {
        requests.push(null);
      }

      // Esegui le richieste solo se ci sono dati da caricare
      const validRequests = requests.filter(req => req !== null);

      if (validRequests.length === 0) {
        console.log('Nessun KPI da caricare');
        return;
      }

      forkJoin(validRequests)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
        next: (responses: any[]) => {
          console.log('Dati KPI caricati:', responses);

          // Processo le risposte
          let responseIndex = 0;

          // Prima risposta: signal (scope = 'kpi', se presente)
          if (requests[0] !== null && responses[responseIndex]) {
            const signalData = responses[responseIndex];
            if (signalData.result === 'OK' && signalData.data && Array.isArray(signalData.data)) {
              console.log(`Dati segnali caricati: ${signalData.data.length} record`);

              // Mappa i dati ai KPI corrispondenti usando result_signal = kpi.name
              signalData.data.forEach((dataItem: any) => {
                const kpi = this.journeyKpiList.find(k => k.name === dataItem.result_signal);
                if (kpi) {
                  // Usa result_value come valore principale
                  kpi.value = dataItem.result_value;
                  // Opzionalmente salva anche il valore medio
                  if (dataItem.result_avg_value) {
                    kpi.avgValue = dataItem.result_avg_value;
                  }
                }
              });

              console.log('KPI aggiornati:', this.journeyKpiList);
            }
            responseIndex++;
          }

          // Seconda risposta: compositeSignal (scope = 'kpi_custom', se presente)
          if (requests[1] !== null && responses[responseIndex]) {
            const compositeData = responses[responseIndex];
            console.log('Risposta completa KPI custom:', compositeData);

            // La risposta per KPI custom è direttamente un array, non ha il wrapper { result, data }
            if (Array.isArray(compositeData) && compositeData.length > 0) {
              console.log(`Dati KPI compositi caricati: ${compositeData.length} record`);

              // Mappa i dati ai KPI corrispondenti usando uid
              compositeData.forEach((dataItem: any) => {
                console.log('Cercando KPI con uid:', dataItem.uid);
                const kpi = this.journeyKpiCustomList.find(k => k.uid === dataItem.uid);
                console.log('KPI trovato:', kpi);
                if (kpi) {
                  // Usa value come valore principale
                  kpi.value = dataItem.value;
                  console.log('Valore assegnato:', kpi.value);
                }
              });

              console.log('KPI Custom aggiornati:', this.journeyKpiCustomList);
            } else {
              console.warn('Risposta KPI custom non è un array valido o è vuoto');
            }
          }
        },
        error: (error) => {
          console.error('Errore nel caricamento dei dati KPI:', error);
        }
      });
    });
  }

  // loadJourneyDetails(): void {
  //   // Dispatch l'azione loadJourneyDetails allo store

  //   this.journeyStore.dispatch(JourneyActions.loadJourneyDetails({
  //     uid: this.paramUid,
  //     startTime: this.paramStartTime,
  //     endTime: this.paramEndTime
  //   }));

  //   // Mostra l'indicatore di caricamento
  //   this.showRouteLoadingIndicator(true);

  //   // Sottoscrivi ai dettagli dallo store con migliore gestione del completamento
  //   this.journeyDetails$?.pipe(
  //     takeUntil(this.destroy$),
  //     tap(journeys => {
  //       console.log('JourneysDetails  ricevuti:', journeys.length, journeys); // Questo mostrerà sempre i journey, anche se vuoti

  //     }),
  //     // Attendi che ci siano dettagli
  //     filter(details => details.length > 0),
  //     // Prendi solo il primo emissione dopo il filtro (importante!)
  //     // take(1),
  //     // Gestisci eventuali errori di rendering
  //     catchError(error => {
  //       console.error('Errore durante l\'elaborazione dei dettagli:', error);
  //       this.showRouteLoadingIndicator(false);
  //       this.showErrorMessage('Si è verificato un errore durante la preparazione del percorso');
  //       return EMPTY;
  //     })
  //   ).subscribe(details => {
  //     console.log('Caricamento dettagli journey completato:', details.length);
  //     this.journeysDetails = details;

  //     // Popola i waypoints dalle coordinate
  //     this.waypoints = details
  //       .filter(journey => journey.gps_lat != null && journey.gps_lon != null)
  //       .map(journey => [journey.gps_lat!, journey.gps_lon!] as [number, number]);

  //     //console.log('Waypoints viaggio:', this.waypoints.length);

  //     // Disegna il percorso solo ora che i dati sono completi
  //     if (this.map && this.journeysDetails.length > 0) {
  //       // Piccolo timeout per garantire che l'UI si aggiorni
  //       setTimeout(() => {
  //         this.drawRoute();
  //       }, 100);
  //     } else {
  //       this.showRouteLoadingIndicator(false);
  //       if (this.waypoints.length === 0) {
  //         this.showInfoMessage('Nessun punto di percorso trovato per il periodo selezionato');
  //       }
  //     }
  //   });
  // }
  ngAfterViewInit(): void {
    // INIZIALIZZO LA MAPPA BASE
    console.log('🔥 SCATTA ngAfterViewInit - Componente inizializzato');
    this.initMap();



    // Subscribe to active page changes
    console.log('🔍 Sottoscrizione a activePage$ - Inizio ascolto cambio pagina');
    this.mapContainerService.activePage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((activePage) => {
        console.log('📄 Ricevuto cambio pagina:', activePage, '- isInitialized:', this.isInitialized);
        // Avoid setting the page the first time
        if (!this.isInitialized) {
          console.log('⚠️ Prima inizializzazione - Salto il caricamento dei dati');
          this.isInitialized = true;
       //   return;
        }

        console.log('✅ Pagina cambiata a:', activePage);
        if (activePage === 'actia-journey') {
          console.log('🎯 PAGINA ACTIA-JOURNEY ATTIVA - Preparo caricamento dati');
          // console.log('QUI DEVO CONTROOLLARE E NON RICARICARE');
          // FZ comntrollo che se torno sullo stesso veicolo non ricarico
          // ma dorvrò aggiornare solo parte in progress

          // Forza il ridimensionamento della mappa quando la pagina diventa attiva
          setTimeout(() => {
            console.log('📐 Triggering window resize event per cambio pagina');
            window.dispatchEvent(new Event('resize'));
          }, 50);

          setTimeout(() => {
            if (this.map) {
              console.log('🗺️ Ridimensionamento mappa per cambio pagina attiva');
              this.map.invalidateSize();
            }
          }, 150);

          // Load journey data only when on the journey page
          console.log('🚀 CHIAMATA loadJourneys(false) - Caricamento journey per pagina attiva');
          this.loadJourneys(false);
          // this.loadJourneyDetails();
        } else {
          console.log('❌ Pagina NON è actia-journey, salto il caricamento');
        }
      });    // // //     Aggiungi un listener per il livello di zoom
    // this.map!.on('zoomend', () => {
    //   const currentZoom = this.map!.getZoom();
    // //  console.log('Livello di zoom attuale:', currentZoom);

    //   if (currentZoom >= this.zoomThreshold) {
    //     // Aggiungi il gruppo dei waypoint se non è già visibile
    //     if (!this.map!.hasLayer(this.journeyWaypointsGroup)) {
    //       this.map!.addLayer(this.journeyWaypointsGroup);
    //       console.log('Waypoints aggiunti alla mappa.');
    //     }
    //   } else {
    //     // Rimuovi il gruppo dei waypoint se è visibile
    //     if (this.map!.hasLayer(this.journeyWaypointsGroup)) {
    //       this.map!.removeLayer(this.journeyWaypointsGroup);
    //       console.log('Waypoints rimossi dalla mappa.');
    //     }
    //   }
    // });

    // Aggiorna le icone di stato tramite il servizio
    // this.mapService.updateStatusIcons(this.isLoading$, this.isRefreshing$)
    // this.mapService.updateStatusIcons(this.isLoading$, this.isRefreshing$);
    // AGGIORNO LA CARDO CON I TOTALI
    // Aggiungi un listener per il ridimensionamento della finestra
    // fromEvent(window, 'resize')
    //   .pipe(
    //   debounceTime(200),
    //   takeUntil(this.destroy$)
    // )
    //   .subscribe(() => {
    //     this.resizeMap();
    //   });

    // Configurazione di ResizeObserver per rilevare cambiamenti nel container della mappa
    this.setupResizeObserver();

    // Listener per gli eventi di apertura/chiusura della sidebar
    if (this.sidenav) {
      this.sidenav.openedChange
        .pipe(takeUntil(this.destroy$))
        .subscribe((opened) => {
          console.log('Sidebar opened state changed:', opened);

          // Simula un evento di ridimensionamento della finestra
          setTimeout(() => {
            console.log('Triggering window resize event per sidebar change');
            window.dispatchEvent(new Event('resize'));
          }, 50);

          // Ridimensionamento aggiuntivo dopo la transizione CSS
          setTimeout(() => {
            if (this.map) {
              console.log('Ridimensionamento mappa per cambio stato sidebar');
              this.map.invalidateSize();
              // Forza un secondo ridimensionamento
              setTimeout(() => {
                if (this.map) {
                  this.map.invalidateSize();
                }
              }, 100);
            }
          }, 400); // 400ms per essere sicuri che la transizione sia completata
        });
    }

    // PER GESTIRE IL BREAKPOINT PER MOBILE, SU ACTIA USARE POI IL SEVIZIO E LA VARIBILE AMBIENTE
    this.breakpointObserver
      .observe([Breakpoints.Handset])
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        this.isMobile = result.matches;

        // Se siamo su mobile, imposta la mappa come attiva e chiudi la sidebar
        if (this.isMobile) {
          this.activeFooterButton = 'map';
          // Assicurati che la sidebar sia chiusa su mobile
          if (this.sidenav) {
            this.sidenav.close();
          }
        } else {
          // Su desktop, ripristina il comportamento predefinito
          this.activeFooterButton = 'sidebar';
        }
      });

    // Force the map to recalculate its dimensions after view initialization
    setTimeout(() => {
      if (this.map) {
        this.map.invalidateSize();
        console.log('Map size invalidated after view init');
      }
    }, 100);

    // Avvia l'aggiornamento automatico
    // this.startAutoUpdate();
  }
  private initMap(): void {
    //SETTO MAPPA BASE  E POI AGGIUNGO TUTTO QUELLO CHE MI SERVE NELLA SPECIFICCA PAGIAN DI MAPPA, VEDI TOTALI M
    this.map = this.mapService.initMap(this.mapId, {
      center: [45.0703, 7.6869],
      zoom: 12
    });

    // Forza un ridimensionamento della mappa dopo l'inizializzazione
    setTimeout(() => {
      if (this.map) {
        console.log('Ridimensionamento mappa dopo inizializzazione');
        this.map.invalidateSize();
      }
    }, 100);

    // Disegna il percorso sulla mappa
    // this.drawRoute();
    // AGGIUNGO LO STATO DI CARICAMENTO E AGGIORNAMENTO SULL AMAPPA IN BASSO A SINISTRA
    // this.mapService.addStatusControl();
  }

  showMap(): void {
    this.setActiveFooterButton('map'); // Imposta "map" come attivo
    console.log('Mostra mappa');
    this.sidenav.close(); // Chiude la sidebar

    // Trigger resize event per simulare il ridimensionamento manuale
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);

    // Fai il resize della mappa dopo la chiusura della sidebar
    setTimeout(() => {
      if (this.map) {
        console.log('Eseguendo resize della mappa dopo showMap');
        this.map.invalidateSize();
      }
    }, 300); // Delay per permettere alla transizione della sidebar di completarsi
  }

  toggleSidebar(): void {
    this.setActiveFooterButton('sidebar'); // Imposta "sidebar" come attivo
    this.sidenav.toggle(); // Apre o chiude la sidebar

    // Trigger resize event per simulare il ridimensionamento manuale
    setTimeout(() => {
      window.dispatchEvent(new Event('resize'));
    }, 50);

    // Se stiamo passando alla vista mappa (sidebar chiusa), fai il resize della mappa
    setTimeout(() => {
      if (!this.sidenav.opened && this.map) {
        console.log('Eseguendo resize della mappa dopo chiusura sidebar');
        this.map.invalidateSize();
      }
    }, 300); // Delay per permettere alla transizione della sidebar di completarsi
  }
  setActiveFooterButton(button: string): void {
    this.activeFooterButton = button;
  }

  resizeMap(): void {
    console.log('Resizing map');
    if (this.map) {
      console.log('Resizing map IN');
      this.map.invalidateSize();
    }
  }

  // ROUTING DISABLED: Metodo drawRoute non più utilizzato per HERE API

  private drawRoute(): void {
    if (!this.map) return;

    //this.percorso(false);
    console.log('chamo percorso');
    this.percorso(true);
  }


  // ROUTING DISABLED: Metodo percorso non più utilizzato per HERE API

  percorso(showArrows: boolean = true) {
    // Pulisci le polyline esistenti prima di disegnare nuove
   // this.clearMapPolylines();
    let departureTime: string | null = null;
    let depTimeinurl = '';

    if (this.journeys && this.journeys.length > 0) {
      // Trova il primo journey con start_time valido
      const journeyWithDate = this.journeys.find((j) => j.start_time);

      if (journeyWithDate && journeyWithDate.start_time) {
        // Converti il timestamp nel formato ISO richiesto da HERE API
        const journeyDate = new Date(journeyWithDate.start_time);
        departureTime = journeyDate.toISOString().replace('.000Z', '');
        depTimeinurl = `&departureTime=${departureTime}`;
        console.log(`Usando data di partenza dal journey: ${departureTime}`);
      } else {
        console.log(
          'Nessuna data di partenza valida trovata nei journey, uso valore di default'
        );
      }
    }

    // const importUrl =
    //   `https://router.hereapi.com/v8/import?apikey=${environment.hereApiKey}` +
    //   `&return=polyline,summary,actions,instructions,potentialTimeDependentViolations,truckRoadTypes,incidents,routeHandle,turnByTurnActions,routingZones,passthrough,elevation&spans=length,truckAttributes,stateCode,routingZones,gates,incidents,truckRoadTypes,segmentId,segmentRef&transportMode=truck&allow[hov]=true&allow[hot]=true&lang=it-it${depTimeinurl}`;

    if (this.waypoints.length === 0) {
      this.isLoading = false;
      this.showRouteLoadingIndicator(false);
      console.log('Nessun waypoint disponibile per il percorso. 1');
      this.showInfoMessage(
        this.translate.instant('mapPage.noRouteFound'),
        5000
      );
      console.error('Nessun waypoint disponibile per il percorso.');

      return;
    }

    // // Crea un gruppo per le polyline che conterrà tutti i segmenti
    // const polylineGroup = L.featureGroup();

    // Mappa tutti i waypoints in formato { lat, lng }
    const allWaypoints = this.waypoints.map(([lat, lng]) => ({ lat, lng }));
    // this.routeSummaries = [];
    // // Verifica se la traccia ha più di 100 punti
    // if (allWaypoints.length <= 100) {
    //   // Se la traccia è corta, usa l'approccio originale
    //   this.processRouteSegment(
    //     allWaypoints,
    //     0,
    //     polylineGroup,
    //     importUrl,
    //     showArrows
    //   );
    // } else {
      // Se la traccia è lunga, dividi in segmenti da 100 punti
      // console.log(
      //   `Traccia lunga (${allWaypoints.length} punti), suddivisione in blocchi da 100 punti`
      // );

      // Dividi i waypoints in blocchi da 100, con sovrapposizione per garantire continuità
    //   const segments: Array<{ lat: number; lng: number }[]> = [];
    //   for (let i = 0; i < allWaypoints.length; i += 99) {
    //     // 99 per lasciare spazio alla sovrapposizione
    //     // Se non è il primo segmento, includi l'ultimo punto del segmento precedente
    //     const start = i === 0 ? 0 : i - 1;
    //     const end = Math.min(i + 99, allWaypoints.length);
    //     segments.push(allWaypoints.slice(start, end));
    //   }

    //   console.log(`Traccia suddivisa in ${segments.length} segmenti`);

    //   // Processa sequenzialmente i segmenti
    //   //this.processSegmentsSequentially(segments, 0, polylineGroup, importUrl, showArrows);

    //   this.processSegmentsInParallel(
    //     segments,
    //     polylineGroup,
    //     importUrl,
    //     showArrows
    //   );
    // }

    this.waypointsVisible = true;
    this.highFrequencyGlobalVisible = false;
    this.highFrequencyGlobalVisibleNoHere = false;
        this.finalizeRouteDisplay()
  }


  // Funzione per processare sequenzialmente i segmenti
  // private processSegmentsSequentially(
  //   segments: Array<{ lat: number; lng: number }[]>,
  //   currentIndex: number,
  //   polylineGroup: L.FeatureGroup,
  //   importUrl: string,
  //   showArrows: boolean
  // ): void {
  //   // Controllo di sicurezza: se abbiamo processato tutti i segmenti, finalizza
  //   if (currentIndex >= segments.length) {
  //     // Modifica qui: rimuovi l'aggiunta del polylineGroup alla mappa poiché è già stato fatto
  //     // e chiama solo finalizeRouteDisplay per i waypoints e il centering
  //     this.finalizeRouteDisplay(polylineGroup, false); // Passa false per indicare che le polyline sono già sulla mappa
  //     return;
  //   }

  //   console.log(
  //     `Processando segmento ${currentIndex + 1}/${segments.length} (${
  //       segments[currentIndex].length
  //     } punti)`
  //   );

  //   // Crea l'oggetto traccia per questo segmento
  //   const traccia = { trace: segments[currentIndex] };

  //   // Chiama l'API per questo segmento
  //   const subscription = this.httpWithoutInterceptor
  //     .post(importUrl, traccia)
  //     .pipe(takeUntil(this.cancelPendingRequests$))
  //     .subscribe({
  //       next: (response: any) => {
  //         // Processa la risposta per questo segmento
  //         if (
  //           response.routes &&
  //           response.routes[0] &&
  //           response.routes[0].sections
  //         ) {
  //           this.processRouteResponse(response, polylineGroup, showArrows);
  //         } else {
  //           console.warn(
  //             `Nessuna route trovata per il segmento ${currentIndex + 1}`
  //           );
  //         }

  //         // Procedi con il prossimo segmento
  //         subscription.unsubscribe();
  //         this.processSegmentsSequentially(
  //           segments,
  //           currentIndex + 1,
  //           polylineGroup,
  //           importUrl,
  //           showArrows
  //         );
  //       },
  //       error: (error) => {
  //         console.error(
  //           `Errore durante l'elaborazione del segmento ${currentIndex + 1}:`,
  //           error
  //         );

  //         // Anche in caso di errore, prova a procedere con il prossimo segmento
  //         subscription.unsubscribe();
  //         this.processSegmentsSequentially(
  //           segments,
  //           currentIndex + 1,
  //           polylineGroup,
  //           importUrl,
  //           showArrows
  //         );
  //       },
  //       complete: () => {
  //         // Rimuovi questa sottoscrizione dall'elenco quando completata
  //         const index = this.activeHttpRequests.indexOf(subscription);
  //         if (index > -1) {
  //           this.activeHttpRequests.splice(index, 1);
  //         }
  //       }
  //     });

  //   // Aggiungi la sottoscrizione all'elenco di quelle attive
  //   this.activeHttpRequests.push(subscription);
  // }

  // ROUTING DISABLED: Metodo processRouteSegment non più utilizzato per HERE API
  /*
  // Funzione per processare un singolo segmento
  private processRouteSegment(
    waypoints: { lat: number; lng: number }[],
    segmentIndex: number,
    polylineGroup: L.FeatureGroup,
    importUrl: string,
    showArrows: boolean
  ): void {
    console.log(`Processando traccia di ${waypoints.length} punti`);

    const traccia = { trace: waypoints };

    const subscription = this.httpWithoutInterceptor
      .post(importUrl, traccia)
      .pipe(takeUntil(this.cancelPendingRequests$))
      .subscribe({
        next: (response: any) => {
          if (
            !response.routes ||
            !response.routes[0] ||
            !response.routes[0].sections
          ) {
            this.showRouteLoadingIndicator(false);
            this.isLoading = false;
            console.log('Nessun waypoint disponibile per il percorso. 2');
            this.showInfoMessage(
              this.translate.instant('mapPage.noRouteFound'),
              5000
            );
            return;
          }

          console.log('RESPONSE', response);
          // this.routeSummaries = [];

          if (response && response.routes) {
            response.routes.forEach((route) => {
              if (route.sections) {
                this.routeSummaries.push(
                  ...route.sections.map((section) => section.summary)
                );
              }
            });
          }

          console.log('Route summaries:', this.routeSummaries);
          // Processa la risposta
          this.processRouteResponse(response, polylineGroup, showArrows);

          // Finalizza la visualizzazione del percorso
          this.finalizeRouteDisplay(polylineGroup);

          subscription.unsubscribe();
        },
        error: (error) => {
          console.error("Errore durante l'importazione del percorso:", error);
          this.showRouteLoadingIndicator(false);
          this.isLoading = false;
          subscription.unsubscribe();
        },
        complete: () => {
          // Rimuovi questa sottoscrizione dall'elenco quando completata
          const index = this.activeHttpRequests.indexOf(subscription);
          if (index > -1) {
            this.activeHttpRequests.splice(index, 1);
          }
        }
      });

    // Aggiungi la sottoscrizione all'elenco di quelle attive
    this.activeHttpRequests.push(subscription);
  }
  */
  // ROUTING DISABLED: Metodo processRouteResponse non più utilizzato per HERE API
  /*
  private processRouteResponse(
    response: any,
    polylineGroup: L.FeatureGroup,
    showArrows: boolean,
    lineColor: string = '#3388ff',
    lineWeight: number = 8,
    lineOpacity: number = 0.7
  ): void {
    response.routes[0].sections.forEach(
      (section: any, sectionIndex: number) => {
        const encodedPolyline = section.polyline;
        if (!encodedPolyline) {
          console.warn(`Polyline mancante nella sezione ${sectionIndex}`);
          return; // Salta alla prossima sezione
        }

        const decodedResult = FlexiblePolyline.decode(encodedPolyline);
        const sectionCoordinates = decodedResult.polyline;

        // Aggiungi le coordinate di questa sezione all'array complessivo
        this.allRoutesCoords = [...this.allRoutesCoords, ...sectionCoordinates];

        // Crea una polyline per questa sezione
        const line = L.polyline(sectionCoordinates, {
          color: lineColor,
          weight: lineWeight,
          opacity: lineOpacity
        });

        // Aggiungi direttamente alla mappa oltre che al gruppo
        line.addTo(this.map!);
        line.addTo(polylineGroup);

        // Crea sempre il decoratore ma aggiungilo solo se showArrows è true
        const decorator = L.polylineDecorator(line, {
          patterns: [
            {
              offset: '5%',
              repeat: '10%',
              symbol: L.Symbol.arrowHead({
                pixelSize: 12,
                polygon: true,
                pathOptions: {
                  fillOpacity: 0.8,
                  weight: 0,
                  color: 'black'
                }
              })
            }
          ]
        });

        // Aggiungi il decoratore al gruppo delle frecce
        this.arrowDecoratorsGroup.addLayer(decorator);

        // Mostra il decoratore solo se richiesto
        if (showArrows) {
          decorator.addTo(this.map!);
        }

        // Aggiorna l'indicatore di caricamento per mostrare il progresso
        this.showLoadingProgress(sectionIndex, 1);
      }
    );

    // Se showArrows è true, aggiungi l'intero gruppo alla mappa
    if (showArrows && !this.map!.hasLayer(this.arrowDecoratorsGroup)) {
      this.arrowDecoratorsGroup.addTo(this.map!);
    }
  }
  */
  // private showLoadingProgress(sectionIndex: number): void {
  //   const indicator = document.getElementById('route-loading-indicator');
  //   if (indicator) {
  //     const progressText = indicator.querySelector('span');
  //     if (progressText) {
  //       progressText.textContent = `${this.translate.instant('mapPage.routeCalculation')} (${sectionIndex + 1})`;
  //     }
  //   }
  // }

  private showLoadingProgress(completed: number, total: number): void {
    const percentage = Math.round((completed / total) * 100);

    // Aggiorna la progress bar della mappa (esistente)
    const indicator = document.getElementById('route-loading-indicator');
    if (indicator) {
      const progressText = indicator.querySelector('span');
      if (progressText) {
        progressText.textContent = `${this.translate.instant(
          'mapPage.routeCalculation'
        )} (${completed}/${total} - ${percentage}%)`;
      }
    }

    // Aggiorna la progress bar della sidebar mobile (nuovo)
    if (this.isMobile) {
      this.loadingProgress = percentage;
      this.loadingMessage = `${this.translate.instant(
        'mapPage.routeCalculation'
      )} (${completed}/${total})`;
      this.cdr.detectChanges(); // Forza l'aggiornamento della vista
    }
  }

  // Funzione per finalizzare la visualizzazione del percorso
  private finalizeRouteDisplay(

  ): void {
    if (!this.map) return;

    console.log('🎯 finalizeRouteDisplay - Inizio finalizzazione percorso');
    console.log('📍 waypointsVisible:', this.waypointsVisible);
    console.log('📊 journeysDetails length:', this.journeysDetails?.length || 0);

    // Aggiungi il gruppo alla mappa solo se richiesto

    let waypointFromJourney = this.getJourneyCoordinates();

    // Prima di aggiungere i nuovi waypoint, rimuovi quelli esistenti
    if (this.waypointDotsGroup && this.map!.hasLayer(this.waypointDotsGroup)) {
      console.log('🗑️ Rimozione waypoint dots esistenti');
      this.map!.removeLayer(this.waypointDotsGroup);
      this.waypointDotsGroup = null;
    }

    // Aggiungi i waypoint come pallini
    console.log('🔘 Creazione waypoint dots...');
    this.waypointDotsGroup = this.addWaypointDots(this.journeysDetails);
    console.log('✅ Waypoint dots creato:', !!this.waypointDotsGroup);

    if (this.waypointsVisible) {
      console.log('👁️ Waypoints visibili - Aggiunta alla mappa');
      this.waypointDotsGroup.addTo(this.map!);
    } else {
      console.log('❌ Waypoints NON visibili - Forzo comunque l\'aggiunta per debug');
      this.waypointDotsGroup.addTo(this.map!);
    }

    // Gestisci i punti di inizio/fine dei journey
    if (this.journeyEndpointsGroup && this.map) {
      this.map.removeLayer(this.journeyEndpointsGroup);
      this.journeyEndpointsGroup = null;
    }

    this.journeyEndpointsGroup = this.addJourneyEndpoints();
    this.journeyEndpointsGroup.addTo(this.map!);

    // Centra la mappa su tutti gli elementi
    const allLayersGroup = L.featureGroup([

      this.waypointDotsGroup,
      this.journeyEndpointsGroup
    ]);

    this.map!.fitBounds(allLayersGroup.getBounds());

    // Imposta visibilità e stato UI
    this.waypointsVisible = true;
    // this.routePointsVisible = false;
    this.journeyEndpointsVisible = true;
    console.log('✅ Impostato waypointsVisible = true alla fine di finalizeRouteDisplay');
    this.showRouteLoadingIndicator(false);
    // FZ SOLO QUI AGGIUNGO LA TOOLBAR PERCHè COSì HO LA MAPPA DISEGNATA
    this.vehicleService.addToolbar(...this.toolbarButton);
    this.isLoading = false;
  }
  // FZ QUESTO é OK MA ON GETISCE LA SUDDIVISIONE IN 100 WAYPOINT PE ROGNI CHIAMATA

  // percorso(showArrows: boolean = true) {
  //   // Pulisci le polyline esistenti prima di disegnare nuove
  //   this.clearMapPolylines();
  //   let departureTime: string | null = null;
  //   let depTimeinurl = '';
  //   // const importUrl = `https://router.hereapi.com/v8/import?return=polyline,summary&transportMode=truck&apiKey=${environment.hereApiKey}`
  //   if (this.journeys && this.journeys.length > 0) {
  //     // Trova il primo journey con start_time valido
  //     const journeyWithDate = this.journeys.find((j) => j.start_time);

  //     if (journeyWithDate && journeyWithDate.start_time) {
  //       // Converti il timestamp nel formato ISO richiesto da HERE API
  //       const journeyDate = new Date(journeyWithDate.start_time);
  //       departureTime = journeyDate.toISOString().replace('.000Z', '');
  //       depTimeinurl = `&departureTime=${departureTime}`;
  //       console.log(`Usando data di partenza dal journey: ${departureTime}`);
  //     } else {
  //       console.log(
  //         'Nessuna data di partenza valida trovata nei journey, uso valore di default'
  //       );
  //     }
  //   }

  //   const importUrl =
  //     `https://router.hereapi.com/v8/import?apikey=${environment.hereApiKey}` +
  //     `&return=polyline,summary,actions,instructions,potentialTimeDependentViolations,truckRoadTypes,incidents,routeHandle,turnByTurnActions,routingZones,passthrough,elevation&spans=length,truckAttributes,stateCode,routingZones,gates,incidents,truckRoadTypes,segmentId,segmentRef&transportMode=truck&allow[hov]=true&allow[hot]=true&lang=it-it${depTimeinurl}`;

  //   if (this.waypoints.length === 0) {
  //     this.isLoading = false;
  //     this.showRouteLoadingIndicator(false);
  //     this.showInfoMessage(this.translate.instant(
  //       'mapPage.noRouteFound'),

  //       5000
  //     );
  //     console.error('Nessun waypoint disponibile per il percorso.');
  //     return;
  //   }
  //   const traccia = {
  //     trace: this.waypoints.map(([lat, lng]) => ({ lat, lng }))
  //   };

  //   // Crea un gruppo per le polyline
  //   const polylineGroup = L.featureGroup();

  //   const subscription = this.httpWithoutInterceptor
  //     .post(importUrl, traccia)
  //     .pipe(takeUntil(this.cancelPendingRequests$))
  //     .subscribe({
  //       next: (response: any) => {
  //         //  console.log('Response completa:', response);

  //         if (
  //           !response.routes ||
  //           !response.routes[0] ||
  //           !response.routes[0].sections
  //         ) {
  //           this.showRouteLoadingIndicator(false);
  //           this.isLoading = false;
  //           this.showInfoMessage(
  //            this.translate.instant(
  //       'mapPage.noRouteFound'),
  //             5000
  //           );
  //           return;
  //         }

  //         // Array per memorizzare tutte le coordinate dai vari segmenti

  //         // Array per memorizzare i segmenti di tutte le sezioni
  //         // this.segments = [];

  //         // Colori diversi per le diverse sezioni

  //         // Itera attraverso tutte le sezioni della route
  //         response.routes[0].sections.forEach(
  //           (section: any, sectionIndex: number) => {
  //             //   console.log(`Elaborazione sezione ${sectionIndex}:`, section);

  //             const encodedPolyline = section.polyline;
  //             if (!encodedPolyline) {
  //               console.warn(`Polyline mancante nella sezione ${sectionIndex}`);
  //               return; // Salta alla prossima sezione
  //             }

  //             const decodedResult = FlexiblePolyline.decode(encodedPolyline);
  //             const sectionCoordinates = decodedResult.polyline;

  //             // Aggiungi le coordinate di questa sezione all'array complessivo
  //             this.allRoutesCoords = [
  //               ...this.allRoutesCoords,
  //               ...sectionCoordinates
  //             ];

  //             // console.log(`Sezione ${sectionIndex}: ${sectionCoordinates.length} punti`);

  //             // Crea una polyline per questa sezione con un colore diverso

  //             const line = L.polyline(sectionCoordinates, {
  //               color: '#3388ff',
  //               weight: 8,
  //               opacity: 0.7
  //             }).addTo(polylineGroup);

  //             // // Aggiungi un listener per il click sulla polyline
  //             // line.on('click', (event: L.LeafletMouseEvent) => {
  //             //   const clickedLatLng = event.latlng;
  //             //   // Trova il segmento più vicino al punto cliccato
  //             //   const closestSegmentIndex = this.findClosestSegmentIndex(clickedLatLng);
  //             //   if (closestSegmentIndex !== -1) {
  //             //     this.onCardSelected({ index: closestSegmentIndex, selected: true });
  //             //   }
  //             // });

  //             // Aggiungi frecce direzionali se richiesto
  //             if (showArrows) {
  //               const decorator = L.polylineDecorator(line, {
  //                 patterns: [
  //                   {
  //                     offset: '5%',
  //                     repeat: '10%',
  //                     symbol: L.Symbol.arrowHead({
  //                       pixelSize: 12,
  //                       polygon: true,
  //                       pathOptions: {
  //                         fillOpacity: 0.8,
  //                         weight: 0,
  //                         color: 'black'
  //                       }
  //                     })
  //                   }
  //                 ]
  //               }).addTo(this.map!);
  //             }
  //           }
  //         );

  //         //   console.log('Coordinate totali combinate:', allCoordinates.length);

  //         let waypointFromJourney = this.getJourneyCoordinates();
  //         //  console.log('waypointFromJourney',waypointFromJourney)

  //         // Prima di aggiungere i nuovi waypoint, rimuovi quelli esistenti
  //         if (
  //           this.waypointDotsGroup &&
  //           this.map!.hasLayer(this.waypointDotsGroup)
  //         ) {
  //           this.map!.removeLayer(this.waypointDotsGroup);
  //           this.waypointDotsGroup = null;
  //         }

  //         // Aggiungi i waypoint come pallini
  //         this.waypointDotsGroup = this.addWaypointDots(this.journeysDetails);
  //         this.waypointDotsGroup.addTo(this.map!);

  //         // FZ elimnino i pènti partenza e arrivo waypoint se ci sono già xkè nel caso di ricarica  A A rimaneva 2 volte
  //         if (this.journeyEndpointsGroup && this.map) {
  //           this.map.removeLayer(this.journeyEndpointsGroup);
  //           this.journeyEndpointsGroup = null;
  //         }

  //         this.journeyEndpointsGroup = this.addJourneyEndpoints();

  //         this.journeyEndpointsGroup.addTo(this.map!);

  //         // Aggiungi tutti i gruppi alla mappa
  //         polylineGroup.addTo(this.map!);
  //         // waypointsGroup.addTo(this.map!);
  //         // this.startMarkersGroup.addTo(this.map!);
  //         // this.endMarkersGroup.addTo(this.map!);

  //         // Centra la mappa su tutti gli elementi
  //         const allLayersGroup = L.featureGroup([
  //           polylineGroup,
  //           // this.startMarkersGroup,
  //           // this.endMarkersGroup,
  //           this.waypointDotsGroup,
  //           this.journeyEndpointsGroup
  //         ]);
  //         this.map!.fitBounds(allLayersGroup.getBounds());
  //         // Nascondi l'indicatore quando il percorso è disegnato
  //         this.waypointsVisible = true;
  //         this.routePointsVisible = false;
  //         this.journeyEndpointsVisible = true;
  //         this.showRouteLoadingIndicator(false);
  //         this.isLoading = false;
  //         subscription.unsubscribe();
  //       },
  //       error: (error) => {
  //         console.error("Errore durante l'importazione del percorso:", error);
  //         // Nascondi l'indicatore quando il percorso è disegnato
  //         this.showRouteLoadingIndicator(false);
  //         this.isLoading = false;
  //         subscription.unsubscribe();
  //       },
  //        complete: () => {
  //       // Rimuovi questa sottoscrizione dall'elenco quando completata
  //       const index = this.activeHttpRequests.indexOf(subscription);
  //       if (index > -1) {
  //         this.activeHttpRequests.splice(index, 1);
  //       }
  //     }
  //     });
  // }

  toggleWaypoints(): void {
    if (!this.map || !this.waypointDotsGroup) {
      console.warn('Mappa o waypointDotsGroup non disponibile');
      return;
    }

    if (this.waypointsVisible) {
      this.map.removeLayer(this.waypointDotsGroup);
      console.log('Waypoints rimossi dalla mappa');

      this.waypointsVisible = false;

      // this.toggleHighFrequencyPoints();
    } else {
      this.map.addLayer(this.waypointDotsGroup);
      console.log('Waypoints aggiunti alla mappa');
      this.waypointsVisible = true;
      //   this.highFrequencyGlobalVisible = false;

      //  this.drawRoute()
      //      if (this.waypointPolylineGroup) {
      //          this.finalizeRouteDisplay(this.waypointPolylineGroup, true);

      //      }else{
      // this.drawRoute();
      //      }
    }

    // Aggiorna il testo o l'icona del bottone
    this.updateWaypointToggleButton();
  }
  // Metodo aggiuntivo per aggiornare lo stato del bottone
  private updateWaypointToggleButton(): void {
    const button = document.getElementById('waypoint-toggle-btn');
    if (button) {
      const icon = button.querySelector('i');
      if (icon) {
        // Cambia l'icona in base allo stato
        if (this.waypointsVisible) {
          icon.className = 'fas fa-eye'; // Icona occhio aperto
          button.setAttribute('title', 'Nascondi waypoint');
        } else {
          icon.className = 'fas fa-eye-slash'; // Icona occhio chiuso
          button.setAttribute('title', 'Mostra waypoint');
        }
      }
    }
  }

  toggleWaypointLines(): void {
    if (!this.map) {
      console.warn('Mappa non disponibile');
      return;
    }

    if (!this.waypoints || this.waypoints.length < 2) {
      console.warn('Non ci sono abbastanza waypoints per creare linee');
      return;
    }

    if (this.waypointLinesVisible) {
      // Rimuovi le linee dalla mappa
      if (this.waypointLinesGroup && this.map.hasLayer(this.waypointLinesGroup)) {
        this.map.removeLayer(this.waypointLinesGroup);
        console.log('✅ Linee waypoints rimosse dalla mappa');
      }
      this.waypointLinesVisible = false;
    } else {
      // Crea e aggiungi le linee alla mappa
      this.createWaypointLines();
      this.waypointLinesVisible = true;
      console.log('✅ Linee waypoints aggiunte alla mappa');
    }
  }

  private createWaypointLines(): void {
    if (!this.map || !this.waypoints || this.waypoints.length < 2) {
      return;
    }

    // Rimuovi il gruppo esistente se presente
    if (this.waypointLinesGroup && this.map.hasLayer(this.waypointLinesGroup)) {
      this.map.removeLayer(this.waypointLinesGroup);
    }

    // Crea un nuovo gruppo per le linee
    this.waypointLinesGroup = L.featureGroup();

    // Crea una polyline che collega tutti i waypoints in ordine
    const polyline = L.polyline(this.waypoints, {
      color: '#FF6B35',
      weight: 3,
      opacity: 0.8,
      dashArray: '5, 10' // Linea tratteggiata
    });

    // Aggiungi la polyline al gruppo
    this.waypointLinesGroup.addLayer(polyline);

    // Aggiungi il gruppo alla mappa
    this.waypointLinesGroup.addTo(this.map);

    console.log('🔗 Creata polyline con', this.waypoints.length, 'waypoints');
  }

  private getJourneyCoordinates(): Array<{
    trip_id: number;
    coordinates: {
      start: [number, number] | null;
      end: [number, number] | null;
    };
  }> {
    if (!this.journeys || this.journeys.length === 0) {
      console.warn('Nessun journey disponibile');
      return [];
    }

    // Ordina i journey per trip_id per garantire la sequenza corretta
    const sortedJourneys = [...this.journeys].sort(
      (a, b) => a.trip_id - b.trip_id
    );

    // Per ogni journey, crea un oggetto con trip_id e coordinate di inizio e fine
    sortedJourneys.forEach((journey) => {
      const journeyCoordinates = {
        trip_id: journey.trip_id,
        coordinates: {
          start:
            journey.start_lat != null && journey.start_lon != null
              ? ([journey.start_lat, journey.start_lon] as [number, number])
              : null,
          end:
            journey.end_lat != null && journey.end_lon != null
              ? ([journey.end_lat, journey.end_lon] as [number, number])
              : null
        }
      };

      // Aggiungi questo journey al risultato solo se ha almeno una coordinata valida
      if (
        journeyCoordinates.coordinates.start ||
        journeyCoordinates.coordinates.end
      ) {
        this.JourneyCoords.push(journeyCoordinates);
      }
    });
    // console.log('JourneyCoords', this.JourneyCoords)
    // console.log(`Generate informazioni per ${this.JourneyCoords.length} journey con coordinate`);
    return this.JourneyCoords;
  }

  // Modifichiamo la funzione addWaypointDots per accettare i dettagli
  private addWaypointDots(journeyDetails?: JourneyDetails[]): L.FeatureGroup {
    // console.log('ZAK',journeyDetails)
    // Crea un gruppo di layer per i waypoints
    const waypointDotsGroup = L.featureGroup();

    // Aggiungi un marker per ogni waypoint
    this.waypoints.forEach((wp, index) => {
      // Trova i dettagli corrispondenti del waypoint nei journeysDetails
      const waypointDetail = journeyDetails?.find(
        (detail) => detail.gps_lat === wp[0] && detail.gps_lon === wp[1]
      );

      // Determina il colore in base allo status
      let waypointColor = '#4CAF50'; // Default verde per MOVING
      let strokeColor = '#388E3C';
      let textColor = 'white'; // Default testo bianco

      // Modifica colore in base allo status se disponibile
      if (waypointDetail && waypointDetail.status) {
        switch (waypointDetail.status) {
          case 'MOVING':
            waypointColor = '#4CAF50'; // Verde per veicoli in movimento
            strokeColor = '#388E3C';
            textColor = 'white';
            break;
          case 'PARKED':
            waypointColor = '#9E9E9E'; // Grigio per veicoli parcheggiati
            strokeColor = '#757575';
            textColor = 'white';
            break;
          case 'IDLE':
            waypointColor = '#FF9800'; // Arancione per veicoli al minimo
            strokeColor = '#F57C00';
            textColor = 'white';
            break;
          case 'UNKNOWN':
            waypointColor = '#2196F3'; // Blu per status sconosciuto
            strokeColor = '#1976D2';
            textColor = 'white';
            break;
          case 'NOTCONNECTED':
            waypointColor = '#FFFFFF'; // Bianco per veicoli non connessi
            strokeColor = '#BDBDBD';
            textColor = 'black'; // Testo nero per migliore visibilità su sfondo bianco
            break;
          default:
            waypointColor = '#4CAF50'; // Verde come default
            strokeColor = '#388E3C';
            textColor = 'white';
        }
      }

      // Crea un punto SVG con colore dinamico e numero all'interno
      const markerSize = 16; // Dimensione del marker
      const waypointMarkerHtml = `
      <svg width="${markerSize}" height="${markerSize}" viewBox="0 0 ${markerSize} ${markerSize}" xmlns="http://www.w3.org/2000/svg">
        <circle cx="${markerSize / 2}" cy="${markerSize / 2}" r="${
        markerSize / 2 - 1
      }"
                fill="${waypointColor}" stroke="${strokeColor}" stroke-width="1" opacity="1"/>
        <text x="${markerSize / 2}" y="${markerSize / 2 + 4}"
              text-anchor="middle"
              fill="${textColor}"
              font-family="Arial"
              font-size="${markerSize / 2}"
              font-weight="bold">${index + 1}</text>
      </svg>
    `;

      const waypointIcon = L.divIcon({
        className: 'waypoint-dot-marker',
        html: waypointMarkerHtml,
        iconSize: [markerSize, markerSize],
        iconAnchor: [markerSize / 2, markerSize / 2]
      });

      // Crea il marker e aggiungilo al gruppo
      const marker = L.marker([wp[0], wp[1]], {
        icon: waypointIcon,
        zIndexOffset: 1000
      }).addTo(waypointDotsGroup);

      // Aggiungi tooltip con informazioni sul waypoint
      if (waypointDetail) {
        const status =
          this.mapService.translateVehicleStatus(waypointDetail.status) ||
          'N/D';
        const speed =
          waypointDetail.gps_speed_v !== undefined
            ? `${waypointDetail.gps_speed_v.toFixed(2)} km/h`
            : 'N/D';
        const time = waypointDetail.gps_tstamp
          ? new Date(waypointDetail.gps_tstamp).toLocaleTimeString()
          : 'N/D';

        marker.bindTooltip(
          `
        <div class="waypoint-tooltip">
          <div><strong>P. ${index + 1}</strong></div>
          <div>${this.translate.instant('mapPage.state')}: ${status}</div>
          <div>${this.translate.instant('global.speed')}: ${speed}</div>
          <div>${this.translate.instant('mapPage.hours')}: ${time}</div>
        </div>
      `,
          {
            offset: [0, -8],
            direction: 'top'
          }
        );
      }
    });

    return waypointDotsGroup;
  }
  /**
   * Disegna il percorso per un singolo journey utilizzando i relativi waypoints
   * @param tripId L'ID del journey di cui visualizzare il percorso
   * @returns Promise con il gruppo Leaflet contenente la polyline creata
   */
  // percorsoSingleJourney(
  //   tripId: number,
  //   lineColor: string = '#38a83e',
  //   shouldFitBounds: boolean = true,
  //   showArrows: boolean = true
  // ): Promise<L.FeatureGroup> {
  //   return new Promise((resolve, reject) => {
  //     // Trova il journey specifico
  //     const journeyData = this.JourneyCoords.find(
  //       (journey) => journey.trip_id === tripId
  //     );
  //     const journey = this.journeys.find((j) => j.trip_id === tripId);
  //     if (!journeyData) {
  //       reject(`Journey con ID ${tripId} non trovato`);
  //       return;
  //     }

  //     // Recupero della data di partenza specifica per questo journey
  //     let departureTimeParam = '';
  //     if (journey && journey.start_time) {
  //       // Converti il timestamp nel formato ISO richiesto da HERE API
  //       const journeyDate = new Date(journey.start_time);
  //       const departureTime = journeyDate.toISOString().replace('.000Z', '');
  //       departureTimeParam = `&departureTime=${departureTime}`;
  //       console.log(
  //         `Usando data di partenza dal journey ${tripId}: ${departureTime}`
  //       );
  //     } else {
  //       console.log(
  //         `Nessuna data di partenza trovata per journey ${tripId}, uso valore di default`
  //       );
  //     }

  //     // Controlla se ci sono waypoints disponibili
  //     if (
  //       !(journeyData as any).pushWaypoints ||
  //       (journeyData as any).pushWaypoints.length === 0
  //     ) {
  //       console.warn(`Nessun waypoint disponibile per il journey ${tripId}`);
  //       reject(`Nessun waypoint per journey ${tripId}`);
  //       return;
  //     }

  //     // Crea un gruppo per la polyline
  //     const journeyRouteGroup = L.featureGroup();

  //     // URL per l'API HERE Maps
  //     const importUrl =
  //       `https://router.hereapi.com/v8/import?apikey=${environment.hereApiKey}` +
  //       `&return=polyline,summary,actions,instructions,potentialTimeDependentViolations,truckRoadTypes,incidents,routeHandle,turnByTurnActions,routingZones,passthrough,elevation&spans=length,truckAttributes,stateCode,routingZones,gates,incidents,truckRoadTypes,segmentId,segmentRef&transportMode=truck&allow[hov]=true&allow[hot]=true&lang=it-it${departureTimeParam}`;

  //     // Ottieni i punti ad alta frequenza se disponibili
  //     const highFreqPoints = this.highFrequencyPointsByTripId.get(tripId);
  //     console.log(
  //       `Punti ad alta frequenza per il journey ${tripId}:`,
  //       highFreqPoints
  //     );

  //     // Usa merge se disponibili, altrimenti solo pushWaypoints
  //     let tracePoints: { lat: number; lng: number }[] = [];
  //     if (
  //       highFreqPoints &&
  //       highFreqPoints.length > 0 &&
  //       (journeyData as any).pushWaypoints
  //     ) {
  //       tracePoints = this.mergeWaypointsAndHighFrequency(
  //         (journeyData as any).pushWaypoints,
  //         highFreqPoints
  //       );
  //     } else if ((journeyData as any).pushWaypoints) {
  //       tracePoints = (journeyData as any).pushWaypoints.map(
  //         ([lat, lng]: [number, number]) => ({ lat, lng })
  //       );
  //     } else {
  //       reject('Nessun waypoint disponibile');
  //       return;
  //     }

  //     const traccia = { trace: tracePoints };
  //     // Prepara i dati da inviare
  //     // const traccia = {
  //     //   trace: (journeyData as any).pushWaypoints.map(
  //     //     ([lat, lng]: [number, number]) => ({ lat, lng })
  //     //   )
  //     // };
  //     console.log('TRACCIA', traccia);
  //     console.log('TRACCAI2', this.toLatLngCSV(tracePoints));

  //     console.log(
  //       `Calcolo percorso per journey ${tripId} con ${
  //         (journeyData as any).pushWaypoints.length
  //       } waypoints`
  //     );

  //     const subscription = this.httpWithoutInterceptor
  //       .post(importUrl, traccia)
  //       .pipe(takeUntil(this.cancelPendingRequests$))
  //       .subscribe({
  //         next: (response: any) => {
  //           if (
  //             !response.routes ||
  //             !response.routes[0] ||
  //             !response.routes[0].sections
  //           ) {
  //             this.showRouteLoadingIndicator(false);
  //             this.isLoading = false;
  //             console.log('Nessun waypoint disponibile per il percorso. 3');
  //             this.showInfoMessage(
  //               this.translate.instant('mapPage.noRouteFound'),
  //               5000
  //             );
  //             reject('Formato risposta API non valido');
  //             return;
  //           }

  //           // Crea un gruppo separato per i decoratori di questo journey
  //           const journeyArrowDecorators = L.layerGroup();

  //           // Processa le sezioni della route
  //           response.routes[0].sections.forEach((section: any) => {
  //             const encodedPolyline = section.polyline;
  //             if (!encodedPolyline) {
  //               console.warn('Polyline mancante nella sezione');
  //               return;
  //             }

  //             // Decodifica la polyline
  //             const decodedResult = FlexiblePolyline.decode(encodedPolyline);
  //             const sectionCoordinates = decodedResult.polyline;

  //             // Crea una polyline verde per questa sezione
  //             const line = L.polyline(sectionCoordinates, {
  //               color: lineColor,
  //               weight: 10,
  //               opacity: 0.8,
  //               className: `journey-route-${tripId}`
  //             }).addTo(journeyRouteGroup);

  //             // Aggiungi il decorator solo se showArrows è true
  //             if (showArrows) {
  //               const decorator = L.polylineDecorator(line, {
  //                 patterns: [
  //                   {
  //                     offset: '5%',
  //                     repeat: '10%',
  //                     symbol: L.Symbol.arrowHead({
  //                       pixelSize: 12,
  //                       polygon: true,
  //                       pathOptions: {
  //                         fillOpacity: 0.8,
  //                         weight: 0,
  //                         color: 'dark'
  //                       }
  //                     })
  //                   }
  //                 ]
  //               }).addTo(this.map!);

  //               // Aggiungi il decorator al gruppo
  //               journeyRouteGroup.addLayer(decorator);
  //               journeyArrowDecorators.addLayer(decorator);
  //             }
  //           });

  //           // Salva il gruppo dei decoratori per questo journey
  //           this.journeyArrowDecoratorsMap.set(tripId, journeyArrowDecorators);

  //           // Aggiungi il gruppo alla mappa
  //           journeyRouteGroup.addTo(this.map!);

  //           // Center the map on the polyline ONLY if shouldFitBounds is true
  //           if (shouldFitBounds && journeyRouteGroup.getLayers().length > 0) {
  //             console.log('AGGGIOENO POSI>IONE');
  //             this.map!.fitBounds(journeyRouteGroup.getBounds(), {
  //               padding: [50, 50],
  //               maxZoom: 18
  //             });
  //           }

  //           console.log(
  //             `Percorso per journey ${tripId} visualizzato con successo ${
  //               showArrows ? 'con' : 'senza'
  //             } frecce`
  //           );
  //           resolve(journeyRouteGroup);
  //         },
  //         error: (error) => {
  //           console.error(
  //             `Errore durante l'importazione del percorso per journey ${tripId}:`,
  //             error
  //           );
  //           reject(error);
  //         },
  //         complete: () => {
  //           // Rimuovi questa sottoscrizione dall'elenco quando completata
  //           const index = this.activeHttpRequests.indexOf(subscription);
  //           if (index > -1) {
  //             this.activeHttpRequests.splice(index, 1);
  //           }
  //         }
  //       });
  //   });
  // }

  // Funzione di merge
  private mergeWaypointsAndHighFrequency(
    pushWaypoints: [number, number][],
    highFreqPoints: any[]
  ): { lat: number; lng: number }[] {
    // Normalizza pushWaypoints in oggetti con tstamp null
    const waypoints = (pushWaypoints || []).map(([lat, lng]) => ({
      lat,
      lng,
      tstamp: null
    }));

    // Normalizza highFreqPoints (già oggetti con lat/lng/tstamp)
    const hfPoints = (highFreqPoints || []).map((pt: any) => ({
      lat: pt.lat,
      lng: pt.lng,
      tstamp: pt.tstamp || null
    }));

    // Unisci e ordina per tstamp (i null vanno in fondo)
    const merged = [...waypoints, ...hfPoints].sort((a, b) => {
      if (!a.tstamp && !b.tstamp) return 0;
      if (!a.tstamp) return 1;
      if (!b.tstamp) return -1;
      return new Date(a.tstamp).getTime() - new Date(b.tstamp).getTime();
    });

    // Ritorna solo lat/lng
    return merged.map((p) => ({ lat: p.lat, lng: p.lng }));
  }

  // Modifica a onCardSelect2 per rimuovere i decoratori freccia quando si seleziona un journey
  onCardSelect2(event: { index: number; selected: boolean }): void {
    console.log(
      `onCardSelect2 chiamato con tripId=${event.index}, selected=${event.selected}`
    );
    this.clearMapPolylines();
    const tripId = event.index;
    const shouldSelect = event.selected;

    // Forza reset dello stato di loading se bloccato
    if (this.mapContainerService.getJourneyLoadingState() || this.isLoading) {
      console.warn('Operazione in corso, forzando reset...');
      this.mapContainerService.setJourneyLoading(false);
      this.isLoading = false;
      this.showRouteLoadingIndicator(false);

      // Cancella tutte le richieste HTTP pendenti
      this.cancelPendingRequests$.next();
    }

    // CASO: DESELEZIONA
    if (!shouldSelect) {
      console.log(`Deselezionando journey ${tripId}`);
      this.deselectJourneyCard(tripId);
      this.currentlySelectedJourneyId = null;
      this.filterWaypointsByJourney(tripId, false);

      // MODIFICA: quando deselezioni, mostra le frecce
      this.toggleAllArrows(true);
      //FZ 25072025 pulisce e ridesgna rotta principale
      this.clearMapPolylines();

      // Reset degli stati di loading senza chiamare drawRoute
      this.showRouteLoadingIndicator(false);
      this.isLoading = false;

      return;
    }

    // CASO: SELEZIONA NUOVA CARD
    console.log(`Selezionando journey ${tripId}`);

    // FASE 1: Deseleziona la card precedente se esiste
    if (
      this.currentlySelectedJourneyId !== null &&
      this.currentlySelectedJourneyId !== tripId
    ) {
      try {
        console.log(
          `Deselezionando journey precedente ${this.currentlySelectedJourneyId}`
        );
        this.deselectJourneyCard(this.currentlySelectedJourneyId);

        // CORREZIONE: Nascondi esplicitamente i waypoint del journey precedente
        //this.filterWaypointsByJourney(this.currentlySelectedJourneyId, false);

        // IMPORTANTE: Pulizia più aggressiva per garantire che tutti i layer vengano rimossi
        if (this.journeyHighlightMap.has(this.currentlySelectedJourneyId)) {
          const previousLayers =
            this.journeyHighlightMap.get(this.currentlySelectedJourneyId) || [];
          previousLayers.forEach((layer) => {
            if (this.map && this.map.hasLayer(layer)) {
              this.map.removeLayer(layer);
            }
          });
          this.journeyHighlightMap.delete(this.currentlySelectedJourneyId);
        }
      } catch (error) {
        console.error('Errore durante la deselezione precedente:', error);
      }
    }

    // FASE 2: Reset completo dello stato
    this.journeys.forEach((journey) => {
      journey.selected = journey.trip_id === tripId;
    });

    // Memorizza il nuovo journey selezionato
    this.currentlySelectedJourneyId = tripId;

    // FASE 3: Filtra i waypoints prima di disegnare il percorso
    try {
      // CORREZIONE: Assicurati che tutti gli altri waypoint siano nascosti prima di mostrare quelli nuovi
      // Non chiamiamo filterWaypointsByJourney qui, lo faremo dopo aver ottenuto i waypoints

      // Trova il journey nelle JourneyCoords con il trip_id corrispondente
      const journeyData = this.JourneyCoords.find((j) => j.trip_id === tripId);

      if (!journeyData) {
        this.showErrorMessage(
          `${this.translate.instant('mapPage.noJourneyFoundWithId')} ${tripId}`
        );
        return;
      }

      // FASE 4: Gestisci la selezione senza calcolo rotta HERE
      const newHighlightedElements: L.Layer[] = [];
      this.showRouteLoadingIndicator(true);
      this.isLoading = true;

      // Utilizza setTimeout per dare tempo alla UI di aggiornarsi
      setTimeout(() => {
        this.getSingleJourneyWaypoints(tripId)
          .then((waypoints) => {
            if (!waypoints || waypoints.length === 0) {
              throw new Error(`Nessun waypoint ricevuto per journey ${tripId}`);
            }

            // Aggiorna i waypoints del journey nei JourneyCoords
            (journeyData as any).pushWaypoints = waypoints;

            // Ora chiama filterWaypointsByJourney con i waypoints ottenuti
            this.filterWaypointsByJourney(tripId, true, waypoints);

            // Senza calcolo rotta HERE, completa direttamente l'operazione
            this.journeyHighlightMap.set(tripId, newHighlightedElements);
            this.showRouteLoadingIndicator(false);
            this.isLoading = false;

            // Nascondi le frecce dopo la selezione
            this.toggleAllArrows(false);

            // Centra la mappa sui waypoints del journey selezionato
            if (waypoints && waypoints.length > 0) {
              const bounds = L.latLngBounds(waypoints);
              this.map?.fitBounds(bounds, {
                padding: [50, 50],
                maxZoom: 18
              });
            }
          })
          .catch((error) => {
            console.error(`Errore nella selezione journey ${tripId}:`, error);
            this.showRouteLoadingIndicator(false);
            this.isLoading = false;
            this.showErrorMessage(
              this.translate.instant('mapPage.erroreLoadingRoute'),
              5000
            );
            // In caso di errore, non filtrare i waypoints per evitare operazioni incomplete

            // Usa i waypoints già disponibili nei JourneyCoords per centrare la mappa (evita doppia chiamata)
            if ((journeyData as any).pushWaypoints && (journeyData as any).pushWaypoints.length > 0) {
              const bounds = L.latLngBounds((journeyData as any).pushWaypoints);
              this.map?.fitBounds(bounds, {
                padding: [50, 50],
                maxZoom: 18
              });
            } else if (journeyData.coordinates.start && journeyData.coordinates.end) {
              // Fallback: usa coordinate di inizio e fine se disponibili
              const bounds = L.latLngBounds([journeyData.coordinates.start, journeyData.coordinates.end]);
              this.map?.fitBounds(bounds, {
                padding: [100, 100],
                maxZoom: 15
              });
            }

            // Fallback path se disponibile
            if ((journeyData.coordinates as any).path) {
              this.showInfoMessage(
                this.translate.instant('mapPage.journeyUse') + tripId
              );
             // this.useFallbackPath(journeyData, tripId, newHighlightedElements);
            }
          });
      }, 50); // Un piccolo delay per garantire che la UI si aggiorni correttamente
    } catch (error) {
      console.error('Errore critico in onCardSelect2:', error);
      this.showRouteLoadingIndicator(false);
      this.isLoading = false;
      this.showErrorMessage('Si è verificato un errore durante la selezione');
    }
  }

  private showJourneyWaypoints(
    waypoints: [number, number][],
    tripId: number,
    highlightedElements: L.Layer[]
  ): void {
    if (!this.map || waypoints.length === 0) return;

    console.log(
      `Visualizzo ${waypoints.length} waypoints per journey ${tripId}`
    );

    // Crea un gruppo per i waypoints
    const waypointsGroup = L.featureGroup().addTo(this.map);

    // Aggiungi il gruppo agli elementi evidenziati
    highlightedElements.push(waypointsGroup);

    // Aggiungi un marker per ogni waypoint
    waypoints.forEach((waypoint, index) => {
      // Salta punti con coordinate null o indefinite
      if (!waypoint[0] || !waypoint[1]) return;

      // Colore diverso per il primo, l'ultimo e i waypoints intermedi
      let markerColor = 'black'; // Colore nero per waypoint intermedi
      let markerSize = 10; // Punti intermedi più grandi

      if (index === 0) {
        markerColor = '#4CAF50'; // Verde per il punto di partenza
        markerSize = 10;
      } else if (index === waypoints.length - 1) {
        markerColor = '#6b6b6b'; // Rosso per il punto di arrivo
        markerSize = 10;
      }

      // Usa un divIcon con SVG per includere il numero del punto
      const waypointNumber = index + 1;
      const markerHtml = `
      <svg width="${markerSize * 2}" height="${markerSize * 2}" viewBox="0 0 ${
        markerSize * 2
      } ${markerSize * 2}" xmlns="http://www.w3.org/2000/svg">
        <circle cx="${markerSize}" cy="${markerSize}" r="${markerSize}" fill="${markerColor}" stroke="white" stroke-width="1" />
        <text x="${markerSize}" y="${markerSize + 4}"
              text-anchor="middle"
              fill="white"
              font-family="Arial"
              font-size="${markerSize - 2}px"
              font-weight="bold">${waypointNumber}</text>
      </svg>
    `;

      const markerIcon = L.divIcon({
        className: 'waypoint-number-marker',
        html: markerHtml,
        iconSize: [markerSize * 2, markerSize * 2],
        iconAnchor: [markerSize, markerSize]
      });

      // Crea un marker con l'icona personalizzata
      const marker = L.marker(waypoint, {
        icon: markerIcon,
        zIndexOffset: 1000 // Assicura che questi marker appaiano sopra gli altri elementi
      }).bindTooltip(`Point ${waypointNumber} / ${waypoints.length}`);

      // Aggiungi il marker al gruppo
      marker.addTo(waypointsGroup);
    });

    // Centra la mappa sui waypoints
    this.map.fitBounds(waypointsGroup.getBounds(), {
      padding: [50, 50],
      maxZoom: 18
    });
  }

  private showErrorMessage(message: string, duration: number = 5000): void {
    this.showMapMessage(message, 'error', duration);
  }
  private showInfoMessage(message: string, duration: number = 3000): void {
    this.showMapMessage(message, 'info', duration);
  }

  private deselectJourneyCard(tripId: number): void {
    // 1. Aggiorna lo stato del journey nell'array
    this.journeys.forEach((journey) => {
      if (journey.trip_id === tripId) {
        journey.selected = false;
      }
    });

    // 2. Rimuovi gli elementi evidenziati dalla mappa
    const highlightedElements = this.journeyHighlightMap.get(tripId) || [];
    highlightedElements.forEach((element) => {
      if (this.map && this.map.hasLayer(element)) {
        this.map.removeLayer(element);
      }
    });

    // 3. Rimuovi il journey dalla mappa dei journey evidenziati
    this.journeyHighlightMap.delete(tripId);

    // 4. Forza aggiornamento della vista
    this.cdr.detectChanges();
  }

  // Metodo di supporto per usare il path precompilato come fallback
  // private useFallbackPath(
  //   journeyData: any,
  //   tripId: number,
  //   highlightedElements: L.Layer[]
  // ): void {
  //   console.log(`Inizializzazione fallback per journey ${tripId}`);

  //   // Controlla se i marker devono essere visibili per questo journey
  //   const showMarkers = this.showFallbackMarkers.get(tripId) !== false; // Default: true

  //   // Caso 1: Abbiamo un percorso precompilato
  //   if (
  //     (journeyData.coordinates as any).path &&
  //     (journeyData.coordinates as any).path.length > 0
  //   ) {
  //     const pathCoordinates = (journeyData.coordinates as any).path;
  //     console.log(
  //       `Usando percorso precompilato per journey ${tripId} con ${pathCoordinates.length} punti`
  //     );

  //     // Crea la polyline tratteggiata nera con le coordinate precompilate
  //     const highlightedLine = L.polyline(pathCoordinates, {
  //       color: 'black',
  //       weight: 2,
  //       opacity: 0.8,
  //       dashArray: '5, 10'
  //     }).addTo(this.map!);

  //     highlightedElements.push(highlightedLine);

  //     // Aggiungi marker per inizio e fine del percorso solo se showMarkers è true
  //     if (pathCoordinates.length > 0 && showMarkers) {
  //       this.addFallbackMarkers(
  //         pathCoordinates[0],
  //         pathCoordinates[pathCoordinates.length - 1],
  //         tripId,
  //         highlightedElements
  //       );
  //     }

  //     // Aggiorna la vista
  //     const bounds = L.polyline(pathCoordinates).getBounds();
  //     this.map!.fitBounds(bounds, { padding: [100, 100] });
  //   }

  //   // Caso 2: Non abbiamo percorso precompilato, ma abbiamo punti di inizio e fine
  //   else if (journeyData.coordinates.start && journeyData.coordinates.end) {
  //     console.log(
  //       `Nessun percorso precompilato per journey ${tripId}, uso linea diretta tra i punti`
  //     );

  //     // Crea una linea tratteggiata diretta tra inizio e fine
  //     const directLine = L.polyline(
  //       [journeyData.coordinates.start, journeyData.coordinates.end],
  //       {
  //         color: 'black',
  //         weight: 2,
  //         opacity: 0.7,
  //         dashArray: '5, 10',
  //         lineCap: 'round'
  //       }
  //     ).addTo(this.map!);

  //     highlightedElements.push(directLine);

  //     // Aggiungi una freccia per indicare la direzione
  //     const decorator = L.polylineDecorator(directLine, {
  //       patterns: [
  //         {
  //           offset: '50%',
  //           repeat: 0,
  //           symbol: L.Symbol.arrowHead({
  //             pixelSize: 15,
  //             polygon: true,
  //             pathOptions: {
  //               fillOpacity: 0.8,
  //               weight: 0,
  //               color: 'black'
  //             }
  //           })
  //         }
  //       ]
  //     }).addTo(this.map!);

  //     highlightedElements.push(decorator);

  //     // Aggiungi marker per inizio e fine
  //     this.addFallbackMarkers(
  //       journeyData.coordinates.start,
  //       journeyData.coordinates.end,
  //       tripId,
  //       highlightedElements
  //     );

  //     // Aggiorna la vista
  //     const bounds = L.latLngBounds([
  //       journeyData.coordinates.start,
  //       journeyData.coordinates.end
  //     ]);
  //     this.map!.fitBounds(bounds, { padding: [100, 100] });
  //   }
  //   // Caso 3: Abbiamo solo uno dei due punti (inizio o fine)
  //   else if (journeyData.coordinates.start || journeyData.coordinates.end) {
  //     const singlePoint =
  //       journeyData.coordinates.start || journeyData.coordinates.end;
  //     console.log(
  //       `Solo un punto disponibile per journey ${tripId}, mostro solo il marker`
  //     );

  //     // Aggiungi un singolo marker
  //     const markerHtml = `
  //     <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
  //       <circle cx="12" cy="12" r="10" fill="black" stroke="white" stroke-width="2"/>
  //       <text x="12" y="16" text-anchor="middle" fill="white" font-weight="bold">${
  //         journeyData.coordinates.start
  //           ? this.translate.instant('mapPage.D')
  //           : this.translate.instant('mapPage.A')
  //       }</text>
  //     </svg>
  //   `;

  //     const icon = L.divIcon({
  //       className: 'route-endpoint-marker',
  //       html: markerHtml,
  //       iconSize: [24, 24],
  //       iconAnchor: [12, 12]
  //     });

  //     const marker = L.marker(singlePoint, { icon: icon })
  //       .bindPopup(
  //         `<b>${
  //           journeyData.coordinates.start
  //             ? this.translate.instant('mapPage.departure')
  //             : this.translate.instant('mapPage.arrival')
  //         } Journey ${tripId}</b>`
  //       )
  //       .addTo(this.map!);

  //     highlightedElements.push(marker);

  //     // Centra su questo punto
  //     this.map!.setView(singlePoint, 15);
  //   }
  //   // Caso 4: Non abbiamo né percorso né punti
  //   else {
  //     console.warn(
  //       `Il journey ${tripId} non ha né percorsi né coordinate valide`
  //     );
  //     this.showErrorMessage(
  //       `${this.translate.instant(
  //         'mapPage.journey'
  //       )} ${tripId} ${this.translate.instant('mapPage.noLatLon')}`
  //     );
  //   }

  //   // Memorizza gli elementi per poterli rimuovere in seguito
  //   this.journeyHighlightMap.set(tripId, highlightedElements);
  // }

  // Metodo di supporto per creare i marker di inizio e fine
  private addFallbackMarkers(
    startPoint: [number, number],
    endPoint: [number, number],
    tripId: number,
    highlightedElements: L.Layer[]
  ): void {
    // Trova il journey corrente per controllare lo status
    const currentJourney = this.journeys.find((j) => j.trip_id === tripId);
    //console.log('ZAK->', currentJourney);
    const isInProgress = currentJourney?.status === 'IN_PROGRESS';

    // Marker di inizio percorso (P)
    const startMarkerHtml = `
  <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" fill="black" stroke="white" stroke-width="2"/>
    <text x="12" y="16" text-anchor="middle" fill="white" font-weight="bold">${this.translate.instant(
      'mapPage.D'
    )}</text>
  </svg>
  `;

    const startIcon = L.divIcon({
      className: 'route-endpoint-marker',
      html: startMarkerHtml,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });

    const startMarker = L.marker(startPoint, { icon: startIcon })
      .bindPopup(
        `<b>${this.translate.instant(
          'mapPage.departure'
        )} ${this.translate.instant('mapPage.journey')}  ${tripId}</b>`
      )
      .addTo(this.map!);

    // Marker di fine percorso (A) - VERDE se IN_PROGRESS, altrimenti GRIGIO
    const fillColor = isInProgress ? '#4CAF50' : '#6b6b6b';
    const strokeColor = isInProgress ? '#388E3C' : '#555555';

    const endMarkerHtml = `
<svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
  <circle cx="12" cy="12" r="10" fill="${fillColor}" stroke="${strokeColor}" stroke-width="2"/>
  ${
    isInProgress
      ? // Freccia direzionale per journey IN_PROGRESS
        `<g transform="translate(12, 12) rotate(${
          currentJourney?.angle_current || 0
        })">
       <path d="M0,-6 L4,4 L0,2 L-4,4 Z" fill="white" />
     </g>`
      : // "A" per journey completati
        `<text x="12" y="16" text-anchor="middle" fill="white" font-weight="bold">${this.translate.instant(
          'mapPage.A'
        )}</text>`
  }
</svg>
`;
    const endIcon = L.divIcon({
      className: 'route-endpoint-marker',
      html: endMarkerHtml,
      iconSize: [24, 24],
      iconAnchor: [12, 12]
    });

    const endMarker = L.marker(endPoint, { icon: endIcon })
      .bindPopup(
        `<b>${this.translate.instant(
          'mapPage.arrival'
        )} ${this.translate.instant('mapPage.journey')} ${tripId}</b>`
      )
      .addTo(this.map!);

    // Aggiungi i marker agli elementi da rimuovere
    highlightedElements.push(startMarker);
    highlightedElements.push(endMarker);
  }
  /**
   * Ottiene i waypoints per un singolo journey dal servizio, filtrando per il range temporale del journey
   * @param tripId ID del journey di cui ottenere i waypoints
   * @returns Promise con array di waypoints [lat, lon] o null in caso di errore
   */
  getSingleJourneyWaypoints(
    tripId: number
  ): Promise<[number, number][] | null> {
    return new Promise((resolve, reject) => {
      // Trova il journey per ottenere start_time e end_time
      const journey = this.journeys.find((j) => j.trip_id === tripId);

      if (!journey) {
        console.warn(`Journey con ID ${tripId} non trovato`);
        reject(`Journey ${tripId} non trovato`);
        return;
      }

      if (!journey.start_time || !journey.end_time) {
        console.warn(`Journey ${tripId} non ha orari di inizio/fine validi`);
        reject(`Journey ${tripId} senza orari validi`);
        return;
      }

      // Formatta le date nel formato richiesto dall'API (ISO string)
      const startTime = new Date(journey.start_time).toISOString();
      const endTime = new Date(journey.end_time).toISOString();

      console.log(
        `Richiedo waypoints per journey ${tripId} da ${startTime} a ${endTime}`
      );

      // Chiama il servizio con VIN e range temporale
      this.journeyService
        .getJourneyDetails(this.paramUid, startTime, endTime)
        .subscribe({
          next: (journeyDetails) => {
            console.log(
              `Ricevuti ${journeyDetails.length} dettagli per journey ${tripId}`
            );

            // Estrai i waypoints dai dettagli
            const waypoints = journeyDetails
              .filter(
                (detail) => detail.gps_lat != null && detail.gps_lon != null
              )
              .map(
                (detail) =>
                  [detail.gps_lat!, detail.gps_lon!] as [number, number]
              );

            console.log(
              `Estratti ${waypoints.length} waypoints per journey ${tripId}`
            );
            resolve(waypoints);
          },
          error: (err) => {
            console.error(
              `Errore nel recupero dei waypoints per journey ${tripId}:`,
              err
            );
            reject(err);
          }
        });
    });
  }

  /**
   * Mostra un messaggio sulla mappa come una legenda
   * @param message Messaggio da visualizzare
   * @param type Tipo di messaggio (error, info, warning)
   * @param duration Durata in ms (0 per messaggio persistente)
   */
  private showMapMessage(
    message: string,
    type: 'error' | 'info' | 'warning',
    duration: number = 5000
  ): void {
    if (!this.map) return;

    // Rimuovi eventuali messaggi esistenti
    this.clearMapMessage();

    // Crea il controllo per il messaggio
    this.mapMessageControl = new L.Control({ position: 'bottomleft' });

    if (this.mapMessageControl) {
      this.mapMessageControl.onAdd = (map) => {
        const div = L.DomUtil.create('div', `map-message map-message-${type}`);

        // Stili diversi in base al tipo
        let icon = '&#9432;'; // info icon
        if (type === 'error') icon = '&#9888;'; // warning icon
        if (type === 'warning') icon = '&#8252;'; // exclamation icon

        div.innerHTML = `
      <div class="map-message-content">
        <div class="map-message-icon">${icon}</div>
        <div class="map-message-text">${this.escapeHtml(message)}</div>
        ${duration > 0 ? '<div class="map-message-close">×</div>' : ''}
      </div>
    `;

        // Aggiungi lo stile CSS
        div.style.backgroundColor =
          type === 'error'
            ? '#ffebee'
            : type === 'warning'
            ? '#fff3e0'
            : '#e8f5e9';
        div.style.borderLeft = `4px solid ${
          type === 'error'
            ? '#6b6b6b'
            : type === 'warning'
            ? '#ff9800'
            : '#4caf50'
        }`;
        div.style.padding = '12px';
        div.style.margin = '10px';
        div.style.borderRadius = '4px';
        div.style.boxShadow = '0 2px 5px rgba(0,0,0,0.2)';
        div.style.maxWidth = '300px';
        div.style.fontSize = '14px';
        div.style.color = '#333';

        // Stili per contenitore interno
        const content = div.querySelector(
          '.map-message-content'
        ) as HTMLElement;
        if (content) {
          content.style.display = 'flex';
          content.style.alignItems = 'flex-start';
        }

        // Stili per icona
        const iconElement = div.querySelector(
          '.map-message-icon'
        ) as HTMLElement;
        if (iconElement) {
          iconElement.style.marginRight = '10px';
          iconElement.style.fontSize = '18px';
          iconElement.style.color =
            type === 'error'
              ? '#6b6b6b'
              : type === 'warning'
              ? '#ff9800'
              : '#4caf50';
        }

        // Stili per pulsante chiusura
        const closeButton = div.querySelector(
          '.map-message-close'
        ) as HTMLElement;
        if (closeButton) {
          closeButton.style.marginLeft = 'auto';
          closeButton.style.cursor = 'pointer';
          closeButton.style.fontSize = '20px';
          closeButton.style.color = '#999';

          // Gestione evento in maniera corretta
          L.DomEvent.on(closeButton, 'click', () => {
            this.clearMapMessage();
          });
        }
        return div;
      };
    }

    if (this.mapMessageControl) {
      this.mapMessageControl.addTo(this.map);
    }

    // Auto-rimuovi dopo la durata specificata
    if (duration > 0) {
      this.messageTimeoutId = setTimeout(() => {
        this.clearMapMessage();
      }, duration);
    }
  }

  // Metodo helper per evitare problemi di sicurezza con HTML nei messaggi
  private escapeHtml(unsafe: string): string {
    return unsafe
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
  /**
   * Rimuove il messaggio dalla mappa
   */
  private clearMapMessage(): void {
    if (this.messageTimeoutId) {
      clearTimeout(this.messageTimeoutId);
      this.messageTimeoutId = null;
    }

    if (this.mapMessageControl && this.map) {
      this.map.removeControl(this.mapMessageControl);
      this.mapMessageControl = null;
    }
  }

  // Nuova funzione per visualizzare i punti del percorso (1 ogni 10)
  // private addRouteDots(): L.FeatureGroup {
  //   // Crea un gruppo di layer per i punti del percorso
  //   const routeDotsGroup = L.featureGroup();

  //   // Campiona l'array allRoutesCoords (1 punto ogni 10)
  //   const sampledPoints = this.allRoutesCoords.filter(
  //     (_, index) => index % 10 === 0
  //   );

  //   console.log(
  //     `Visualizzo ${sampledPoints.length} punti del percorso (1 ogni 10 di ${this.allRoutesCoords.length})`
  //   );

  //   // Aggiungi un marker per ogni punto campionato
  //   sampledPoints.forEach((point, index) => {
  //     // Crea un punto SVG grigio con numero all'interno
  //     const markerSize = 10; // Dimensione del marker
  //     const routeMarkerHtml = `
  //     <svg width="${markerSize}" height="${markerSize}" viewBox="0 0 ${markerSize} ${markerSize}" xmlns="http://www.w3.org/2000/svg">
  //       <circle cx="${markerSize / 2}" cy="${markerSize / 2}" r="${
  //       markerSize / 2 - 1
  //     }"
  //               fill="#777777" stroke="#555555" stroke-width="1" opacity="0.8"/>

  //     </svg>
  //   `;

  //     const routeIcon = L.divIcon({
  //       className: 'route-dot-marker',
  //       html: routeMarkerHtml,
  //       iconSize: [markerSize, markerSize],
  //       iconAnchor: [markerSize / 2, markerSize / 2]
  //     });

  //     // Crea il marker e aggiungi un tooltip con indice reale
  //     const marker = L.marker(point, { icon: routeIcon }).bindTooltip(
  //       `Punto ${index + 1} (indice reale: ${index * 10})`,
  //       {
  //         permanent: false,
  //         direction: 'top',
  //         offset: [0, -3]
  //       }
  //     );

  //     // Aggiungi il marker al gruppo
  //     routeDotsGroup.addLayer(marker);
  //   });

  //   // Il gruppo viene creato ma NON aggiunto alla mappa
  //   // (sarà gestito tramite il metodo toggleRoutePoints)

  //   return routeDotsGroup;
  // }

  private addHighFrequencyDots(): L.FeatureGroup {
    // Crea un gruppo di layer per i punti del percorso
    const HFDotsGroup = L.featureGroup();

    // Campiona l'array allRoutesCoords (1 punto ogni 10)
    const sampledPoints = this.allHFCoords.filter(
      (_, index) => index % 10 === 0
    );

    console.log(
      `Visualizzo ${sampledPoints.length} punti del percorso (1 ogni 10 di ${this.allHFCoords.length})`
    );

    // Aggiungi un marker per ogni punto campionato
    sampledPoints.forEach((point, index) => {
      // Crea un punto SVG grigio con numero all'interno
      const markerSize = 10; // Dimensione del marker
      const routeMarkerHtml = `
      <svg width="${markerSize}" height="${markerSize}" viewBox="0 0 ${markerSize} ${markerSize}" xmlns="http://www.w3.org/2000/svg">
        <circle cx="${markerSize / 2}" cy="${markerSize / 2}" r="${
        markerSize / 2 - 1
      }"
                fill="#777777" stroke="#555555" stroke-width="1" opacity="0.8"/>

      </svg>
    `;

      const routeIcon = L.divIcon({
        className: 'route-dot-marker',
        html: routeMarkerHtml,
        iconSize: [markerSize, markerSize],
        iconAnchor: [markerSize / 2, markerSize / 2]
      });

      // Crea il marker e aggiungi un tooltip con indice reale
      const marker = L.marker(point, { icon: routeIcon }).bindTooltip(
        `Punto ${index + 1} (indice reale: ${index * 10})`,
        {
          permanent: false,
          direction: 'top',
          offset: [0, -3]
        }
      );

      // Aggiungi il marker al gruppo
      HFDotsGroup.addLayer(marker);
    });

    // Il gruppo viene creato ma NON aggiunto alla mappa
    // (sarà gestito tramite il metodo toggleRoutePoints)

    return HFDotsGroup;
  }

  // Metodo per attivare/disattivare la visualizzazione dei punti del percorso
  // public toggleRoutePoints(): void {
  //   if (!this.map) {
  //     console.warn('Mappa non disponibile');
  //     return;
  //   }

  //   // Crea il gruppo se non esiste
  //   if (!this.routeDotsGroup) {
  //     this.routeDotsGroup = this.addRouteDots();
  //   }

  //   // Controlla il livello di zoom
  //   //const currentZoom = this.map.getZoom();
  //   // if (currentZoom < this.zoomThreshold) {
  //   //   this.showInfoMessage(`Livello di zoom (${currentZoom}) inferiore alla soglia (${this.zoomThreshold}). Aumenta lo zoom per vedere i punti del percorso.`);
  //   //   return;
  //   // }

  //   if (this.routePointsVisible) {
  //     // Rimuovi dalla mappa
  //     if (this.map.hasLayer(this.routeDotsGroup)) {
  //       this.map.removeLayer(this.routeDotsGroup);
  //     }
  //     this.routePointsVisible = false;
  //     console.log('Punti del percorso nascosti');
  //   } else {
  //     // Aggiungi alla mappa
  //     this.routeDotsGroup.addTo(this.map);
  //     this.routePointsVisible = true;
  //     console.log('Punti del percorso visualizzati');
  //   }

  //   // Aggiorna il bottone (se lo implementi nell'interfaccia)
  //   this.updateRoutePointsButton();
  // }

  // Aggiorna lo stato del bottone dei punti del percorso
  private updateRoutePointsButton(): void {
    const button = document.getElementById('route-points-toggle-btn');
    if (button) {
      const icon = button.querySelector('mat-icon');
      if (icon) {
        // Aggiorna l'icona in base allo stato
        icon.textContent = this.routePointsVisible
          ? 'timeline'
          : 'timeline_outlined';
      }

      // Aggiorna la classe
      if (this.routePointsVisible) {
        button.classList.add('active');
      } else {
        button.classList.remove('active');
      }
    }
  }

  private showRouteLoadingIndicator(show: boolean): void {
    this.isRoutingInProgress = show;
    this.mapContainerService.setJourneyLoading(show); // Aggiorna il service
    // Rimuovi un eventuale indicatore esistente
    const existingIndicator = document.getElementById(
      'route-loading-indicator'
    );
    if (existingIndicator) {
      existingIndicator.remove();
    }

    if (show && this.map) {
      // Crea l'elemento indicatore
      const indicator = document.createElement('div');
      indicator.id = 'route-loading-indicator';
      indicator.className = 'route-loading-indicator';
      indicator.innerHTML = `
      <div class="spinner"></div>
      <span>${this.translate.instant('mapPage.routeCalculation')}</span>
    `;

      // Aggiungi stili CSS inline
      indicator.style.position = 'absolute';
      indicator.style.bottom = '20px';
      indicator.style.left = '20px';
      indicator.style.backgroundColor = 'rgba(255, 255, 255, 0.9)';
      indicator.style.padding = '8px 12px';
      indicator.style.borderRadius = '4px';
      indicator.style.boxShadow = '0 2px 5px rgba(0, 0, 0, 0.2)';
      indicator.style.zIndex = '1000';
      indicator.style.display = 'flex';
      indicator.style.alignItems = 'center';

      // Stili per lo spinner
      const spinner = indicator.querySelector('.spinner') as HTMLElement;
      if (spinner) {
        spinner.style.width = '20px';
        spinner.style.height = '20px';
        spinner.style.borderRadius = '50%';
        spinner.style.border = '3px solid #f3f3f3';
        spinner.style.borderTop = '3px solid #3388ff';
        spinner.style.animation = 'spin 1s linear infinite';
        spinner.style.marginRight = '10px';
      }

      // Aggiungi regole keyframe per l'animazione
      const styleSheet = document.createElement('style');
      styleSheet.textContent = `
      @keyframes spin {
        0% { transform: rotate(0deg); }
        100% { transform: rotate(360deg); }
      }
    `;
      document.head.appendChild(styleSheet);

      // Aggiungi l'indicatore al container della mappa
      const mapContainer = this.map.getContainer();
      mapContainer.appendChild(indicator);
    }
  }

  /**
   * Crea marker per tutti i punti di inizio e fine degli journey
   * @returns Il gruppo Leaflet contenente tutti i marker
   */
  private addJourneyEndpoints(): L.FeatureGroup {
    // Crea un gruppo di layer per i punti inizio/fine
    const journeyEndpointsGroup = L.featureGroup();

    // Ottieni la lista ordinata dei journey
    const sortedJourneys = [...this.JourneyCoords].sort(
      (a, b) => a.trip_id - b.trip_id
    );
    const totalJourneys = sortedJourneys.length;

    // Itera su tutti i journey e crea marker per i punti inizio/fine
    sortedJourneys.forEach((journey, index) => {
      const { trip_id, coordinates } = journey;
      const isFirst = index === 0;
      const isLast = index === totalJourneys - 1;
      const prevJourneyId =
        index > 0 ? sortedJourneys[index - 1].trip_id : null;
      const nextJourneyId =
        index < totalJourneys - 1 ? sortedJourneys[index + 1].trip_id : null;

      // Trova i dati completi dei journey correlati
      const currentJourney = this.journeys.find((j) => j.trip_id === trip_id);
      const isInProgress = currentJourney?.status === 'IN_PROGRESS'; // Check if journey is in progress
      const prevJourney = prevJourneyId
        ? this.journeys.find((j) => j.trip_id === prevJourneyId)
        : null;
      const nextJourney = nextJourneyId
        ? this.journeys.find((j) => j.trip_id === nextJourneyId)
        : null;

      // Punto di inizio
      if (coordinates.start) {
        // Solo la partenza del primo journey ha dimensione maggiore e z-index alto
        const isFirstStart = isFirst;
        const markerSize = isFirstStart ? 14 : 12;
        const zIndexOffset = isFirstStart ? 10000 : 8000;

        // Determina colori in base alla posizione
        const fillColor = isFirst ? '#4CAF50' : '#6b6b6b';
        const strokeColor = '#4CAF50';
        const strokeWidth = isFirst ? 1 : 2;

        // SVG con lettera "P" solo per il primo punto
        const startMarkerHtml = `
  <svg width="${markerSize * 2}" height="${markerSize * 2}" viewBox="0 0 ${
          markerSize * 2
        } ${markerSize * 2}" xmlns="http://www.w3.org/2000/svg">
    ${
      isFirst
        ? `<circle cx="${markerSize}" cy="${markerSize}" r="${
            markerSize + 1
          }" fill="rgba(255,255,255,0.5)" stroke="white" stroke-width="1" />`
        : ''
    }
    <circle cx="${markerSize}" cy="${markerSize}" r="${markerSize - 1}"
            fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" opacity="0.95"/>
    ${
      isFirst
        ? `<text x="${markerSize}" y="${markerSize + 4}"
         text-anchor="middle"
         fill="white"
         font-family="Arial"
         font-size="${markerSize - 2}"
         font-weight="bold">${this.translate.instant('mapPage.D')}</text>`
        : ''
    }
  </svg>
`;

        const startIcon = L.divIcon({
          className: 'journey-endpoint-marker start',
          html: startMarkerHtml,
          iconSize: [markerSize * 2, markerSize * 2],
          iconAnchor: [markerSize, markerSize]
        });

        // Costruisci il popup HTML personalizzato
        let popupContent = '';

        if (isFirst) {
          // Il primo punto è solo partenza
          const startTime = currentJourney?.start_time
            ? new Date(currentJourney.start_time).toLocaleString()
            : 'N/D';

          popupContent = `
          <div class="custom-popup journey-popup bg-white">
            <div class="journey-popup-header" style="background-color: #4CAF50; color: white; padding: 8px; border-radius: 4px 4px 0 0;">
              <h4 style="margin: 0; font-size: 14px;">${this.translate.instant(
                'mapPage.departure'
              )} ${this.translate.instant('mapPage.journey')} ${trip_id}</h4>
            </div>
            <div class="journey-popup-content" style="padding: 8px;">
              <p style="margin: 4px 0;"><strong>${this.translate.instant(
                'mapPage.timing'
              )}:</strong> ${startTime}</p>
              <p style="margin: 4px 0;"><strong>${this.translate.instant(
                'mapPage.address'
              )}:</strong> ${currentJourney?.addressStart || 'N/D'}</p>
            </div>
          </div>
        `;
        } else {
          // Punti intermedi sono sia arrivo del journey precedente che partenza di questo journey
          const arrivalTime = prevJourney?.end_time
            ? new Date(prevJourney.end_time).toLocaleString()
            : 'N/D';
          const departureTime = currentJourney?.start_time
            ? new Date(currentJourney.start_time).toLocaleString()
            : 'N/D';

          popupContent = `
          <div class="custom-popup journey-popup bg-white">
            <div class="journey-popup-header" style="background-color: #6b6b6b; color: white; padding: 8px; border-radius: 4px 4px 0 0;">
              <h4 style="margin: 0; font-size: 14px;">${this.translate.instant(
                'mapPage.arrival'
              )} ${this.translate.instant(
            'mapPage.journey'
          )} ${prevJourneyId}</h4>
            </div>
            <div class="journey-popup-content" style="padding: 8px; border-bottom: 1px solid #eee;">
              <p style="margin: 4px 0;"><strong>${this.translate.instant(
                'mapPage.timing'
              )}:</strong> ${arrivalTime}</p>
              <p style="margin: 4px 0;"><strong>${this.translate.instant(
                'mapPage.address'
              )}:</strong> ${prevJourney?.addressEnd || 'N/D'}</p>
            </div>
            <div class="journey-popup-header" style="background-color: #4CAF50; color: white; padding: 8px; margin-top: 8px;">
              <h4 style="margin: 0; font-size: 14px;">${this.translate.instant(
                'mapPage.departure'
              )}  ${this.translate.instant('mapPage.journey')} ${trip_id}</h4>
            </div>
            <div class="journey-popup-content" style="padding: 8px;">
              <p style="margin: 4px 0;"><strong>${this.translate.instant(
                'mapPage.timing'
              )}:</strong> ${departureTime}</p>
              <p style="margin: 4px 0;"><strong>${this.translate.instant(
                'mapPage.address'
              )}:</strong> ${currentJourney?.addressStart || 'N/D'}</p>
            </div>
          </div>
        `;
        }

        const startMarker = L.marker(coordinates.start, {
          icon: startIcon,
          zIndexOffset: zIndexOffset
        }).bindPopup(popupContent, {
          maxWidth: 300,
          minWidth: 250,
          className: 'journey-custom-popup',
          closeButton: true,
          autoPan: true
        });

        journeyEndpointsGroup.addLayer(startMarker);
      }

      // Punto di fine
      if (coordinates.end) {
        // Solo l'arrivo dell'ultimo journey ha dimensione maggiore e z-index alto
        const isLastEnd = isLast;
        const markerSize = isLastEnd ? 14 : 12;
        const zIndexOffset = isLastEnd ? 10000 : 8000;

        // Determina colori in base alla posizione

        // Determina colori in base alla posizione e allo stato
        const fillColor = isInProgress ? '#4CAF50' : '#6b6b6b'; // Verde se IN_PROGRESS, altrimenti grigio
        const strokeColor = isInProgress
          ? '#388E3C'
          : isLast
          ? '#6b6b6b'
          : '#4CAF50'; // Bordo adattato
        const strokeWidth = isLast ? 1 : 2;
        // SVG con lettera "A" solo per l'ultimo punto
        // SVG con lettera "A" solo per l'ultimo punto

        const endMarkerHtml = `
  <svg width="${markerSize * 2}" height="${markerSize * 2}" viewBox="0 0 ${
          markerSize * 2
        } ${markerSize * 2}" xmlns="http://www.w3.org/2000/svg">
    ${
      isLast
        ? `<circle cx="${markerSize}" cy="${markerSize}" r="${
            markerSize + 1
          }" fill="rgba(255,255,255,0.5)" stroke="white" stroke-width="1" />`
        : ''
    }
    <circle cx="${markerSize}" cy="${markerSize}" r="${markerSize - 1}"
            fill="${fillColor}" stroke="${strokeColor}" stroke-width="${strokeWidth}" opacity="0.95"/>
    ${
      isLast
        ? isInProgress
          ? // Freccia direzionale per journey IN_PROGRESS
            `<g transform="translate(${markerSize}, ${markerSize}) rotate(${
              currentJourney?.angle_current || 0
            })">
   <path d="M0,-${markerSize / 2} L${markerSize / 3},${markerSize / 3} L0,${
              markerSize / 5
            } L-${markerSize / 3},${markerSize / 3} Z"
         fill="white" />
 </g>`
          : // "A" per journey completati
            `<text x="${markerSize}" y="${markerSize + 4}"
           text-anchor="middle"
           fill="white"
           font-family="Arial"
           font-size="${markerSize - 2}"
           font-weight="bold">${this.translate.instant('mapPage.A')}</text>`
        : ''
    }
  </svg>
`;

        const endIcon = L.divIcon({
          className: 'journey-endpoint-marker end',
          html: endMarkerHtml,
          iconSize: [markerSize * 2, markerSize * 2],
          iconAnchor: [markerSize, markerSize]
        });

        // Costruisci il popup HTML personalizzato
        let popupContent = '';

        if (isLast) {
          // L'ultimo punto è solo arrivo
          const endTime = currentJourney?.end_time
            ? new Date(currentJourney.end_time).toLocaleString()
            : 'N/D';

          // Colore dinamico basato sullo stato
          const headerColor = isInProgress ? '#4CAF50' : '#6b6b6b';

          popupContent = `
    <div class="custom-popup journey-popup bg-white">
      <div class="journey-popup-header" style="background-color: ${headerColor}; color: white; padding: 8px; border-radius: 4px 4px 0 0;">
        <h4 style="margin: 0; font-size: 14px;">${this.translate.instant(
          'mapPage.arrival'
        )}  ${this.translate.instant('mapPage.journey')}  ${trip_id}</h4>
      </div>
      <div class="journey-popup-content" style="padding: 8px;">
        <p style="margin: 4px 0;"><strong>${this.translate.instant(
          'mapPage.timing'
        )}:</strong> ${endTime}</p>
        <p style="margin: 4px 0;"><strong>${this.translate.instant(
          'mapPage.address'
        )}:</strong> ${currentJourney?.addressEnd || 'N/D'}</p>
        <p style="margin: 4px 0;"><strong>${this.translate.instant(
          'mapPage.state'
        )}:</strong> ${
            this.translateJourneyStatus(currentJourney?.status!) || 'N/D'
          }</p>
      </div>
    </div>
    `;
        } else {
          // Punti intermedi sono sia arrivo del journey corrente che partenza del prossimo
          const arrivalTime = currentJourney?.end_time
            ? new Date(currentJourney.end_time).toLocaleString()
            : 'N/D';
          const departureTime = nextJourney?.start_time
            ? new Date(nextJourney.start_time).toLocaleString()
            : 'N/D';
          // Colori dinamici basati sullo stato del journey corrente
          const arrivalHeaderColor = isInProgress ? '#4CAF50' : '#6b6b6b';
          const departureHeaderColor = '#4CAF50'; // La partenza è sempre verde

          popupContent = `
    <div class="custom-popup journey-popup bg-white">
      <div class="journey-popup-header" style="background-color: ${arrivalHeaderColor}; color: white; padding: 8px; border-radius: 4px 4px 0 0;">
        <h4 style="margin: 0; font-size: 14px;">${this.translate.instant(
          'mapPage.arrival'
        )} ${this.translate.instant('mapPage.journey')} ${trip_id}</h4>
      </div>
      <div class="journey-popup-content" style="padding: 8px; border-bottom: 1px solid #eee;">
        <p style="margin: 4px 0;"><strong>${this.translate.instant(
          'mapPage.timing'
        )}:</strong> ${arrivalTime}</p>
        <p style="margin: 4px 0;"><strong>${this.translate.instant(
          'mapPage.address'
        )}:</strong> ${currentJourney?.addressEnd || 'N/D'}</p>
        <p style="margin: 4px 0;"><strong>${this.translate.instant(
          'mapPage.state'
        )}:</strong> ${
            this.translateJourneyStatus(currentJourney?.status!) || 'N/D'
          }</p>
      </div>
      <div class="journey-popup-header" style="background-color: ${departureHeaderColor}; color: white; padding: 8px; margin-top: 8px;">
        <h4 style="margin: 0; font-size: 14px;">${this.translate.instant(
          'mapPage.departure'
        )} ${this.translate.instant('mapPage.journey')} ${nextJourneyId}</h4>
      </div>
      <div class="journey-popup-content" style="padding: 8px;">
        <p style="margin: 4px 0;"><strong>${this.translate.instant(
          'mapPage.timing'
        )}:</strong> ${departureTime}</p>
        <p style="margin: 4px 0;"><strong>${this.translate.instant(
          'mapPage.address'
        )}:</strong> ${nextJourney?.addressStart || 'N/D'}</p>
        <p style="margin: 4px 0;"><strong>${this.translate.instant(
          'mapPage.state'
        )}:</strong> ${
            this.translateJourneyStatus(nextJourney?.status!) || 'N/D'
          }</p>
      </div>
    </div>
    `;
        }

        const endMarker = L.marker(coordinates.end, {
          icon: endIcon,
          zIndexOffset: zIndexOffset
        }).bindPopup(popupContent, {
          maxWidth: 300,
          minWidth: 250,
          className: 'journey-custom-popup',
          closeButton: true,
          autoPan: true
        });

        journeyEndpointsGroup.addLayer(endMarker);
      }
    });

    return journeyEndpointsGroup;
  }
  /**
   * Toggle della visualizzazione dei punti di inizio e fine degli journey
   */
  public toggleJourneyEndpoints(): void {
    if (!this.map) {
      console.warn('Mappa non disponibile');
      return;
    }

    // Crea il gruppo se non esiste
    if (!this.journeyEndpointsGroup) {
      this.journeyEndpointsGroup = this.addJourneyEndpoints();
    }

    if (this.journeyEndpointsVisible) {
      // Rimuovi dalla mappa
      if (this.map.hasLayer(this.journeyEndpointsGroup)) {
        this.map.removeLayer(this.journeyEndpointsGroup);
      }
      this.journeyEndpointsVisible = false;
      console.log('Punti inizio/fine journey nascosti');
    } else {
      // Aggiungi alla mappa
      this.journeyEndpointsGroup.addTo(this.map);
      this.journeyEndpointsVisible = true;
      console.log('Punti inizio/fine journey visualizzati');
    }

    // Aggiorna il bottone (se implementato nell'interfaccia)
    this.updateJourneyEndpointsButton();
  }

  // Aggiorna lo stato del bottone dei punti inizio/fine
  private updateJourneyEndpointsButton(): void {
    const button = document.getElementById('journey-endpoints-toggle-btn');
    if (button) {
      const icon = button.querySelector('mat-icon');
      if (icon) {
        // Aggiorna l'icona in base allo stato
        icon.textContent = this.journeyEndpointsVisible
          ? 'room'
          : 'room_outlined';
      }
    }
  }
  /**
   * Reimposta completamente la mappa e tutte le variabili associate
   * allo stato iniziale, simulando un onInit
   */
  resetMap(): void {
    console.log('Reimpostazione completa della mappa in corso...');

    // 1. RIMOZIONE LAYER DALLA MAPPA
    if (this.map) {
      // PULIZIA APPROFONDITA PER I DECORATORI DI FRECCE
      // 1. Rimuovi il gruppo principale delle frecce
      if (this.map.hasLayer(this.arrowDecoratorsGroup)) {
        this.map.removeLayer(this.arrowDecoratorsGroup);
      }

      // 2. Rimuovi tutti i gruppi di frecce individuali
      this.journeyArrowDecoratorsMap.forEach((decoratorGroup) => {
        if (this.map!.hasLayer(decoratorGroup)) {
          this.map!.removeLayer(decoratorGroup);
        }
      });

      // 3. Pulizia specifica per i decoratori che potrebbero non essere tracciati
      document
        .querySelectorAll('.leaflet-pane path[stroke-dasharray]')
        .forEach((el) => {
          el.remove();
        });

      // 4. Pulizia dei simboli di freccia (arrowhead)
      document
        .querySelectorAll('.leaflet-pane path[fill="black"]')
        .forEach((el) => {
          el.remove();
        });

      // Rimuovi tutti i layer personalizzati
      this.map.eachLayer((layer) => {
        // Mantieni solo il layer di base tile
        if (!(layer instanceof L.TileLayer)) {
          // Rimozione esplicita per ogni tipo di layer
          this.map!.removeLayer(layer);
        }
      });

      // Pulizia specifica per le polilinee che potrebbero non essere state catturate
      const polylines = document.querySelectorAll('.leaflet-overlay-pane path');
      polylines.forEach((path) => {
        path.remove();
      });
    }

    // 2. SVUOTA ARRAY E MAPPE
    this.waypoints = [];
    this.allRoutesCoords = [];
    this.JourneyCoords = [];
    this.highlightedSegments = [];
    this.journeys = [];
    this.journeysDetails = [];

    this.journeyHighlightMap.clear();
    this.showFallbackMarkers.clear();

    // Svuota e reinizializza esplicitamente le collezioni dei decoratori
    this.journeyArrowDecoratorsMap.clear();
    this.arrowDecoratorsGroup = L.layerGroup();

    // 3. RESETTA GRUPPI LAYER
    this.waypointDotsGroup = null;
    this.routeDotsGroup = null;
    this.journeyEndpointsGroup = null;
    this.startMarkersGroup = null;
    this.endMarkersGroup = null;
    this.unmatchedWaypointsGroup = null;
    this.waypointsGroup = null;
    this.journeyWaypointsGroup = L.featureGroup(); // Nuovo gruppo vuoto

    // 4. RESETTA VARIABILI DI STATO
    this.waypointsVisible = false;
    this.routePointsVisible = false;
    this.journeyEndpointsVisible = false;
    this.isRoutingInProgress = false;
    this.isLoading = false;
    this.error = null;

    // 5. RIMUOVI ELEMENTI UI
    this.showRouteLoadingIndicator(false);
    this.clearMapMessage();

    // 6. AGGIORNA CONTROLLI UI
    this.updateWaypointToggleButton();
    this.updateRoutePointsButton();
    this.updateJourneyEndpointsButton();

    // 7. REIMPOSTA LA VISTA DELLA MAPPA
    if (this.map) {
      this.map.setView([45.0703, 7.6869], 12);
    }

    console.log('Mappa reimpostata con successo');
  }

  // Nuova funzione che ripulisce i dati ma non i layer della mappa
  private resetMapData(): void {
    console.log('Resetting map data...');

    // 1. RIMOZIONE LAYER DALLA MAPPA
    if (this.map) {
      // PULIZIA APPROFONDITA PER I DECORATORI DI FRECCE
      // 1. Rimuovi il gruppo principale delle frecce
      if (this.map.hasLayer(this.arrowDecoratorsGroup)) {
        this.map.removeLayer(this.arrowDecoratorsGroup);
      }

      // 2. Rimuovi tutti i gruppi di frecce individuali
      this.journeyArrowDecoratorsMap.forEach((decoratorGroup) => {
        if (this.map!.hasLayer(decoratorGroup)) {
          this.map!.removeLayer(decoratorGroup);
        }
      });

      // 3. Pulizia specifica per i decoratori che potrebbero non essere tracciati
      document
        .querySelectorAll('.leaflet-pane path[stroke-dasharray]')
        .forEach((el) => {
          el.remove();
        });

      // 4. Pulizia dei simboli di freccia (arrowhead)
      document
        .querySelectorAll('.leaflet-pane path[fill="black"]')
        .forEach((el) => {
          el.remove();
        });

      // Pulizia specifici layer personalizzati
      if (this.waypointLinesGroup && this.map.hasLayer(this.waypointLinesGroup)) {
        this.map.removeLayer(this.waypointLinesGroup);
        this.waypointLinesGroup = null;
        console.log('🗑️ Layer linee waypoints rimosso');
      }

      // Rimuovi tutti i layer personalizzati
      this.map.eachLayer((layer) => {
        // Mantieni solo il layer di base tile
        if (!(layer instanceof L.TileLayer)) {
          this.map!.removeLayer(layer);
        }
      });

      // Pulizia specifica per le polilinee
      const polylines = document.querySelectorAll('.leaflet-overlay-pane path');
      polylines.forEach((path) => {
        path.remove();
      });
    }

    // Rimuovi tutti gli highlight esistenti
    this.clearAllHighlights();

    // 1. Reset degli array di dati
    this.waypoints = [];
    this.allRoutesCoords = [];
    this.JourneyCoords = [];
    this.journeysDetails = [];

    // Reset dei flag di visibilità
    this.waypointLinesVisible = false;

    // 2. Resetta la selezione corrente
    if (this.currentlySelectedJourneyId !== null) {
      this.deselectJourneyCard(this.currentlySelectedJourneyId);
      this.currentlySelectedJourneyId = null;
    }

    // 3. Pulisci le mappe di stato
    this.journeyHighlightMap.clear();

    // Svuota e reinizializza esplicitamente le collezioni dei decoratori
    this.journeyArrowDecoratorsMap.clear();
    this.arrowDecoratorsGroup = L.layerGroup();

    console.log('Map data reset complete');
  }

  // Nuova funzione per rimuovere solo i waypoint dalla mappa
  private clearMapWaypoints(): void {
    // Rimuovi waypointDotsGroup se esiste
    if (this.waypointDotsGroup && this.map!.hasLayer(this.waypointDotsGroup)) {
      this.map!.removeLayer(this.waypointDotsGroup);
      this.waypointDotsGroup = null;
    }

    // Rimuovi journeyEndpointsGroup se esiste
    if (
      this.journeyEndpointsGroup &&
      this.map!.hasLayer(this.journeyEndpointsGroup)
    ) {
      this.map!.removeLayer(this.journeyEndpointsGroup);
      this.journeyEndpointsGroup = null;
    }
  }

  private clearAllHighlights(): void {
    console.log('Pulizia di tutti gli highlight...');

    // 1. Rimuovi tutti i layer evidenziati dalla mappa
    this.journeyHighlightMap.forEach((layers, tripId) => {
      layers.forEach((layer) => {
        if (this.map && this.map.hasLayer(layer)) {
          this.map.removeLayer(layer);
        }
      });
    });

    // 2. Svuota la mappa degli highlight
    this.journeyHighlightMap.clear();

    // 3. Deseleziona tutte le journey
    this.journeys.forEach((journey) => {
      journey.selected = false;
    });

    // 4. Resetta l'ID della journey selezionata
    this.currentlySelectedJourneyId = null;

    console.log('Tutti gli highlight sono stati rimossi');
  }

  // Formatta la data per la visualizzazione
  // formatDisplayDate(dateStr: string): string {
  //   if (!dateStr) return '';
  //   const date = new Date(dateStr);
  //   return formatDate(date, 'dd/MM/yyyy HH:mm', 'en-US');
  // }
  formatDisplayDate(dateStr: string): string {
    if (!dateStr) return '';
    // Forza il formato ISO se necessario (Safari richiede il formato con 'T')
    let safeDateStr = dateStr.replace(' ', 'T');
    let date = new Date(safeDateStr);
    if (isNaN(date.getTime())) {
      // Prova a sostituire anche eventuali '/' con '-'
      safeDateStr = safeDateStr.replace(/\//g, '-');
      date = new Date(safeDateStr);
      if (isNaN(date.getTime())) return '';
    }
    return formatDate(date, 'dd/MM/yyyy HH:mm', 'en-US');
  }
  // // Applica intervallo date personalizzato
  // applyCustomDateRange(): void {
  //   if (this.dateRange.valid) {
  //     const start = this.dateRange.get('start')!.value;
  //     const end = this.dateRange.get('end')!.value;

  //     if (start) {
  //       this.paramStartTime = formatDate(start, 'yyyy-MM-dd', 'en-US') + ' ' + this.startTime + ':00';
  //     } else {
  //       console.warn('Start date is null');
  //     }
  //     if (end) {
  //       this.paramEndTime = formatDate(end, 'yyyy-MM-dd', 'en-US') + ' ' + this.endTime + ':59';
  //     } else {
  //       console.warn('End date is null');
  //     }

  //   }

  //     this.resetMap();
  //     this.loadJourneys();
  //     this.loadJourneyDetails();

  // }

  // applyUid(): void {
  //   if (this.tempUid.length > 0) {
  //     // Aggiorna l'UID principale
  //     this.paramUid = this.tempUid;

  //     // Pulisci la mappa e carica i nuovi dati
  //     this.resetMap();
  //     this.loadJourneys();
  //     this.loadJourneyDetails();
  //   }
  // }

  // Metodi per la gestione degli intervalli di tempo
  // selectLastHours(hours: number): void {
  //   const end = new Date();
  //   const start = new Date();
  //   start.setHours(end.getHours() - hours);

  //   this.paramStartTime = formatDate(start, 'yyyy-MM-dd HH:mm:ss', 'en-US');
  //   this.paramEndTime = formatDate(end, 'yyyy-MM-dd HH:mm:ss', 'en-US');
  //  this.applyCustomDateRange();
  // }
  // Verifica se la selezione del range è valida (max 7 giorni)

  // Mostra il dialog
  showDateDialog(): void {
    const dialogConfig = new MatDialogConfig();

    // Imposta larghezza base
    dialogConfig.width = '400px';

    if (this.mapContainerService.getJourneyLoadingState()) {
      console.log('Operazione in corso, richiesta ignorata');
      return;
    }
    this.lastDateSelectionMethod = 'preset';
    // Reset di tutte le variabili relative alla selezione date
    console.log('Reset delle variabili di selezione data');

    // Reset del preset selezionato
    this.selectedPresetOption = null;

    // Inizializza materialDateRange con i valori di default (oggi)
    const today = new Date();
    const startOfToday = new Date(today);
    startOfToday.setHours(0, 0, 0, 0);

    // Reset delle date con valori di default
    this.materialDateRange = {
      start: startOfToday,
      end: today
    };

    // Reset anche dateRange per mantenere sincronizzazione
    this.dateRange = [startOfToday, today];

    // Aggiungi stili personalizzati prima di aprire la dialog
    const selectElements = document.querySelectorAll(
      '.mat-form-field-appearance-fill .mat-form-field-flex'
    );
    selectElements.forEach((el) => {
      el.classList.add('bg-red-50');
    });

    // Apri la dialog Material
    this.dialogRef = this.dialog.open(this.dateRangeDialog, {
      // width: '360px',
      // height: '220px',
      disableClose: false
    });

    // Gestisci la chiusura
    this.dialogRef
      .afterClosed()
      .pipe(takeUntil(this.destroy$))
      .subscribe((result) => {
        // Puoi gestire qui eventuali azioni post-chiusura
        console.log('Dialog chiuso, risultato:', result);
      });
  }
  closeDialog(): void {
    this.dialogRef?.close();
  }

  // Gestisce la selezione dal dropdown
  selectPresetFromDropdown(event: any) {
    if (event.value === 'customdate') {
      this.lastDateSelectionMethod = 'datepicker'; // Salva che è stato usato il preset
      // Aumenta le dimensioni della dialog per mostrare il datepicker
      // if (this.dialogRef) {
      //   this.dialogRef.updateSize('360px', '360px');
      // }
    } else {
      const selectedValue = event.value;
      this.selectTimeRange({ value: event.value });
      this.lastDateSelectionMethod = 'preset'; // Salva che è stato usato il preset
      // if (this.dialogRef) {
      //   this.dialogRef.updateSize('360px', '220px');
      // }
    }

    // this.closeDialog();
  }

  selectTimeRange(event: any): void {
    if (!event || !event.value) return;

    const value = event.value;
    // Chiudi il dialog se è aperto
    this.displayDateRangeDialog = false;

    console.log(`Selezionato intervallo predefinito: ${value}`);

    switch (value) {
      case 'today':
        this.selectToday();
        break;
      case 'yesterday':
        this.selectYesterday();
        break;
      case 'last2':
        this.selectLastDays(2);
        break;
      case 'last3':
        this.selectLastDays(3);
        break;
      case 'last4':
        this.selectLastDays(4);
        break;
      case 'last5':
        this.selectLastDays(5);
        break;
      case 'last6':
        this.selectLastDays(6);
        break;
      case 'last7':
        this.selectLastDays(7);
        break;
      default:
        console.warn(`Intervallo non riconosciuto: ${value}`);
    }
  }

  // Seleziona il giorno corrente (da inizio a fine giornata)
  selectToday(): void {
    const today = new Date();
    const start = new Date(today);
    start.setHours(0, 0, 0);

    this.paramStartTime = formatDate(start, 'yyyy-MM-dd HH:mm:ss', 'en-US');
    this.paramEndTime = formatDate(today, 'yyyy-MM-dd HH:mm:ss', 'en-US');

    // Resetta e ricarica i dati
    // this.resetMap();
    // this.loadJourneys(true);
  }

  // Seleziona il giorno precedente (da inizio a fine giornata di ieri)
  selectYesterday(): void {
    // Ottieni la data di oggi
    const today = new Date();

    // Crea la data di ieri
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    // Imposta l'inizio di ieri (00:00:00)
    const yesterdayStart = new Date(yesterday);
    yesterdayStart.setHours(0, 0, 0, 0);

    // Imposta la fine di ieri (23:59:59)
    const yesterdayEnd = new Date(yesterday);
    yesterdayEnd.setHours(23, 59, 59, 999);

    // Formatta le date per i parametri
    this.paramStartTime = formatDate(
      yesterdayStart,
      'yyyy-MM-dd HH:mm:ss',
      'en-US'
    );
    this.paramEndTime = formatDate(
      yesterdayEnd,
      'yyyy-MM-dd HH:mm:ss',
      'en-US'
    );

    console.log(
      `Selezionato intervallo: ${this.paramStartTime} - ${this.paramEndTime}`
    );

    // Resetta e ricarica i dati
    // this.resetMap();
    // this.loadJourneys(true);
  }

  // Metodo per selezionare gli ultimi N giorni
  selectLastDays(days: number): void {
    const today = new Date();
    const end = new Date(today);
    const now = new Date();
    end.setHours(now.getHours(), now.getMinutes(), now.getSeconds());

    const start = new Date(today);
    start.setDate(today.getDate() - days + 1); // +1 per includere il giorno corrente
    start.setHours(0, 0, 0);

    this.paramStartTime = formatDate(start, 'yyyy-MM-dd HH:mm:ss', 'en-US');
    this.paramEndTime = formatDate(end, 'yyyy-MM-dd HH:mm:ss', 'en-US');

    // Resetta e ricarica i dati
    // this.resetMap();
    // this.loadJourneys(true);
  }

  onDatepickerChanged(): void {
    this.lastDateSelectionMethod = 'datepicker'; // Salva che è stato usato il datepicker
  }
  // Applica il range personalizzato
  applyCustomDateRange() {
    console.log('metodo', this.lastDateSelectionMethod);

    // Se siamo su mobile, gestisci la vista
    if (this.isMobile) {
      this.isLoading = true;
      this.loadingProgress = 0;
      this.loadingMessage = this.translate.instant('mapPage.loading');

      // Se siamo sulla sidebar, passa alla vista mappa
      if (this.activeFooterButton === 'sidebar') {
        this.showMap(); // Questo chiuderà la sidebar e mostrerà la mappa
      } else {
        // Se siamo già sulla mappa, assicuriamoci che la sidebar sia aperta per mostrare il loading
        this.sidenav.open();
        this.setActiveFooterButton('sidebar');
      }
    }

    if (this.lastDateSelectionMethod === 'preset') {
      // Usa il preset
      console.log('Applicando il preset selezionato');
      //this.selectTimeRange(this.selectedPresetOption);
      this.loadJourneys(true);
      this.closeDialog();
    } else {
      if (this.materialDateRange.start && this.materialDateRange.end) {
        console.log('Start date prima:', this.materialDateRange.start);
        console.log('End date prima:', this.materialDateRange.end);

        // Crea copie esplicite delle date per evitare problemi di riferimento
        const startDate = new Date(this.materialDateRange.start);
        const endDate = new Date(this.materialDateRange.end);

        // Imposta l'ora alla fine della giornata per la data di fine (23:59:59)
        endDate.setHours(23, 59, 59, 999);

        console.log('Start date dopo:', startDate);
        console.log('End date dopo:', endDate);

        // Format the dates as ISO strings
        this.paramStartTime = startDate.toISOString();
        this.paramEndTime = endDate.toISOString();

        console.log('Start ISO:', this.paramStartTime);
        console.log('End ISO:', this.paramEndTime);

        this.loadJourneys(true);
        this.closeDialog();
      } else {
        console.error('Date non valide:', this.materialDateRange);
        // Se le date non sono valide, rimuovi il loading su mobile
        if (this.isMobile) {
          this.isLoading = false;
        }
      }
    }
  }

  isDateRangeValid(): boolean {
    if (this.materialDateRange.start && this.materialDateRange.end) {
      const diffTime = Math.abs(
        this.materialDateRange.end.getTime() -
          this.materialDateRange.start.getTime()
      );
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 7; // max 7 giorni
    }
    return true;
  }
  onDateSelect(event: any) {
    // Controllo solo quando si hanno due date selezionate
    if (this.dateRange && this.dateRange.length === 2) {
      if (!this.isDateRangeValid()) {
        // Mostra un messaggio di errore
        this.showInfoMessage(
          this.translate.instant('mapPage.selectedPeriodExceeds7Days')
        );

        // Opzionale: reimposta l'ultima data selezionata
        // Se l'utente ha appena selezionato la data di fine, la reimpostiamo
        if (event && this.dateRange[1] === event) {
          // Calcola la data massima consentita (inizio + 7 giorni)
          const maxEndDate = new Date(this.dateRange[0]);
          maxEndDate.setDate(maxEndDate.getDate() + 7);

          // Imposta la fine al massimo consentito
          this.dateRange[1] = maxEndDate;
        }
      }
    }
  }

  // Metodo per chiudere il calendario
  closeDatepicker() {
    // Se stai usando un riferimento al calendario
    if (this.calendar) {
      this.calendar.overlayVisible = false;
    }
  }

  // Metodo per confermare la selezione
  confirmDateSelection() {
    if (
      this.isDateRangeValid() &&
      this.dateRange &&
      this.dateRange.length === 2
    ) {
      this.closeDatepicker();
      // Qui puoi aggiungere altre azioni, come l'applicazione dell'intervallo
    }
  }

  /**
   * Rimuove tutte le polyline dalla mappa
   */
  private clearMapPolylines(): void {
    if (!this.map) return;

    this.map.eachLayer((layer) => {
      // Verifica se il layer è una polyline standard
      if (layer instanceof L.Polyline && !(layer instanceof L.TileLayer)) {
        this.map!.removeLayer(layer);
        return;
      }

      // Verifica per polylineDecorator usando una verifica sicura per TypeScript
      // @ts-ignore - Accesso alla proprietà 'options.patterns' aggiunta dal plugin
      if (layer.options && layer.options.patterns) {
        this.map!.removeLayer(layer);
      }
    });

    console.log('Polyline rimosse dalla mappa');
  }

  private setupResizeObserver(): void {
    // console.log('Setup ResizeObserver journey');
    if ('ResizeObserver' in window && this.map) {
      const mapContainer = this.map.getContainer();

      this.resizeObserver = new ResizeObserver((entries) => {
        if (this.map) {
          // console.log('Mappa ridimensionata tramite ResizeObserver Journey, entries:', entries.length);
          this.map.invalidateSize();
          // Doppio ridimensionamento per essere sicuri
          setTimeout(() => {
            if (this.map) {
              this.map.invalidateSize();
            }
          }, 50);
        }
      });

      // Osserva il container della mappa
      this.resizeObserver.observe(mapContainer);

      // Trova e osserva anche il mat-sidenav-content
      const sidenavContent = document.querySelector('mat-sidenav-content');
      if (sidenavContent) {
        this.resizeObserver.observe(sidenavContent);
        // console.log('ResizeObserver configurato anche per mat-sidenav-content');
      }

      // Osserva anche il container principale della sidenav
      const sidenavContainer = document.querySelector('mat-sidenav-container');
      if (sidenavContainer) {
        this.resizeObserver.observe(sidenavContainer);
        // console.log('ResizeObserver configurato anche per mat-sidenav-container');
      }
    }
  }

  ngOnDestroy(): void {
    // Ferma l'aggiornamento automatico
    if (this.map) {
      console.log('Destroying map in component destruction');
      this.map.remove();
      this.map = null as any;
    }
   // this.stopAutoUpdate();

    // Cancella le richieste HTTP pendenti
    this.cancelPendingRequests$.next();
    this.cancelPendingRequests$.complete();

    // Annulla tutte le sottoscrizioni attive
    this.activeHttpRequests.forEach((subscription) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
      }
    });

    // Completa il Subject destroy$
    this.destroy$.next();
    this.destroy$.complete();
  }

  // GESTIONE SCIA PER IN_PROGRESS
  /**
   * Avvia l'aggiornamento automatico per i journey in corso
   */
  // private startAutoUpdate(): void {
  //   // Pulisci eventuali interval precedenti
  //   this.stopAutoUpdate();

  //   console.log(
  //     `Avvio aggiornamento automatico ogni ${
  //       this.autoUpdateIntervalTime / 1000
  //     } secondi`
  //   );

  //   // Crea un nuovo interval
  //   this.autoUpdateInterval = setInterval(() => {
  //     this.checkForInProgressJourneys();
  //   }, this.autoUpdateIntervalTime);
  // }

  // /**
  //  * Termina l'aggiornamento automatico
  //  */
  // private stopAutoUpdate(): void {
  //   if (this.autoUpdateInterval) {
  //     clearInterval(this.autoUpdateInterval);
  //     this.autoUpdateInterval = null;
  //     console.log('Aggiornamento automatico fermato');
  //   }
  // }

  /**
   * Verifica se ci sono journey in corso e aggiorna i dati se necessario
   */
  // private checkForInProgressJourneys(): void {
  //   console.log('Verifica journey in corso...');

  //   // Verifica se ci sono journey con status "IN_PROGRESS"
  //   const inProgressJourneys = this.journeys.filter(
  //     (journey) => journey.status === 'IN_PROGRESS'
  //   );

  //   // Esci se non ci sono journey in corso
  //   if (inProgressJourneys.length === 0) {
  //     console.log('Nessun journey in corso');
  //     return;
  //   }

  //   console.log(`Trovati ${inProgressJourneys.length} journey in corso`);

  //   // Memorizza il numero attuale di journey e l'ultimo journey (per end_time)
  //   const currentJourneyCount = this.journeys.length;

  //   // Trova il journey con end_time più recente per il confronto successivo
  //   const sortedJourneys = [...this.journeys].sort((a, b) => {
  //     const timeA = a.end_time ? new Date(a.end_time).getTime() : 0;
  //     const timeB = b.end_time ? new Date(b.end_time).getTime() : 0;
  //     return timeB - timeA;
  //   });
  //   const lastJourney = sortedJourneys[0];

  //   // Aggiorna i dati chiamando il servizio con gli stessi parametri
  //   this.journeyService
  //     .getJourneyList(this.paramUid, this.paramStartTime, this.paramEndTime)
  //     .subscribe({
  //       next: (updatedJourneys) => {
  //         // CASO 1: Stesso numero di journey - controlla cambiamenti nell'ultimo
  //         if (updatedJourneys.length === currentJourneyCount) {
  //           // Trova l'ultimo journey tra quelli aggiornati
  //           const updatedSortedJourneys = [...updatedJourneys].sort((a, b) => {
  //             const timeA = a.end_time ? new Date(a.end_time).getTime() : 0;
  //             const timeB = b.end_time ? new Date(b.end_time).getTime() : 0;
  //             return timeB - timeA;
  //           });
  //           const updatedLastJourney = updatedSortedJourneys[0];

  //           // Confronta i valori chiave
  //           if (lastJourney && updatedLastJourney) {
  //             const endTimeChanged =
  //               lastJourney.end_time !== updatedLastJourney.end_time;
  //             const endLatChanged =
  //               lastJourney.end_lat !== updatedLastJourney.end_lat;
  //             const endLonChanged =
  //               lastJourney.end_lon !== updatedLastJourney.end_lon;

  //             if (endTimeChanged || endLatChanged || endLonChanged) {
  //               console.log(
  //                 "AGGIORNAMENTO: Rilevate modifiche nell'ultimo journey:",
  //                 {
  //                   ID: updatedLastJourney.trip_id,
  //                   'End Time': `${lastJourney.end_time} → ${updatedLastJourney.end_time}`,
  //                   'End Lat': `${lastJourney.end_lat} → ${updatedLastJourney.end_lat}`,
  //                   'End Lon': `${lastJourney.end_lon} → ${updatedLastJourney.end_lon}`
  //                 }
  //               );

  //               // Aggiorna solo il journey modificato
  //               const tripId = updatedLastJourney.trip_id;
  //               if (tripId) {
  //                 this.updateInProgressJourney(tripId);
  //               }
  //             } else {
  //               console.log("Nessuna modifica rilevata nell'ultimo journey");
  //             }
  //           }
  //         }
  //         // CASO 2: Numero di journey aumentato
  //         else if (updatedJourneys.length > currentJourneyCount) {
  //           const newJourneysCount =
  //             updatedJourneys.length - currentJourneyCount;
  //           console.log(
  //             `NUOVO JOURNEY: Rilevati ${newJourneysCount} nuovi journey`
  //           );

  //           // Identifica i nuovi journey
  //           const existingIds = new Set(this.journeys.map((j) => j.trip_id));
  //           const newJourneys = updatedJourneys.filter(
  //             (j) => !existingIds.has(j.trip_id)
  //           );

  //           console.log(
  //             'Dettagli nuovi journey:',
  //             newJourneys.map((j) => ({
  //               id: j.trip_id,
  //               start: j.start_time,
  //               end: j.end_time,
  //               status: j.status
  //             }))
  //           );

  //           // Aggiorna completamente l'elenco
  //           this.journeys = updatedJourneys;
  //           this.cdr.detectChanges();
  //         }
  //         // CASO 3: Numero di journey diminuito
  //         else {
  //           console.log(
  //             `ATTENZIONE: Il numero di journey è diminuito da ${currentJourneyCount} a ${updatedJourneys.length}`
  //           );
  //         }
  //       },
  //       error: (err) => {
  //         console.error(
  //           'Errore durante il controllo dei journey in corso:',
  //           err
  //         );
  //       }
  //     });
  // }

  /**
   * Aggiorna un singolo journey in corso
   * @param tripId ID del journey da aggiornare
   */
  // private updateInProgressJourney(tripId: number): void {
  //   console.log(`Verifica aggiornamenti per journey in corso ${tripId}`);

  //   // Trova il journey corrente
  //   const currentJourney = this.journeys.find((j) => j.trip_id === tripId);
  //   if (!currentJourney) {
  //     console.warn(`Journey ${tripId} non trovato`);
  //     return;
  //   }

  //   // Ottieni la data attuale per l'intervallo
  //   const startTime = currentJourney.start_time;
  //   const endTime = new Date().toISOString(); // Ora corrente come fine
  //   const currentEndTime = currentJourney.end_time
  //     ? new Date(currentJourney.end_time).getTime()
  //     : 0;

  //   // Richiedi i nuovi dati del journey
  //   this.journeyService
  //     .getJourneyList(this.paramUid, startTime, endTime)
  //     .subscribe({
  //       next: (updatedJourneys) => {
  //         // Trova il journey aggiornato nella risposta
  //         const updatedJourney = updatedJourneys.find(
  //           (j) => j.trip_id === tripId
  //         );

  //         if (!updatedJourney) {
  //           console.warn(`Journey ${tripId} non trovato nei dati aggiornati`);
  //           return;
  //         }

  //         // Verifica se è necessario aggiornare confrontando l'end_time
  //         const updatedEndTime = updatedJourney.end_time
  //           ? new Date(updatedJourney.end_time).getTime()
  //           : 0;

  //         // Verifica se ci sono cambiamenti significativi
  //         const hasEndTimeChanged = updatedEndTime > currentEndTime;

  //         const haslatChanged =
  //           currentJourney.end_lat !== updatedJourney.end_lat;
  //         const haslonChanged =
  //           currentJourney.end_lon !== updatedJourney.end_lon;

  //         const needsUpdate =
  //           hasEndTimeChanged || haslatChanged || haslonChanged;

  //         if (needsUpdate) {
  //           console.log(
  //             `Journey ${tripId} ha cambiamenti significativi, aggiornamento in corso`
  //           );

  //           // Aggiorna il journey nell'array
  //           const journeyIndex = this.journeys.findIndex(
  //             (j) => j.trip_id === tripId
  //           );
  //           if (journeyIndex >= 0) {
  //             // Preserva il trip_id originale se necessario
  //             const originalTripId = this.journeys[journeyIndex].trip_id;

  //             // Aggiorna i dati del journey
  //             this.journeys[journeyIndex] = {
  //               ...updatedJourney,
  //               trip_id: originalTripId // Assicura che trip_id rimanga invariato
  //             };

  //             // Crea un nuovo array per forzare l'aggiornamento della vista
  //             this.journeys = [...this.journeys];

  //             // Forza la rilevazione dei cambiamenti
  //             this.cdr.detectChanges();

  //             this.showInfoMessage(
  //               `${this.translate.instant(
  //                 'mapPage.journey'
  //               )} ${tripId} ${this.translate.instant(
  //                 'mapPage.refreshingNewData'
  //               )}`,
  //               2000
  //             );

  //             // Procedi con l'aggiornamento dei waypoints e della mappa
  //             this.getSingleJourneyWaypoints(tripId)
  //               .then((newWaypoints) => {
  //                 if (!newWaypoints || newWaypoints.length === 0) {
  //                   console.log(`Nessun nuovo dettaglio per journey ${tripId}`);
  //                   return;
  //                 }

  //                 console.log(
  //                   `Ricevuti ${newWaypoints.length} nuovi waypoints per journey ${tripId}`
  //                 );

  //                 // Aggiorna la cache con i nuovi waypoints
  //                 this.inProgressJourneys.set(tripId, {
  //                   coordinates: newWaypoints,
  //                   lastUpdateTime: new Date()
  //                 });

  //                 // Aggiorna JourneyCoords con i waypoints aggiornati
  //                 const journeyCoord = this.JourneyCoords.find(
  //                   (j) => j.trip_id === tripId
  //                 );
  //                 if (journeyCoord) {
  //                   (journeyCoord as any).pushWaypoints = newWaypoints;
  //                 }

  //                 // Aggiorna la visualizzazione sulla mappa
  //                 const highlightedElements =
  //                   this.journeyHighlightMap.get(tripId) || [];
  //                 highlightedElements.forEach((element) => {
  //                   if (this.map && this.map.hasLayer(element)) {
  //                     this.map.removeLayer(element);
  //                   }
  //                 });

  //                 // Colore arancione per journey in corso
  //                 const inProgressColor = '#FF9800';

  //                 // Ridisegna il percorso con il nuovo colore, senza spostare la mappa
  //                 this.percorsoSingleJourney(tripId, inProgressColor, false)
  //                   .then((routeGroup) => {
  //                     const newHighlightedElements: L.Layer[] = [];
  //                     routeGroup.getLayers().forEach((layer) => {
  //                       newHighlightedElements.push(layer);
  //                     });
  //                     this.journeyHighlightMap.set(
  //                       tripId,
  //                       newHighlightedElements
  //                     );

  //                     // Aggiorna i dati visualizzati nell'UI
  //                     this.updateJourneyDisplayData(tripId);
  //                   })
  //                   .catch((error) => {
  //                     console.error(
  //                       `Errore nell'aggiornare il percorso: ${error}`
  //                     );
  //                   });
  //               })
  //               .catch((err) => {
  //                 console.error(`Errore nel recupero waypoints: ${err}`);
  //               });
  //           }
  //         } else {
  //           console.log(
  //             `Nessun cambiamento significativo per journey ${tripId}, aggiornamento saltato`
  //           );
  //         }
  //       },
  //       error: (err) => {
  //         console.error(
  //           `Errore nell'aggiornare i dati del journey ${tripId}:`,
  //           err
  //         );
  //       }
  //     });
  // }
  /**
   * Aggiorna i dati visualizzati nell'UI per il journey in corso
   */
  private updateJourneyDisplayData(tripId: number): void {
    // Aggiorna eventuali informazioni nell'UI
    // come tempo di viaggio, distanza, velocità, etc.
    console.log(`Aggiornati dati UI per journey ${tripId}`);
  }

  onBack(): void {
    console.log('Tornare indietro, cancellazione richieste in corso...');

    // 1. Cancella tutte le richieste HTTP pendenti
    this.cancelPendingRequests$.next();

    // 2. Annulla tutte le sottoscrizioni attive
    this.activeHttpRequests.forEach((subscription) => {
      if (subscription && !subscription.closed) {
        subscription.unsubscribe();
        console.log('Sottoscrizione HTTP annullata');
      }
    });
    this.activeHttpRequests = [];

    // 3. Ferma eventuali indicatori di caricamento
    this.showRouteLoadingIndicator(false);
    this.isLoading = false;

    // 4. Rimuovi eventuali messaggi dalla mappa
    this.clearMapMessage();

    // 5. Imposta la pagina attiva
    this.mapContainerService.setActivePage('actia-map');

    // 6. elimino tutti gli higfrequqancy selezionati
    this.clearAllHighFrequencyPoints();

    //7. Imposta pagina attiva anche se sono nell'actia-historical-journey
    // in base a dove sono sottoscritto scatto
    this.hjContaierService.setActivePage('actia-historical-journey');

    this.backClicked = true;
    this.highFrequencyGlobalVisible = false; // Nascondi i punti ad alta frequenza
    this.highFrequencyGlobalVisibleNoHere = false; // Nascondi i punti ad alta frequenza senza HERE
    setTimeout(() => (this.backClicked = false), 0); // reset immediato

    console.log(
      'Navigazione completata, tutte le richieste sono state annullate'
    );
  }
  //FZ qui setto i marker dei sinogli journeyu quando clicco su onCardSelect
  private filterWaypointsByJourney(tripId: number, isSelected: boolean, journeyWaypoints?: [number, number][]): void {
    if (!this.map) return;

    // CASO 1: DESELEZIONE - rimuovi i waypoint verdi invece di ripristinare gli originali
    // CASO 1: DESELEZIONE - ripristina i waypoints originali
    if (!isSelected) {
      console.log(
        `Ripristino waypoints originali dopo deselezione journey ${tripId}`
      );

      // Rimuovi il gruppo attuale di waypoints dalla mappa se esiste
      if (this.waypointDotsGroup && this.map.hasLayer(this.waypointDotsGroup)) {
        this.map.removeLayer(this.waypointDotsGroup);
      }

      // Ricrea il gruppo waypoints originale con tutti i punti
      this.waypointDotsGroup = this.addWaypointDots(this.journeysDetails);

      // Se la visualizzazione waypoints era attiva, riaggiungila alla mappa
      if (this.waypointsVisible) {
        this.waypointDotsGroup.addTo(this.map);
      }

      // Ricrea il gruppo journeyEndpoints originale
      this.journeyEndpointsGroup = this.addJourneyEndpoints();

      // Se la visualizzazione endpoint era attiva, riaggiungila alla mappa
      if (this.journeyEndpointsVisible) {
        this.journeyEndpointsGroup.addTo(this.map);
      }

      return;
    } else {
      // Rimuovi il gruppo attuale di waypoints dalla mappa se esiste
      if (this.waypointDotsGroup && this.map.hasLayer(this.waypointDotsGroup)) {
        this.map.removeLayer(this.waypointDotsGroup);
      }

      // Rimuovi il gruppo journeyEndpointsGroup corrente dalla mappa se esiste
      if (
        this.journeyEndpointsGroup &&
        this.map.hasLayer(this.journeyEndpointsGroup)
      ) {
        this.map.removeLayer(this.journeyEndpointsGroup);
        this.journeyEndpointsGroup = null;
      }
    }

    // CASO 2: SELEZIONE - mostra solo i waypoints del journey selezionato
    console.log(`Filtro waypoints per journey ${tripId}`);

    // // 1. Prima crea un gruppo per i waypoints grigi (tutti gli altri)
    // const grayWaypointGroup = L.featureGroup();

    // // Rendi grigi tutti i waypoints esistenti
    // this.waypoints.forEach((wp, index) => {
    //   const markerSize = 16;
    //   const waypointMarkerHtml = `
    //   <svg width="${markerSize}" height="${markerSize}" viewBox="0 0 ${markerSize} ${markerSize}" xmlns="http://www.w3.org/2000/svg">
    //     <circle cx="${markerSize / 2}" cy="${markerSize / 2}" r="${
    //     markerSize / 2 - 1
    //   }"
    //             fill="#9E9E9E" stroke="#757575" stroke-width="1" opacity="0.6"/>
    //   </svg>
    // `;

    //   const waypointIcon = L.divIcon({
    //     className: 'waypoint-dot-marker gray',
    //     html: waypointMarkerHtml,
    //     iconSize: [markerSize, markerSize],
    //     iconAnchor: [markerSize / 2, markerSize / 2]
    //   });

    //   const marker = L.marker([wp[0], wp[1]], {
    //     icon: waypointIcon,
    //     zIndexOffset: 500 // zIndex più basso per i marker grigi
    //   });

    //   grayWaypointGroup.addLayer(marker);
    // });

    // 2. Usa i waypoints specifici per questo journey (se forniti)
    if (!journeyWaypoints || journeyWaypoints.length === 0) {
      console.log(
        `Nessun waypoint fornito per journey ${tripId}, mostro solo punti grigi`
      );
      return;
    }

    // 3. Crea un gruppo per i waypoints verdi numerati
    const greenWaypointGroup = L.featureGroup();

    // Processa ogni waypoint del journey
    journeyWaypoints.forEach((wp, index) => {
      // Identifica se questo è il primo o l'ultimo punto
      const isFirst = index === 0;
      const isLast = index === journeyWaypoints.length - 1;
      const selectedJourney = this.journeys.find(
        (j) => j.trip_id === tripId
      );
      const isInProgress = selectedJourney?.status === 'IN_PROGRESS';
      // Trova il waypointDetail corrispondente a queste coordinate
      const waypointDetail = this.journeysDetails.find(
        (detail) => detail.gps_lat === wp[0] && detail.gps_lon === wp[1]
      );

      // Dimensione maggiore per i punti di inizio e fine
      const markerSize = isFirst || isLast ? 24 : 20;

      // Stili diversi per punti iniziali, finali e intermedi
      let fillColor = '#4CAF50'; // Verde di default
      let strokeColor = '#388E3C';
      let markerLabel = '';
      let zIndex = 1000;

      if (isFirst) {
        // Punto di partenza con 'P'
        fillColor = '#4CAF50'; // Verde
        markerLabel = this.translate.instant('mapPage.D');
        zIndex = 1500; // zIndex più alto per il punto di partenza
      } else if (isLast) {
        // Punto di arrivo con 'A'
        // Punto di arrivo con 'A' - COLORE DIVERSO PER IN_PROGRESS
        fillColor = isInProgress ? '#4CAF50' : '#6b6b6b'; // Verde se IN_PROGRESS, altrimenti grigio
        strokeColor = isInProgress ? '#388E3C' : '#4CAF50'; // Bordo verde scuro se IN_PROGRESS
        markerLabel = this.translate.instant('mapPage.A'); // Testo 'A' per arrivo
        zIndex = 1500; // zIndex più alto per il punto di arrivo
      } else {
        // Punti intermedi numerati
        // Modifica colore in base allo status se disponibile
        if (waypointDetail && waypointDetail.status) {
          switch (waypointDetail.status) {
            case 'MOVING':
              fillColor = '#4CAF50'; // Verde per veicoli in movimento
              strokeColor = '#388E3C';
              break;
            case 'PARKED':
              fillColor = '#9E9E9E'; // Grigio per veicoli parcheggiati
              strokeColor = '#757575';
              break;
            case 'IDLE':
              fillColor = '#FF9800'; // Arancione per veicoli al minimo
              strokeColor = '#F57C00';
              break;
            case 'UNKNOWN':
              fillColor = '#2196F3'; // Blu per status sconosciuto
              strokeColor = '#1976D2';
              break;
            case 'NOTCONNECTED':
              fillColor = '#FFFFFF'; // Bianco per veicoli non connessi
              strokeColor = '#BDBDBD';
              break;
            default:
              fillColor = '#4CAF50'; // Verde come default
              strokeColor = '#388E3C';
          }
        }
        markerLabel = (index + 1).toString();
      }

      const waypointMarkerHtml = `
        <svg width="${markerSize}" height="${markerSize}" viewBox="0 0 ${markerSize} ${markerSize}" xmlns="http://www.w3.org/2000/svg">
          ${
            isFirst || isLast
              ? `<circle cx="${markerSize / 2}" cy="${markerSize / 2}" r="${
                  markerSize / 2 + 1
                }" fill="rgba(255,255,255,0.5)" stroke="white" stroke-width="1" />`
              : ''
          }
          <circle cx="${markerSize / 2}" cy="${markerSize / 2}" r="${
              markerSize / 2 - 1
            }"
                  fill="${fillColor}" stroke="${strokeColor}" stroke-width="${
              isFirst || isLast ? 2 : 1
            }" opacity="0.95"/>
          ${
            isLast && isInProgress
              ? // Freccia direzionale per journey IN_PROGRESS
                `<g transform="translate(${markerSize / 2}, ${
                  markerSize / 2
                }) rotate(${selectedJourney?.angle_current || 0})">
               <path d="M0,-${markerSize / 3} L${markerSize / 4},${
                  markerSize / 4
                } L0,${markerSize / 6} L-${markerSize / 4},${markerSize / 4} Z"
                     fill="white" />
             </g>`
              : // Testo normale per tutti gli altri punti
                `<text x="${markerSize / 2}" y="${markerSize / 2 + 4}"
                   text-anchor="middle"
                   fill="white"
                   font-family="Arial"
                   font-size="${
                     isFirst || isLast ? markerSize / 2 : markerSize / 2 - 2
                   }"
                   font-weight="bold">${markerLabel}</text>`
          }
        </svg>
      `;

      const waypointIcon = L.divIcon({
        className: `waypoint-dot-marker ${
          isFirst ? 'start' : isLast ? 'end' : 'waypoint'
        } journey-${tripId}`,
        html: waypointMarkerHtml,
        iconSize: [markerSize, markerSize],
        iconAnchor: [markerSize / 2, markerSize / 2]
      });

      const marker = L.marker([wp[0], wp[1]], {
        icon: waypointIcon,
        zIndexOffset: zIndex // zIndex maggiore per i punti di inizio e fine
      });

      // Prepara le informazioni aggiuntive
      const statusDet = waypointDetail?.status
        ? this.vehicleService.translateVehicleStatus(waypointDetail.status)
        : 'N/D';
      const speed =
        waypointDetail?.gps_speed_v !== undefined
          ? `${this.utlService.formatNumber(
              waypointDetail.gps_speed_v,
              1
            )} km/h`
          : 'N/D';
      const timestamp = waypointDetail?.gps_tstamp
        ? new Date(waypointDetail.gps_tstamp).toLocaleString()
        : 'N/D';

      // Crea un tooltip più informativo
      marker.bindTooltip(
        `
        <div class="custom-waypoint-tooltip">
          <div><strong>${this.translate.instant('mapPage.journey')} ${tripId} - ${
            isFirst
              ? this.translate.instant('mapPage.departure')
              : isLast
              ? this.translate.instant('mapPage.arrival')
              : `P. ${index + 1}`
          }</strong></div>
          <div>${this.translate.instant('mapPage.state')}: ${statusDet}</div>
          <div>${this.translate.instant('global.speed')}: ${speed}</div>
          <div>${this.translate.instant('mapPage.hours')}: ${timestamp}</div>
        </div>
      `,
        {
          permanent: false,
          direction: 'top',
          offset: [0, -8],
          className: 'custom-waypoint-tooltip'
        }
      );

      greenWaypointGroup.addLayer(marker);
        });

    // Sostituisci il gruppo di waypoints corrente
    this.waypointDotsGroup = greenWaypointGroup;

    // Aggiungi alla mappa se la visualizzazione waypoints è attiva
    if (this.waypointsVisible) {
      greenWaypointGroup.addTo(this.map!);
    }

    console.log(
      `Creato gruppo waypoints verdi con ${journeyWaypoints.length} punti per journey ${tripId}`
    );
  }

  // Aggiungi questa funzione al componente
  translateJourneyStatus(status: string): string {
    if (!status) return 'N/D';

    if (status === 'IN_PROGRESS') {
      return this.translate.instant('mapPage.status_in_progress');
    } else if (status === 'CLOSED') {
      return this.translate.instant('mapPage.status_closed');
    }
    return 'N/D';
  }

  // Mostra/nasconde le frecce per un journey specifico
  toggleArrowsForJourney(tripId: number, show: boolean): void {
    const decoratorsGroup = this.journeyArrowDecoratorsMap.get(tripId);
    if (!decoratorsGroup) return;

    if (show && !this.map!.hasLayer(decoratorsGroup)) {
      decoratorsGroup.addTo(this.map!);
    } else if (!show && this.map!.hasLayer(decoratorsGroup)) {
      this.map!.removeLayer(decoratorsGroup);
    }
  }

  // Mostra/nasconde tutte le frecce
  toggleAllArrows(show: boolean): void {
    if (show && !this.map!.hasLayer(this.arrowDecoratorsGroup)) {
      this.arrowDecoratorsGroup.addTo(this.map!);
    } else if (!show && this.map!.hasLayer(this.arrowDecoratorsGroup)) {
      this.map!.removeLayer(this.arrowDecoratorsGroup);
    }
  }

  // Funzione per processare i segmenti in parallelo
  // private processSegmentsInParallel(
  //   segments: Array<{ lat: number; lng: number }[]>,
  //   polylineGroup: L.FeatureGroup,
  //   importUrl: string,
  //   showArrows: boolean,
  //   lineColor: string = '#3388ff',
  //   lineWeight: number = 8,
  //   lineOpacity: number = 0.7
  // ): void {
  //   console.log(`Avvio elaborazione parallela di ${segments.length} segmenti`);

  //   // Array per tenere traccia di tutte le promesse
  //   const requests: Promise<void>[] = [];

  //   // Contatore per monitorare il progresso
  //   let completedSegments = 0;
  //   const totalSegments = segments.length;

  //   // Crea una promessa per ogni segmento
  //   segments.forEach((segment, index) => {
  //     // Crea l'oggetto traccia per questo segmento
  //     const traccia = { trace: segment };

  //     // Crea una promessa per questa richiesta HTTP
  //     const request = new Promise<void>((resolve, reject) => {
  //       const subscription = this.httpWithoutInterceptor
  //         .post(importUrl, traccia)
  //         .pipe(takeUntil(this.cancelPendingRequests$))
  //         .subscribe({
  //           next: (response: any) => {
  //             // Processa la risposta
  //             if (
  //               response.routes &&
  //               response.routes[0] &&
  //               response.routes[0].sections
  //             ) {
  //               if (response && response.routes) {
  //                 response.routes.forEach((route) => {
  //                   if (route.sections) {
  //                     this.routeSummaries.push(
  //                       ...route.sections.map((section) => section.summary)
  //                     );
  //                   }
  //                 });
  //               }
  //               console.log('Route response:', response);
  //               console.log('Route summaries:', this.routeSummaries);
  //               this.processRouteResponse(
  //                 response,
  //                 polylineGroup,
  //                 showArrows,
  //                 lineColor,
  //                 lineWeight,
  //                 lineOpacity
  //               );
  //             } else {
  //               console.warn(
  //                 `Nessuna route trovata per il segmento ${index + 1}`
  //               );
  //             }

  //             // Aggiorna il contatore di completamento e mostra il progresso
  //             completedSegments++;
  //             this.showLoadingProgress(completedSegments, totalSegments);

  //             // Risolvi la promessa
  //             resolve();
  //           },
  //           error: (error) => {
  //             console.error(
  //               `Errore durante l'elaborazione del segmento ${index + 1}:`,
  //               error
  //             );
  //             completedSegments++;
  //             this.showLoadingProgress(completedSegments, totalSegments);
  //             resolve(); // Risolvi comunque per non bloccare le altre
  //           },
  //           complete: () => {
  //             // Rimuovi questa sottoscrizione dall'elenco quando completata
  //             const idx = this.activeHttpRequests.indexOf(subscription);
  //             if (idx > -1) {
  //               this.activeHttpRequests.splice(idx, 1);
  //             }
  //           }
  //         });

  //       // Aggiungi la sottoscrizione all'elenco di quelle attive
  //       this.activeHttpRequests.push(subscription);
  //     });

  //     // Aggiungi la promessa all'array
  //     requests.push(request);
  //   });

  //   // Attendi che tutte le promesse siano completate
  //   Promise.all(requests)
  //     .then(() => {
  //       console.log(
  //         `Elaborazione completata per tutti i ${segments.length} segmenti`
  //       );
  //       // Finalizza la visualizzazione del percorso
  //       this.finalizeRouteDisplay(polylineGroup, false);
  //     })
  //     .catch((error) => {
  //       console.error(
  //         "Errore durante l'elaborazione parallela dei segmenti:",
  //         error
  //       );
  //       this.finalizeRouteDisplay(polylineGroup, false);
  //     });
  // }
  // FZ28052025 eliminato momentamente per rilascio
  // async handleShowHighFrequency(event: {
  //   index: number;
  //   show: boolean;
  // }): Promise<void> {
  //   console.log('Show high frequency:', event);
  //   this.showRouteLoadingIndicator(true);
  //   const tripId = event.index;

  //   if (event.show) {
  //     // Rimuovi il gruppo attuale di waypoints dalla mappa se esiste
  //     if (
  //       this.waypointDotsGroup &&
  //       this.map!.hasLayer(this.waypointDotsGroup)
  //     ) {
  //       this.map!.removeLayer(this.waypointDotsGroup);
  //     }
  //     await this.displayHighFrequencyPoints(tripId);
  //     if (this.isUserAdmin()) {
  //       this.clearMapPolylines();
  //       this.percorsoSingleJourney(tripId, '#7e22ce', true);
  //       this.showRouteLoadingIndicator(false);
  //     }
  //   } else {
  //     if (this.waypointDotsGroup) this.waypointDotsGroup.addTo(this.map!);
  //     if (this.highFrequencyLayers && this.highFrequencyLayers.has(tripId)) {
  //       // Ottieni il layer da rimuovere
  //       const layerInfo = this.highFrequencyLayers.get(tripId);

  //       // Rimuovi il layer dalla mappa
  //       if (layerInfo && this.map) {
  //         this.map.removeLayer(layerInfo.layer);
  //         console.log(`Rimosso layer per trip_id: ${tripId}`);
  //       }

  //       // Rimuovi il layer dalla mappa dei layer
  //       this.highFrequencyLayers.delete(tripId);

  //       // Aggiorna l'array di trip_id visualizzati
  //       this.displayedTripIds = this.displayedTripIds.filter(
  //         (id) => id !== tripId
  //       );

  //       this.highFrequencyPointsByTripId.delete(tripId);
  //       if (this.isUserAdmin())
  //       {
  //          this.clearMapPolylines();
  //         this.percorsoSingleJourney(tripId, '#38a83e', true);
  //       }

  //       // Aggiorna gli indici dei marker rimanenti
  //       this.recalculateIndices();
  //       this.showRouteLoadingIndicator(false);
  //     } else {
  //       console.log(`Nessun layer trovato per trip_id: ${tripId}`);
  //     }
  //   }
  // }


    handleShowHighFrequency(event: { index: number; show: boolean }): void {
    console.log('Show high frequency:', event);
    const tripId = event.index;
    if (event.show) {
      this.displayHighFrequencyPoints(tripId);
    } else {
      if (this.highFrequencyLayers && this.highFrequencyLayers.has(tripId)) {
        // Ottieni il layer da rimuovere
        const layerInfo = this.highFrequencyLayers.get(tripId);

        // Rimuovi il layer dalla mappa
        if (layerInfo && this.map) {
          this.map.removeLayer(layerInfo.layer);
          console.log(`Rimosso layer per trip_id: ${tripId}`);
        }

        // Rimuovi il layer dalla mappa dei layer
        this.highFrequencyLayers.delete(tripId);

        // Aggiorna l'array di trip_id visualizzati
        this.displayedTripIds = this.displayedTripIds.filter(
          (id) => id !== tripId
        );

        // Aggiorna gli indici dei marker rimanenti
        this.recalculateIndices();
      } else {
        console.log(`Nessun layer trovato per trip_id: ${tripId}`);
      }
    }
  }
  // Metodo per ricalcolare gli indici dei marker dopo la rimozione di un layer
  private recalculateIndices(): void {
    if (this.displayedTripIds.length === 0) return;

    let startIndex = 0;

    // Ordina i trip_id
    this.displayedTripIds.sort((a, b) => a - b);

    // Per ogni trip_id, aggiorna gli indici dei suoi marker
    for (const tripId of this.displayedTripIds) {
      const layerInfo = this.highFrequencyLayers.get(tripId);
      if (!layerInfo) continue;

      let pointIndex = 0;

      // Per ogni marker nel layer, aggiorna il suo indice
      layerInfo.layer.eachLayer((layer: any) => {
        if (layer instanceof L.Marker) {
          // Ottieni il nuovo indice globale
          const globalIndex = startIndex + pointIndex + 1;

          // Aggiorna il contenuto HTML dell'icona
          const icon = layer.getIcon();
          if (icon && icon.options && icon.options.className) {
            // Mantieni la classe di stato (marker-moving, marker-idle, ecc.)
            const className = icon.options.className
              .split(' ')
              .filter((cls) => cls !== 'marker-number')
              .join(' ');

            // Crea una nuova icona con l'indice aggiornato
            const newIcon = L.divIcon({
              className: `marker-number ${className}`,
              html: `${globalIndex}`,
              iconSize: [20, 20],
              iconAnchor: [10, 10]
            });

            // Aggiorna l'icona del marker
            layer.setIcon(newIcon);

            // Aggiorna anche il contenuto del popup
            const popup = layer.getPopup();
            if (popup) {
              const content = popup.getContent();
              if (typeof content === 'string') {
                // Sostituisci il numero del punto nel popup
                const newContent = content.replace(
                  /Punto:<\/strong> \d+/,
                  `Punto:</strong> ${globalIndex}`
                );
                popup.setContent(newContent);
              }
            }
          }

          pointIndex++;
        }
      });

      // Aggiorna il conteggio totale per il prossimo trip_id
      startIndex += pointIndex;
    }
  }
  /**
   * Ottiene i waypoints per un singolo journey dal servizio, filtrando per il range temporale del journey
   * @param tripId ID del journey di cui ottenere i waypoints
   * @returns Promise con array di waypoints [lat, lon] o null in caso di errore
   */
  getHigFrequencyPoints(tripId: number | string): Promise<
    {
      gps_speed_v: undefined;
      lat: number;
      lng: number;
      tstamp?: string;
      status?: string;
      gps_angle_v?: number;
      gps_alt_v?: number;
    }[]
  > {
    return new Promise((resolve, reject) => {
      // Converti tripId in numero se è una stringa
      const numericTripId =
        typeof tripId === 'string' ? parseInt(tripId, 10) : tripId;

      // Trova il journey per ottenere start_time e end_time
      const journey = this.journeys.find((j) => j.trip_id === numericTripId);

      if (!journey) {
        console.warn(`Journey con ID ${tripId} non trovato`);
        reject(`Journey ${tripId} non trovato`);
        return;
      }

      if (!journey.start_time || !journey.end_time) {
        console.warn(`Journey ${tripId} non ha orari di inizio/fine validi`);
        reject(`Journey ${tripId} senza orari validi`);
        return;
      }

      // Formatta le date nel formato richiesto dall'API (ISO string)
      const startTime = new Date(journey.start_time).toISOString();
      const endTime = new Date(journey.end_time).toISOString();

      console.log(
        `Richiedo high frequency points per journey ${tripId} da ${startTime} a ${endTime}`
      );

      // Chiama il servizio con VIN e range temporale
      this.journeyService
        .getHighFrequencyPoints(this.paramUid, startTime, endTime)
        .subscribe({
          next: (highFrequencyDetails) => {
            console.log(
              `Ricevuti ${highFrequencyDetails.length} dettagli per journey ${tripId}`
            );

            // Estrai i punti e convertili nel formato corretto
            const formattedPoints = highFrequencyDetails
              .filter(
                (detail) => detail.gps_lat != null && detail.gps_lon != null
              )
              .map((detail) => ({
                lat: detail.gps_lat!,
                lng: detail.gps_lon!,
                tstamp: detail.tstamp,
                status: detail.status,
                gps_speed_v: detail.gps_speed_v,
                gps_angle_v: detail.gps_angle_v,
                gps_alt_v: detail.gps_alt_v
              }));

            console.log(
              `Estratti ${formattedPoints.length} punti formattati per journey ${tripId}`
            );

            // Memorizza i punti originali per altri usi se necessario
            this.allHFCoords = formattedPoints.map(
              (p) => [p.lat, p.lng] as [number, number]
            );

            // IMPORTANTE: rimuovi questa riga per evitare di aggiungere i punti due volte
            // this.addHighFrequencyDots();

            // Risolvi la promise con i dati formattati correttamente
            resolve(formattedPoints);
          },
          error: (err) => {
            console.error(
              `Errore nel recupero dei punti ad alta frequenza per journey ${tripId}:`,
              err
            );
            reject(err);
          }
        });
    });
  }
  //   getHigFrequencyPoints(tripId: number): any[] {
  //   console.log(`Chiamata a getHigFrequencyPoints per tripId: ${tripId}`);
  //   // Questo è solo un esempio - sostituisci con la tua vera implementazione
  //   return [
  //     { lat: 45.464, lon: 9.190, speed: 30, status: 'MOVING', timestamp: new Date().toISOString() },
  //     { lat: 45.466, lon: 9.192, speed: 0, status: 'STOPPED', timestamp: new Date().toISOString() }
  //   ];
  // }
  // Dichiarazioni di classe per tracciare i layer e gli indici

  async displayHighFrequencyPoints(tripId: number): Promise<void> {
    console.log('Visualizzazione punti ad alta frequenza per tripId:', tripId);

    try {
      if (!this.map) {
        console.error('ERRORE: Mappa non inizializzata!');
        return;
      }

      // Aggiungi stili CSS per i marker se non esistono già
      this.ensureMarkerStyles();

      const numericTripId = Number(tripId);

      // Ottieni i punti
      const points = await this.getHigFrequencyPoints(numericTripId);
      // Recupera i punti da waypointDotsGroup (se esiste)
      let waypointPoints: any[] = [];
      if (this.waypointDotsGroup) {
        this.waypointDotsGroup.eachLayer((layer: any) => {
          if (layer instanceof L.Marker) {
            const latlng = layer.getLatLng();
            // Cerca il dettaglio corrispondente per timestamp e status
            const detail = this.journeysDetails?.find(
              (d) => d.gps_lat === latlng.lat && d.gps_lon === latlng.lng
            );
            waypointPoints.push({
              lat: latlng.lat,
              lng: latlng.lng,
              tstamp: detail?.gps_tstamp || null,
              status: detail?.status || null,
              gps_speed_v: detail?.gps_speed_v,
              gps_angle_v: detail?.gps_angle_v,
              gps_alt_v: detail?.gps_alt_v
            });
          }
        });
      }
      // Unisci i due array (waypoints + high frequency)
      //const allPoints = [...waypointPoints, ...points]
      // TOLTI I WAYPOINT uso solo gli HF del tripd
      // FZ30072025
      const allPoints = [...points]
        .filter(
          (p) => p.lat != null && p.lng != null
        )
        .sort((a, b) => {
          const ta = a.tstamp ? new Date(a.tstamp).getTime() : 0;
          const tb = b.tstamp ? new Date(b.tstamp).getTime() : 0;
          return ta - tb;
        })
        // Rimuovi duplicati lat/lng
        .filter((point, index, self) => {
          const key = `${point.lat},${point.lng}`;
          return self.findIndex((p) => `${p.lat},${p.lng}` === key) === index;
        });
      this.highFrequencyPointsByTripId.set(numericTripId, points);
      console.log('ZApoints:', this.highFrequencyPointsByTripId);

      if (!allPoints || allPoints.length === 0) {
        this.showInfoMessage(
          this.translate.instant('mapPage.noHighFrequencyPoints', { tripId }),
          5000
        );
        return;
      }

      // Calcola l'indice di partenza per questo trip_id
      let startingIndex = 0;

      // Se questo trip_id è già visualizzato, rimuovi il suo layer
      if (this.highFrequencyLayers.has(numericTripId)) {
        const existingLayerInfo = this.highFrequencyLayers.get(numericTripId)!;
        this.map.removeLayer(existingLayerInfo.layer);

        // Aggiorna l'array di trip_id visualizzati
        this.displayedTripIds = this.displayedTripIds.filter(
          (id) => id !== numericTripId
        );
      }

      // Ordina l'array di trip_id visualizzati e trova la posizione per questo trip_id
      this.displayedTripIds.push(numericTripId);
      this.displayedTripIds.sort((a, b) => a - b);

      // Calcola l'indice di partenza in base alla posizione nell'array ordinato
      const tripIndex = this.displayedTripIds.indexOf(numericTripId);
      for (let i = 0; i < tripIndex; i++) {
        const previousTripId = this.displayedTripIds[i];
        const previousLayerInfo = this.highFrequencyLayers.get(previousTripId);
        if (previousLayerInfo) {
          startingIndex += previousLayerInfo.pointCount;
        }
      }

      console.log(`Indice di partenza per trip_id ${tripId}: ${startingIndex}`);

      // Crea un nuovo layer per questo trip_id
      const newLayer = L.layerGroup().addTo(this.map);

      // Aggiungi i marker con numeri progressivi e colori basati sullo stato
      allPoints.forEach((point, index) => {
        if (!point.lat || !point.lng) {
          console.error(`Punto ${index} senza coordinate valide:`, point);
          return;
        }

        // Indice globale per questo punto
        const globalIndex = startingIndex + index + 1;

        // Determina la classe in base allo stato
        let stateClass = '';
        let statusColorClass = '';

        if (point.status) {
          const status = point.status.toUpperCase();
          if (status === 'MOVING') {
            stateClass = 'marker-moving';
            statusColorClass = 'text-green-600';
          } else if (status === 'IDLE') {
            stateClass = 'marker-idle';
            statusColorClass = 'text-orange-500';
          } else if (status === 'PARKED' || status === 'STOPPED') {
            stateClass = 'marker-parked';
            statusColorClass = 'text-gray-500';
          }
        }

        // Usa DivIcon con classe di stato
        const icon = L.divIcon({
          className: `marker-number ${stateClass}`,
          html: `${globalIndex}`,
          iconSize: [20, 20],
          iconAnchor: [10, 10]
        });

        // Crea marker
        const marker = L.marker([point.lat, point.lng], {
          icon: icon
        });

        // Formatta data se disponibile
        let formattedTime = '';
        if (point.tstamp) {
          const date = new Date(point.tstamp);
          formattedTime = date.toLocaleString();
        }

        // Crea popup con Tailwind
        const popupContent = `
        <div class="p-2 bg-white">
          <p class="my-[5px]"><strong>P: </strong> ${globalIndex} (${this.translate.instant(
          'mapPage.journey'
        )}: ${tripId})</p>
          ${
            formattedTime
              ? `<p class="my-[5px]"><strong>${this.translate.instant(
                  'mapPage.hours'
                )}:</strong> ${formattedTime}</p>`
              : ''
          }
          ${
            typeof point.gps_speed_v === 'number'
              ? `<p class="my-[5px] text-blue-600"><strong>${this.translate.instant(
                  'global.speed'
                )}:</strong> ${(point.gps_speed_v as number).toFixed(
                  2
                )} km/h</p>`
              : ''
          }
          ${
            point.status
              ? `<p class="my-[5px] font-bold ${statusColorClass}"><strong>${this.translate.instant(
                  'mapPage.state'
                )}:</strong> ${point.status}</p>`
              : ''
          }

        </div>
      `;

        marker.bindPopup(popupContent, {
          maxWidth: 300
        });

        marker.addTo(newLayer);
      });

      // Aggiorna la struttura dati con il nuovo layer
      this.highFrequencyLayers.set(numericTripId, {
        layer: newLayer,
        pointCount: points.length
      });

      // Zoom alla vista di tutti i punti
      this.zoomToAllHighFrequencyPoints();
    } catch (error) {
      console.error('Errore in displayHighFrequencyPoints:', error);
    }
  }

  // Metodo per assicurarsi che gli stili dei marker siano nel documento
  private ensureMarkerStyles(): void {
    if (!document.getElementById('high-frequency-marker-styles')) {
      const style = document.createElement('style');
      style.id = 'high-frequency-marker-styles';
      style.innerHTML = `
      .marker-number {
        display: flex;
        align-items: center;
        justify-content: center;
        background-color: #fff;
        border: 2px solid #000;
        border-radius: 50%;
        color: #000;
        font-weight: bold;
        font-size: 12px;
        width: 20px;
        height: 20px;
      }

      /* Stili per gli stati */
      .marker-moving {
        border-color: #00C853 !important; /* Verde */
      }

      .marker-idle {
        border-color: #FF9800 !important; /* Arancione */
      }

      .marker-parked {
        border-color: #9E9E9E !important; /* Grigio */
      }

      .custom-popup .leaflet-popup-content-wrapper {
        background: white;
        border-radius: 8px;
        box-shadow: 0 3px 14px rgba(0,0,0,0.4);
      }

      .custom-popup .leaflet-popup-tip {
        background: white;
      }
    `;
      document.head.appendChild(style);
    }
  }

  // Metodo corretto per centrare la mappa su tutti i punti ad alta frequenza
  private zoomToAllHighFrequencyPoints(): void {
    if (this.highFrequencyLayers.size === 0) return;

    try {
      // Crea un array di tutte le coordinate da tutti i layer
      let allCoordinates: L.LatLng[] = [];

      this.highFrequencyLayers.forEach((layerInfo) => {
        // Per ogni layer, estrai le coordinate di tutti i marker
        layerInfo.layer.eachLayer((layer: any) => {
          if (layer instanceof L.Marker) {
            allCoordinates.push(layer.getLatLng());
          }
        });
      });

      if (allCoordinates.length > 0) {
        // Crea un bounds con tutte le coordinate
        const bounds = L.latLngBounds(allCoordinates);

        // Zoom alla vista combinata
        this.map?.fitBounds(bounds, { padding: [50, 50] });
        console.log('Zoom impostato su tutti i punti ad alta frequenza');
      } else {
        console.log('Nessuna coordinata trovata per il zoom');
      }
    } catch (error) {
      console.error('Errore nel calcolo dello zoom:', error);
    }
  }

  // Metodo per disattivare tutti i punti ad alta frequenza
  clearAllHighFrequencyPoints(): void {
    this.highFrequencyLayers.forEach((layerInfo) => {
      this.map?.removeLayer(layerInfo.layer);
    });

    this.highFrequencyLayers.clear();
    this.displayedTripIds = [];
  }
  /**
   * Determina il colore del marker in base allo stato e alla velocità
   */
  private getMarkerColorByStatus(status: string, speed: number): string {
    // Colori in base allo stato
    switch (status?.toUpperCase()) {
      case 'MOVING':
        // Gradazione di colore in base alla velocità
        if (speed > 80) return '#FF0000'; // Rosso per velocità alta
        if (speed > 50) return '#FFA500'; // Arancione per velocità media
        return '#00FF00'; // Verde per velocità bassa
      case 'IDLE':
        return '#FFFF00'; // Giallo per idle
      case 'STOPPED':
        return '#0000FF'; // Blu per fermo
      default:
        return '#808080'; // Grigio per stati sconosciuti
    }
  }

  /**
   * Formatta la data per la visualizzazione
   */
  private formatDate(dateString: string): string {
    const date = new Date(dateString);
    return date.toLocaleString(); // Usa il formato locale
  }

  /**
   * Esporta i waypoints correnti in un file Excel
   */
  exportWaypointsToExcel(): void {
    if (!this.waypoints || this.waypoints.length === 0) {
      console.warn("Nessun waypoint disponibile per l'export");
      return;
    }

    try {
      // Prepara i dati per l'export
      const exportData = this.waypoints.map((waypoint, index) => {
        // Trova i dettagli corrispondenti se disponibili
        const waypointDetail = this.journeysDetails?.find(
          (detail) =>
            detail.gps_lat === waypoint[0] && detail.gps_lon === waypoint[1]
        );

        return {
          'N°': index + 1,
          Latitudine: waypoint[0].toFixed(6),
          Longitudine: waypoint[1].toFixed(6),
          'Velocità (km/h)': waypointDetail?.gps_speed_v?.toFixed(1) || 'N/D',
          Angolo: waypointDetail?.gps_angle_v?.toFixed(0) || 'N/D',
          'Altitudine (m)': waypointDetail?.gps_alt_v?.toFixed(0) || 'N/D',
          Stato: waypointDetail?.status
            ? this.mapService.translateVehicleStatus(waypointDetail.status)
            : 'N/D',
          Timestamp: waypointDetail?.gps_tstamp
            ? new Date(waypointDetail.gps_tstamp).toLocaleString()
            : 'N/D'
        };
      });

      // Crea il worksheet
      const worksheet = XLSX.utils.json_to_sheet(exportData);

      // Imposta la larghezza delle colonne
      const columnWidths = [
        { wch: 5 }, // N°
        { wch: 12 }, // Latitudine
        { wch: 12 }, // Longitudine
        { wch: 15 }, // Velocità
        { wch: 10 }, // Angolo
        { wch: 12 }, // Altitudine
        { wch: 15 }, // Stato
        { wch: 20 } // Timestamp
      ];
      worksheet['!cols'] = columnWidths;

      // Crea il workbook e aggiungi il worksheet
      const workbook = XLSX.utils.book_new();
      const sheetName = 'Waypoints';
      XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);

      // Genera il file Excel
      const excelBuffer = XLSX.write(workbook, {
        bookType: 'xlsx',
        type: 'array'
      });

      // Prepara il nome del file con timestamp
      const plate =
        this.selectedVehicle?.vehicleInfo?.plate ||
        this.selectedVehicle?.plate ||
        'Veicolo';
      const timestamp = new Date().toISOString().split('T')[0];
      const fileName = `Waypoints_${plate}_${timestamp}.xlsx`;

      // Scarica il file
      const blob = new Blob([excelBuffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      saveAs(blob, fileName);

      console.log(
        `Export Excel completato: ${this.waypoints.length} waypoints esportati in ${fileName}`
      );

      // // Mostra un messaggio di successo usando il servizio di notifica esistente
      // alert(`Export completato: ${this.waypoints.length} waypoints esportati in ${fileName}`);
    } catch (error) {
      console.error("Errore durante l'export Excel:", error);
      alert("Errore durante l'export dei waypoints");
    }
  }

  /**
   * Recupera i punti ad alta frequenza per l'intervallo globale (start/end)
   */
  async getGlobalHighFrequencyPoints(): Promise<any[]> {
    if (!this.paramStartTime || !this.paramEndTime) {
      console.warn('Intervallo globale non valido');
      return [];
    }
    try {
      const startTime = new Date(this.paramStartTime).toISOString();
      const endTime = new Date(this.paramEndTime).toISOString();
      const details = await this.journeyService
        .getHighFrequencyPoints(this.paramUid, startTime, endTime)
        .toPromise();
      return (details || [])
        .filter(
          (detail: any) => detail.gps_lat != null && detail.gps_lon != null
        )
        .map((detail: any) => ({
          lat: detail.gps_lat!,
          lng: detail.gps_lon!,
          tstamp: detail.tstamp,
          status: detail.status,
          gps_speed_v: detail.gps_speed_v,
          gps_angle_v: detail.gps_angle_v,
          gps_alt_v: detail.gps_alt_v
        }));
    } catch (err) {
      console.error(
        'Errore nel recupero dei punti alta frequenza globali:',
        err
      );
      return [];
    }
  }

  /**
   * Mostra/nasconde i punti alta frequenza per l'intervallo globale
   */
  async toggleHighFrequencyPoints(): Promise<void> {
    if (!this.map) return;
    this.highFrequencyGlobalVisible = !this.highFrequencyGlobalVisible;
    // if (this.waypointDotsGroup)
    //   this.map.removeLayer(this.waypointDotsGroup);
    if (this.highFrequencyGlobalVisible) {
      // Mostra i punti alta frequenza e la polyline HF
      const points = await this.getGlobalHighFrequencyPoints();
      if (!points.length) {
        this.showInfoMessage(
          this.translate.instant('mapPage.noHighFrequencyPoints'),
          5000
        );
        return;
      }
      // Mostra indicatore di caricamento
      //elimino i waypoint ma calcolo solo su HF
      //FZ30072025 gli F dovrebbero essere completoi e i low potrebbero essere temporalmetne disallineati
      this.showRouteLoadingIndicator(true);
      this.mergedDataHF_Waypoint = this.mergeWaypointsAndPoints(
      [],
        points
      );
      console.log('Merged Data HF Waypoint:', this.mergedDataHF_Waypoint);
      console.log(
        'PUNTI PER ROTTA',
        this.toLatLngCSV(this.mergedDataHF_Waypoint)
      );
      // Usa la funzione per creare e aggiungere i marker
      if (this.highFrequencyGlobalLayer) {
        this.map.removeLayer(this.highFrequencyGlobalLayer);
        this.highFrequencyGlobalLayer = null;
      }
      this.highFrequencyGlobalLayer = this.createJourneyMarkersLayer(
        this.mergedDataHF_Waypoint,
        null // Nessun tripId globale
      );

      this.highFrequencyGlobalLayer.addTo(this.map);
      let departureTimeParam = '';
      if (
        this.mergedDataHF_Waypoint.length > 0 &&
        this.mergedDataHF_Waypoint[0].tstamp
      ) {
        const departureTime = new Date(this.mergedDataHF_Waypoint[0].tstamp)
          .toISOString()
          .replace('.000Z', '');
        departureTimeParam = `&departureTime=${departureTime}`;
      }

      //     if (this.waypointDotsGroup) {
      //   this.map.removeLayer(this.waypointDotsGroup);
      //   console.log('Waypoints rimossi dalla mappa');

      //   this.waypointsVisible = false;

      //  // this.toggleHighFrequencyPoints();
      // }
      this.toggleWaypoints();

      // this.toggleHighFrequencyPoints();

    //  this.percorso_HF(this.mergedDataHF_Waypoint, true, departureTimeParam);
      // if (this.hfpointPolylineGroup)
      // {
      //    this.finalizeRouteDisplay(this.hfpointPolylineGroup, true);

      // }
      // else
      // {
      // this.percorso_HF(this.mergedDataHF_Waypoint, true, departureTimeParam);
      // }
    } else {
      // Nascondi i punti HF e ricarica la polyline standard

      console.log('Waypoints aggiunti alla mappa');
      this.waypointsVisible = true;
      this.waypointsVisible = false;
            this.showRouteLoadingIndicator(true);
      if (this.highFrequencyGlobalLayer) {
        this.map.removeLayer(this.highFrequencyGlobalLayer);
        this.highFrequencyGlobalLayer = null;
      }
      this.toggleWaypoints();

      // ROUTING DISABLED: drawRoute non più utilizzato
      // this.drawRoute();
      // if (this.waypointPolylineGroup){
      //   this.finalizeRouteDisplay(this.waypointPolylineGroup, true);
      // }
      // else
      // {
      //   this.drawRoute();
      // }
    }
  }

  // fz25072025 function originle, modificata elimnado i doppioni
  //  private mergeWaypointsAndPoints(waypointData: any[], points: any[]): any[] {
  //   const waypoints = (waypointData || [])
  //     .map((wp: any) => ({
  //       lat: wp.gps_lat,
  //       lng: wp.gps_lon,
  //       tstamp: wp.tstamp,
  //       status: wp.status,
  //       gps_speed_v: wp.gps_speed_v,
  //       gps_angle_v: wp.gps_angle_v,
  //       gps_alt_v: wp.gps_alt_v
  //     }))
  //     .filter((wp: any) =>
  //       wp.lat != null && wp.lng != null && wp.lat !== '' && wp.lng !== ''
  //     );

  //   const normalizedPoints = (points || [])
  //     .map((pt: any) => ({
  //       lat: pt.lat,
  //       lng: pt.lng,
  //       tstamp: pt.tstamp,
  //       status: pt.status,
  //       gps_speed_v: pt.gps_speed_v,
  //       gps_angle_v: pt.gps_angle_v,
  //       gps_alt_v: pt.gps_alt_v
  //     }))
  //     .filter((pt: any) =>
  //       pt.lat != null && pt.lng != null && pt.lat !== '' && pt.lng !== ''
  //     );

  //   // Unisci e ordina per tstamp crescente
  //   return [...waypoints, ...normalizedPoints].sort((a, b) => {
  //     const ta = a.tstamp ? new Date(a.tstamp).getTime() : 0;
  //     const tb = b.tstamp ? new Date(b.tstamp).getTime() : 0;
  //     return ta - tb;
  //   });
  // }

  private mergeWaypointsAndPoints(waypointData: any[], points: any[]): any[] {
    const waypoints = (waypointData || [])
      .map((wp: any) => ({
        lat: wp.gps_lat,
        lng: wp.gps_lon,
        tstamp: wp.tstamp,
        status: wp.status,
        gps_speed_v: wp.gps_speed_v,
        gps_angle_v: wp.gps_angle_v,
        gps_alt_v: wp.gps_alt_v
      }))
      .filter(
        (wp: any) =>
          wp.lat != null && wp.lng != null && wp.lat !== '' && wp.lng !== ''
      );

    const normalizedPoints = (points || [])
      .map((pt: any) => ({
        lat: pt.lat,
        lng: pt.lng,
        tstamp: pt.tstamp,
        status: pt.status,
        gps_speed_v: pt.gps_speed_v,
        gps_angle_v: pt.gps_angle_v,
        gps_alt_v: pt.gps_alt_v
      }))
      .filter(
        (pt: any) =>
          pt.lat != null && pt.lng != null && pt.lat !== '' && pt.lng !== ''
      );

    // Unisci e ordina per tstamp crescente
    const merged = [...waypoints, ...normalizedPoints].sort((a, b) => {
      const ta = a.tstamp ? new Date(a.tstamp).getTime() : 0;
      const tb = b.tstamp ? new Date(b.tstamp).getTime() : 0;
      return ta - tb;
    });

    // Rimuovi duplicati lat/lng
    const unique: any[] = [];
    const seen = new Set<string>();
    merged.forEach((p) => {
      const key = `${p.lat},${p.lng}`;
      if (!seen.has(key)) {
        seen.add(key);
        unique.push(p);
      }
    });

    return unique;
  }
  // percorso_HF(
  //   allPoints: Array<{ lat: number; lng: number }>,
  //   showArrows: boolean = true,
  //   departureTimeParam: string = ''
  // ) {
  //   // Pulisci le polyline esistenti prima di disegnare nuove
  //   this.clearMapPolylines();
  //   // this.waypointsVisible = false;

  //   if (!this.map) return;

  //   // Filtra i punti con lat/lng null o non validi
  //   const validPoints = allPoints.filter(
  //     (p) =>
  //       typeof p.lat === 'number' &&
  //       typeof p.lng === 'number' &&
  //       !isNaN(p.lat) &&
  //       !isNaN(p.lng)
  //   );

  //   if (!validPoints || validPoints.length === 0) {
  //     this.isLoading = false;
  //     this.showRouteLoadingIndicator(false);
  //     this.showInfoMessage(
  //       this.translate.instant('mapPage.noRouteFound'),
  //       5000
  //     );
  //     console.error('Nessun punto valido disponibile per il percorso HF.');
  //     return;
  //   }

  //   // URL per l'API HERE Maps
  //   const importUrl =
  //     `https://router.hereapi.com/v8/import?apikey=${environment.hereApiKey}` +
  //     `&return=polyline,summary,actions,instructions,potentialTimeDependentViolations,truckRoadTypes,incidents,routeHandle,turnByTurnActions,routingZones,passthrough,elevation&spans=length,truckAttributes,stateCode,routingZones,gates,incidents,truckRoadTypes,segmentId,segmentRef&transportMode=truck&allow[hov]=true&allow[hot]=true&lang=it-it${departureTimeParam}`;

  //   // Crea un gruppo per le polyline che conterrà tutti i segmenti
  //   const polylineGroup = L.featureGroup();

  //   // Verifica se la traccia ha più di 100 punti
  //   if (validPoints.length <= 100) {
  //     // Mappa solo lat/lng
  //     const waypoints = validPoints.map((p) => ({ lat: p.lat, lng: p.lng }));
  //     this.processRouteSegment(
  //       waypoints,
  //       0,
  //       polylineGroup,
  //       importUrl,
  //       showArrows
  //     );
  //   } else {
  //     // Se la traccia è lunga, dividi in segmenti da 100 punti
  //     const segments: Array<{ lat: number; lng: number }[]> = [];
  //     for (let i = 0; i < validPoints.length; i += 99) {
  //       const start = i === 0 ? 0 : i - 1;
  //       const end = Math.min(i + 99, validPoints.length);
  //       // Mappa solo lat/lng per ogni segmento
  //       segments.push(
  //         validPoints.slice(start, end).map((p) => ({ lat: p.lat, lng: p.lng }))
  //       );
  //     }

  //     // Processa i segmenti in parallelo
  //     this.routeSummaries = [];
  //     this.processSegmentsInParallel(
  //       segments,
  //       polylineGroup,
  //       importUrl,
  //       showArrows,
  //       '#7e22ce',
  //       8,
  //       0.7
  //     );
  //   }
  // }
  //   getTotalRouteLength(): number {
  //   if (!Array.isArray(this.routeSummaries)) return 0;
  //   return this.routeSummaries.reduce((sum, summary) => sum + (summary.length || 0), 0);
  // }

  isUserAdmin(): boolean {
    const userInfo: CurrentUserInfo = this.dataService.getCurrentUserInfo();
    return !!userInfo?.user?.group?.isAdminGroup;
  }

  private createJourneyMarkersLayer(
    points: any[],
    tripId: number | null,
    startingIndex: number = 0
  ): L.LayerGroup {
    const newLayer = L.layerGroup();
    // Aggiungi stili CSS per i marker se non esistono già
    this.ensureMarkerStyles();

    points.forEach((point, index) => {
      if (!point.lat || !point.lng) {
        console.error(`Punto ${index} senza coordinate valide:`, point);
        return;
      }

      const globalIndex = startingIndex + index + 1;

      // Determina la classe in base allo stato
      let stateClass = '';
      let statusColorClass = '';

      if (point.status) {
        const status = point.status.toUpperCase();
        if (status === 'MOVING') {
          stateClass = 'marker-moving';
          statusColorClass = 'text-green-600';
        } else if (status === 'IDLE') {
          stateClass = 'marker-idle';
          statusColorClass = 'text-orange-500';
        } else if (status === 'PARKED' || status === 'STOPPED') {
          stateClass = 'marker-parked';
          statusColorClass = 'text-gray-500';
        }
      }

      const icon = L.divIcon({
        className: `marker-number ${stateClass}`,
        html: `${globalIndex}`,
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      });

      const marker = L.marker([point.lat, point.lng], { icon });

      let formattedTime = '';
      if (point.tstamp) {
        const date = new Date(point.tstamp);
        formattedTime = date.toLocaleString();
      }

      const popupContent = `
  <div class="p-2 bg-white">
    ${
      tripId != null
        ? `<p class="my-[5px]"><strong>P: </strong> ${globalIndex} (${this.translate.instant(
            'mapPage.journey'
          )}: ${tripId})</p>`
        : ''
    }
    ${
      formattedTime
        ? `<p class="my-[5px]"><strong>${this.translate.instant(
            'mapPage.hours'
          )}:</strong> ${formattedTime}</p>`
        : ''
    }
    ${
      typeof point.gps_speed_v === 'number'
        ? `<p class="my-[5px] text-blue-600"><strong>${this.translate.instant(
            'global.speed'
          )}:</strong> ${(point.gps_speed_v as number).toFixed(2)} km/h</p>`
        : ''
    }
    ${
      point.status
        ? `<p class="my-[5px] font-bold ${statusColorClass}"><strong>${this.translate.instant(
            'mapPage.state'
          )}:</strong> ${point.status}</p>`
        : ''
    }
  </div>
`;

      marker.bindPopup(popupContent, { maxWidth: 300 });
      marker.addTo(newLayer);
    });

    return newLayer;
  }

  toLatLngCSV(points: { lat: number; lng: number }[]): string {
    // Restituisce una stringa JSON come richiesto: [{"lat":...,"lng":...},...]
    return JSON.stringify(points.map((p) => ({ lat: p.lat, lng: p.lng })));
  }

  // async toggleHighFrequencyPointsNoHere(): Promise<void> {
  //   if (!this.map) return;
  //   this.highFrequencyGlobalVisibleNoHere = !this.highFrequencyGlobalVisibleNoHere;
  //   // if (this.waypointDotsGroup)
  //   //   this.map.removeLayer(this.waypointDotsGroup);
  //   if (this.highFrequencyGlobalVisibleNoHere) {
  //     // Mostra i punti alta frequenza e la polyline HF
  //     const points = await this.getGlobalHighFrequencyPoints();
  //     if (!points.length) {
  //       this.showInfoMessage(
  //         this.translate.instant('mapPage.noHighFrequencyPoints'),
  //         5000
  //       );
  //       return;
  //     }
  //     // Mostra indicatore di caricamento
  //     //elimino i waypoint ma calcolo solo su HF
  //     //FZ30072025 gli F dovrebbero essere completoi e i low potrebbero essere temporalmetne disallineati
  //     this.showRouteLoadingIndicator(true);
  //     this.mergedDataHF_Waypoint = this.mergeWaypointsAndPoints(
  //       [],
  //       points
  //     );
  //     console.log('Merged Data HF Waypoint:', this.mergedDataHF_Waypoint);
  //     console.log(
  //       'PUNTI PER ROTTA',
  //       this.toLatLngCSV(this.mergedDataHF_Waypoint)
  //     );
  //     // Usa la funzione per creare e aggiungere i marker
  //     if (this.highFrequencyGlobalLayerNoHere) {
  //       this.map.removeLayer(this.highFrequencyGlobalLayerNoHere);
  //       this.highFrequencyGlobalLayerNoHere = null;
  //     }
  //     this.highFrequencyGlobalLayerNoHere = this.createJourneyMarkersLayer(
  //       this.mergedDataHF_Waypoint,
  //       null // Nessun tripId globale
  //     );

  //     this.highFrequencyGlobalLayerNoHere.addTo(this.map);
  //     let departureTimeParam = '';
  //     if (
  //       this.mergedDataHF_Waypoint.length > 0 &&
  //       this.mergedDataHF_Waypoint[0].tstamp
  //     ) {
  //       const departureTime = new Date(this.mergedDataHF_Waypoint[0].tstamp)
  //         .toISOString()
  //         .replace('.000Z', '');
  //       departureTimeParam = `&departureTime=${departureTime}`;
  //     }

  //     //     if (this.waypointDotsGroup) {
  //     //   this.map.removeLayer(this.waypointDotsGroup);
  //     //   console.log('Waypoints rimossi dalla mappa');

  //     //   this.waypointsVisible = false;

  //     //  // this.toggleHighFrequencyPoints();
  //     // }


  //      this.drawHighFrequencyPolyline(this.mergedDataHF_Waypoint);
  //         this.toggleWaypoints();
  //     // this.toggleHighFrequencyPoints();

  //   //  this.percorso_HF(this.mergedDataHF_Waypoint, true, departureTimeParam);
  //     // if (this.hfpointPolylineGroup)
  //     // {
  //     //    this.finalizeRouteDisplay(this.hfpointPolylineGroup, true);

  //     // }
  //     // else
  //     // {
  //     // this.percorso_HF(this.mergedDataHF_Waypoint, true, departureTimeParam);
  //     // }
  //   } else {
  //     // Nascondi i punti HF e ricarica la polyline standard

  //     console.log('Waypoints aggiunti alla mappa');
  //           this.showRouteLoadingIndicator(true);
  //     this.waypointsVisible = true;
  //     this.waypointsVisible = false;
  //     // ROUTING DISABLED: riferimenti NoHere non più utilizzati
  //     // if (this.highFrequencyGlobalLayerNoHere) {
  //     //   this.map.removeLayer(this.highFrequencyGlobalLayerNoHere);
  //     //   this.highFrequencyGlobalLayerNoHere = null;
  //     // }
  //     this.toggleWaypoints();

  //     // ROUTING DISABLED: drawRoute non più utilizzato
  //     // this.drawRoute();
  //     // if (this.waypointPolylineGroup){
  //     //   this.finalizeRouteDisplay(this.waypointPolylineGroup, true);
  //     // }
  //     // else
  //     // {
  //     //   this.drawRoute();
  //     // }
  //   }
  // }

//   private drawHighFrequencyPolyline(points: Array<{ lat: number; lng: number }>, color: string = '#c2410c', weight: number = 6, opacity: number = 0.8): void {
//   if (!this.map || !points || points.length < 2) {
//     console.warn('Mappa non disponibile o punti insufficienti per la polyline');
//     return;
//   }
//    this.clearMapPolylines();
//   // Crea la polyline con i punti forniti
//   const polyline = L.polyline(points.map(p => [p.lat, p.lng]), {
//     color,
//     weight,
//     opacity
//   });

//   polyline.addTo(this.map);

//   // Centra la mappa sulla polyline
//   this.map.fitBounds(polyline.getBounds(), { padding: [50, 50] });

//   // (Opzionale) Puoi aggiungere frecce direzionali come in processRouteResponse:
//   const decorator = L.polylineDecorator(polyline, {
//     patterns: [
//       {
//         offset: '5%',
//         repeat: '10%',
//         symbol: L.Symbol.arrowHead({
//           pixelSize: 12,
//           polygon: true,
//           pathOptions: {
//             fillOpacity: 0.8,
//             weight: 0,
//             color: 'black'
//           }
//         })
//       }
//     ]
//   });
//   decorator.addTo(this.map);
//     this.showRouteLoadingIndicator(false);
// }
}
